import { createPlaybackAudioContext } from './outputDeviceSupport'
import { scrollPlayalongPlayingLineIntoCenter } from './playalongStaffLayout'
import {useState, useEffect, useLayoutEffect, useRef} from 'react'
import * as localForage from "localforage";
import useAbcTools from './useAbcTools'
import {isMobile} from 'react-device-detect'
import abcjs from "abcjs";
import { encodeAudioBufferWithSetting } from './audioCompressEncode'
import { getPlaybackSoundFontPlan, getSoundFontVolumeMultiplier } from './soundFontConfig'
import { programOffsetsForPlaybackPlan, abcjsPlaybackSynthOptions } from './abcSynthProgramOffsets'
import { clearAbcjsSoundsCache, clearRejectedAbcjsSoundsCache } from './abcjsSoundsCache'
import { remapFlattenedMidiPrograms } from './localSoundfontInstrumentMap'
import PitchTempoShifter from './pitchTempoShifter'
import { createHiddenTabPlaybackClock } from './hiddenTabPlaybackClock'
import { getPlaybackSettings, combinedPitchSemitones } from './pitchTempoUtils'
import {
    shouldMirrorMidiPlaybackCursor,
    shouldDrawPlaybackCursor,
    ensureAbcjsCursorLine,
    updateAbcjsCursorLine,
    applyPlaybackCursorAtTime,
    cursorPositionFromNoteTimings,
    findNoteTimingAtTime,
    barStartTimingsFromNoteTimings,
    scrollPlaybackCursorIntoTopHalf,
    cursorLineHasActivePosition,
} from './notationPlaybackCursor'
import {
    isStaleSeekEngineReading,
    computeMidiMetronomeCountIn,
    rhythmAlignedCountInInput,
    computeExtraMeasuresAtBeginning,
    computeTimingMusicStartMs,
    audioRatioToTimingProgress,
    timingProgressToAudioSeconds,
    isMidiStartFromBeginning,
    shouldUseMidiMetronomeCountIn,
    resolveCountInHandoffAnchor,
    MIDI_START_FROM_BEGINNING_TOLERANCE_SECONDS,
    computePlaybackMetronomeTempo,
    resolveSequencePathMeasureTiming,
    computeRhythmGridTempo,
    computeCountInSlotCount,
    resolveCountInBeatCount,
    resolveCountInSlotCount,
    effectiveCountInBars,
    computeCountInGridTempo,
    scoreMsPerMeasureForRhythmGrid,
    defaultAbcBeatLengthForMeter,
    notationBeatToAudioSeconds,
    notationBeatToAudioRatio,
    notationMsToAudioRatio,
    shouldScheduleMidiRepeatRestart,
    isMidiBufferNearNaturalEnd,
    shouldIgnoreMidiPlaybackCompletion,
} from './playbackStateLogic'
import {
    getPlaybackMetronomeSettings,
    resolveMetronomeSettingsTune,
    resolveTuneTimeSignature,
    alignPlaybackRhythmToMeter,
} from './playbackMetronomeSettings'
import { slotsPerBar, rhythmFromTimeSignature, defaultMetronomeRhythm } from './metronomeRhythmPresets'
import { normalizeRhythmConfig, ENGINE_MODE_DRUMS } from './rhythmEngineTypes'
import { primeDrumKit } from './drumSampleKit'
import { resolveFillPlaybackOptions, FILL_STYLE_OFF } from './playbackFillSettings'
import { buildPlaybackSequence, barWholeNotesFromMeter } from './playbackFillPattern'
import { buildSoundingWrittenMap, expandVisualObjThroughVoltaRepeats, mapSoundingWholeToWritten } from './voltaRepeatExpand'
import { scheduleMediaCacheStorageCheck } from './mediaCacheStorage'
import { prefersNativeMediaPlayback } from './platformUtils'
import {
  isAbcNativePlayInFlight,
  isAndroidNativePlayerActive,
} from './androidNativePlayback'
import { preloadCountInCueInstrument, scheduleCountInCueNote, firstWarmupCueMidi, firstPlaybackCueMidiFromVisual } from './countInPitchCue'
import { playRhythmSlot } from './rhythmSlotPlayback'
import { getRhythmSwing } from './rhythmGrid'
import { createRhythmOutputBus } from './rhythmOutputBus'
import {
    createRhythmPlaybackController,
    stopRhythmPlaybackController,
    startRhythmCountIn,
    enterRhythmPlaying,
    seekRhythmPlaying,
    setRhythmPlaybackTempo,
    setRhythmPlaybackRhythm,
    getRhythmPlaybackPhase,
    PHASE_IDLE,
    PHASE_COUNT_IN,
    PHASE_ENTRY_GAP,
    PHASE_PLAYING,
    isRhythmPlaybackActive,
    beginRhythmPlayingAtMusicStart,
    reanchorRhythm,
    getRhythmTimelineSnapshot,
} from './rhythmPlaybackController'
import {
    createRhythmTimingDiagnostics,
    recordRhythmSlotEvent,
    resetRhythmTimingDiagnostics,
    buildRhythmDiagnosticsSnapshot,
} from './rhythmTimingDiagnostics'
import { buildPlaybackTimingMap, timingAtMusicSeconds } from './playbackTimingMap'
import { audioTimeForGlobalSlot, reanchorTimelineAtSlot } from './rhythmTimeline'

function assignMediaControllerRef(mediaController, refKey, value) {
    const ref = mediaController && mediaController[refKey]
    if (ref) {
        ref.current = value
    }
}

export default function useAbcSynth(props) {
    
    const metronomeTimeout = useRef(null)
    const rhythmOutputBus = useRef(createRhythmOutputBus())
    const rhythmController = useRef(null)
    const rhythmTimingDiagnostics = useRef(createRhythmTimingDiagnostics())
    const rhythmMusicAnchorRef = useRef({ musicSeconds: 0, audioContextTime: 0, active: false })
    const countInPendingRef = useRef(false)
    const countInMidiPreScheduledRef = useRef(false)
    /** @deprecated alias for rhythmController — tune playback no longer uses Metronome.js */
    const metronome = rhythmController
    const pendingRestartFromZeroRef = useRef(false)
    const gaudioContext = useRef(null)
    const gmidiBuffer = useRef(null)
    const gvisualObj = useRef(null)
    const gtimingCallbacks = useRef(null)
    const gcursor = useRef(null)
    const isLoading = useRef(null)
    const currentTime = useRef(0)
    const pitchShifterRef = useRef(null)
    /** { ctxTime, offsetSec } when the plain abcjs buffer (no shifter) is audible. */
    const nativeBufferClockRef = useRef(null)
    const pitchShifterBufferRef = useRef(null)
    const pitchTempoSettingsRef = useRef({ tempo: 1.0, pitch: 0, fineTune: 0 })
    const playalongOutputLatencyInTimelineRef = useRef({
      alreadyInTimeline: false,
      appliedSeconds: 0,
    })
    const forceStopRef = useRef(false)
    const playbackGenerationRef = useRef(0)
    const gplaybackVisualObj = useRef(null)
    const primeTimerRef = useRef(null)
    const primePromiseRef = useRef(null)
    const midiPlaybackGuardUntilRef = useRef(0)
    // Standalone Abc (e.g. practice warmups) calls startPlaying(true) before count-in;
    // isPlaying stays false until MIDI audio starts after the metronome.
    const forcePlaybackUntilStartRef = useRef(false)
    const isPlayingRef = useRef(false)
    const pendingPlaybackStartSecondsRef = useRef(null)
    const midiPrimeInFlightRef = useRef(false)
    /** Suppress "Could not load notation" after stop/invalidate cancels an in-flight prime. */
    const midiPrimeCancelLatchUntilRef = useRef(0)
    /** Avoid re-entering natural end / restart loops from timing callbacks. */
    const playbackFinishedRef = useRef(false)
    const deferredNaturalEndTimerRef = useRef(null)
    const deferredNaturalEndAttemptRef = useRef(0)
    /** Avoid startMidiAndTiming → startPlaying → startMidiAndTiming retry loops. */
    const midiAudioRestartAfterFailRef = useRef(false)
    const playbackTimingMapRef = useRef(null)
    const lastPlaybackTimingSampleRef = useRef(null)
    const soundingWrittenMapRef = useRef(null)
    const effectiveMsPerMeasureRef = useRef(0)
    const primedMsPerMeasureRef = useRef(0)
    const audioContextKeepAliveRef = useRef(null)
    const beginMidiPlaybackRef = useRef(null)
    const playMidiBridgeRef = useRef(function(options) {
        midiPlaybackGuardUntilRef.current = 0
        if (beginMidiPlaybackRef.current) {
            return beginMidiPlaybackRef.current(options)
        }
        return false
    })
    const mediaControllerRef = useRef(props.mediaController)
    mediaControllerRef.current = props.mediaController

    function shouldSuppressPlaybackEndSeek() {
        const mc = mediaControllerRef.current
        return !!(mc && typeof mc.shouldSuppressPlaybackEndSeek === 'function'
            && mc.shouldSuppressPlaybackEndSeek())
    }
    
    const [tune, setTune] = useState(props.tunebook.abcTools.abc2json(props.abc))
    
    const [showTempo, setShowTempo] = useState(false)
    const [showTranspose, setShowTranspose] = useState(false)
    const [clickSeek, setClickSeek] = useState(0)
    const [tapToPlay, setTapToPlay] = useState(false)
    const [playCancelled, setPlayCancelled] = useState(false)

    function setTapToPlayFlag(value) {
        if (!value) {
            if (props.mediaController) {
                props.mediaController.setTapToPlay(false)
            } else {
                setTapToPlay(false)
            }
            return
        }
        if (tryResumeSynthAndStart()) {
            return
        }
        if (props.mediaController) {
            props.mediaController.setTapToPlay(true)
        } else {
            setTapToPlay(true)
        }
    }

    function resumeSynthAudioContext() {
        if (!abcjs.synth || !abcjs.synth.supportsAudio || !abcjs.synth.supportsAudio()) {
            return null
        }
        const Ctx = window.AudioContext || window.webkitAudioContext
            || window.mozAudioContext || window.msAudioContext
        if (!Ctx) return null
        let ctx = gaudioContext.current
        if (!ctx || ctx.state === 'closed') {
            ctx = createPlaybackAudioContext() || new Ctx()
            gaudioContext.current = ctx
        }
        if (ctx.state === 'suspended') {
            try { ctx.resume() } catch (e) { /* ignore */ }
        }
        // Hold the context open through long offline synth renders (10–30s).
        if (!audioContextKeepAliveRef.current) {
            try {
                const buffer = ctx.createBuffer(1, 1, ctx.sampleRate || 44100)
                const source = ctx.createBufferSource()
                source.buffer = buffer
                const gain = ctx.createGain()
                gain.gain.value = 0
                source.connect(gain)
                gain.connect(ctx.destination)
                source.start(0)
                audioContextKeepAliveRef.current = { source: source, gain: gain }
            } catch (e) { /* ignore */ }
        }
        return ctx
    }

    function teardownSynthAudioKeepalive() {
        if (audioContextKeepAliveRef.current) {
            try {
                audioContextKeepAliveRef.current.source.stop()
            } catch (e) {}
            audioContextKeepAliveRef.current = null
        }
    }

    function suspendSynthAudioContextForNative() {
        teardownSynthAudioKeepalive()
        pauseMidiSynth()
        if (gaudioContext.current && gaudioContext.current.state === 'running') {
            try {
                gaudioContext.current.suspend()
            } catch (e) {}
        }
    }

    async function ensureSynthAudioContextRunning() {
        if (!gaudioContext.current) return false
        if (gaudioContext.current.state === 'running') return true
        if (gaudioContext.current.state === 'closed') return false
        const fromUserGesture = (props.hasPlaybackGesture && props.hasPlaybackGesture())
            || (props.mediaController && props.mediaController.userGesturePlayRef
                && props.mediaController.userGesturePlayRef.current)
        try {
            await gaudioContext.current.resume()
        } catch (e) {
            return false
        }
        if (gaudioContext.current.state !== 'running' && fromUserGesture) {
            try {
                await gaudioContext.current.resume()
            } catch (e) {
                return false
            }
        }
        return gaudioContext.current.state === 'running'
    }

    function needsMidiAudioContextUnlock() {
        if (!abcjs.synth.supportsAudio()) return true
        const ctx = gaudioContext.current
        if (!ctx) return true
        return ctx.state === 'suspended' || ctx.state === 'interrupted'
    }

    function latchMidiPrimeCancel(ms) {
        midiPrimeCancelLatchUntilRef.current = Date.now() + (ms > 0 ? ms : 2500)
        if (props.mediaController && props.mediaController.latchMidiPrimeQuiet) {
            props.mediaController.latchMidiPrimeQuiet(ms > 0 ? ms : 2500)
        }
    }

    function shouldQuietMidiPrimeFailure() {
        if (Date.now() < midiPrimeCancelLatchUntilRef.current) return true
        if (primePromiseRef.current) return true
        return false
    }

    function reportMidiPlaybackFailure() {
        releaseMidiUiLoading()
        setIsPlaying(false)
        if (getForceStop() || shouldQuietMidiPrimeFailure()) {
            if (props.mediaController && props.mediaController.setIsPlaying) {
                props.mediaController.setIsPlaying(false)
            }
            return
        }
        // Another prime superseded this one without bumping generation.
        if (primePromiseRef.current || midiPrimeInFlightRef.current) {
            if (props.mediaController && props.mediaController.setIsPlaying) {
                props.mediaController.setIsPlaying(false)
            }
            return
        }
        if (props.mediaController) {
            props.mediaController.setIsPlaying(false)
            if (needsMidiAudioContextUnlock()) {
                showMidiTapToPlay()
                return
            }
            if (props.mediaController.reportNotationPrimeFailure) {
                props.mediaController.reportNotationPrimeFailure()
                return
            }
        }
        if (needsMidiAudioContextUnlock()) {
            showMidiTapToPlay()
        }
    }

    function showMidiTapToPlay() {
        if (props.mediaController) {
            props.mediaController.setTapToPlay(true)
        } else {
            setTapToPlay(true)
        }
    }

    function releaseMidiUiLoading() {
        // Only hold the kickoff lock during live synth render, count-in, or handoff.
        // Keeping it for any active intent leaves a stale lock after cancelled primes.
        if (!primePromiseRef.current && !countInPendingRef.current && !isRhythmHandoffPhase()) {
            midiPrimeInFlightRef.current = false
        }
        isLoading.current = false
        if (props.mediaController && props.mediaController.setIsLoading) {
            props.mediaController.setIsLoading(false)
        }
    }

    function clearStaleMidiKickoffLock() {
        if (primePromiseRef.current || countInPendingRef.current || isRhythmHandoffPhase()) {
            return false
        }
        if (midiPrimeInFlightRef.current) {
            midiPrimeInFlightRef.current = false
            return true
        }
        return false
    }

    function isMidiKickoffActive() {
        return midiPrimeInFlightRef.current
            || countInPendingRef.current
            || isRhythmHandoffPhase()
            || !!primePromiseRef.current
    }

    function clearForcedPlaybackIntent() {
        forcePlaybackUntilStartRef.current = false
    }

    function clearPlaybackFinished() {
        playbackFinishedRef.current = false
    }

    function clearDeferredNaturalEndCheck() {
        if (deferredNaturalEndTimerRef.current) {
            clearTimeout(deferredNaturalEndTimerRef.current)
            deferredNaturalEndTimerRef.current = null
        }
    }

    function clearDeferredNaturalEndRecovery() {
        clearDeferredNaturalEndCheck()
        deferredNaturalEndAttemptRef.current = 0
    }

    function scheduleDeferredNaturalEndCheck(delayMs) {
        clearDeferredNaturalEndCheck()
        deferredNaturalEndAttemptRef.current += 1
        if (deferredNaturalEndAttemptRef.current > 40) return
        deferredNaturalEndTimerRef.current = setTimeout(function() {
            deferredNaturalEndTimerRef.current = null
            if (shouldIgnoreMidiPlaybackCompletion({
                playbackFinished: playbackFinishedRef.current,
                deferToNative: shouldDeferSynthStopToNative(),
                guardActive: Date.now() < midiPlaybackGuardUntilRef.current,
            })) {
                return
            }
            if (isMidiAudioNearNaturalEnd()) {
                deferredNaturalEndAttemptRef.current = 0
                handlePlaybackCompletion()
            } else {
                scheduleDeferredNaturalEndCheck(150)
            }
        }, Math.max(0, delayMs || 0))
    }

    function clearPendingRepeatRestart() {
        if (metronomeTimeout.current) {
            clearTimeout(metronomeTimeout.current)
            metronomeTimeout.current = null
        }
    }

    /** TimingCallbacks can reach totalBeats before the audible buffer ends (two-part
     *  tunes, fill/tempo skew). Do not finish or Abc-loop until audio is near the end. */
    function isMidiAudioNearNaturalEnd() {
        const dur = gmidiBuffer.current && gmidiBuffer.current.duration > 0
            ? gmidiBuffer.current.duration
            : 0
        return isMidiBufferNearNaturalEnd(dur, getAudioClockPositionSec())
    }

    /** Buffer seconds from the audio clock; currentTime.current goes stale in hidden tabs. */
    function getAudioClockPositionSec() {
        const shifter = pitchShifterRef.current
        if (shifter && typeof shifter.getCurrentTime === 'function') {
            return shifter.getCurrentTime()
        }
        const anchor = nativeBufferClockRef.current
        const ctx = gaudioContext.current
        if (anchor && ctx) {
            return anchor.offsetSec + Math.max(0, ctx.currentTime - anchor.ctxTime)
        }
        return currentTime.current || 0
    }

    function isWebAudioOutputLive() {
        const shifter = pitchShifterRef.current
        if (shifter) {
            return typeof shifter.isConnected === 'function' && shifter.isConnected()
        }
        return !!nativeBufferClockRef.current
    }

    function finishNaturalPlayback(endRewindOpts) {
        if (playbackFinishedRef.current) return
        playbackFinishedRef.current = true
        clearPendingRepeatRestart()
        if (props.mediaController && props.mediaController.disarmPlaybackIntent) {
            props.mediaController.disarmPlaybackIntent()
        }
        stopPlaying()
        if (endRewindOpts && !shouldSuppressPlaybackEndSeek()) {
            seekPlayer(0, false, endRewindOpts)
        }
        if (props.onEnded) props.onEnded()
    }

    function handlePlaybackCompletion() {
        if (shouldIgnoreMidiPlaybackCompletion({
            playbackFinished: playbackFinishedRef.current,
            deferToNative: shouldDeferSynthStopToNative(),
            guardActive: Date.now() < midiPlaybackGuardUntilRef.current,
        })) {
            return
        }
        if (!isMidiAudioNearNaturalEnd()) {
            scheduleDeferredNaturalEndCheck(0)
            return
        }
        clearDeferredNaturalEndRecovery()

        const endRewindOpts = { skipAutoResume: true }
        const repeat = parseInt(props.repeat, 10)
        if (repeat === -1) {
            if (shouldSuppressPlaybackEndSeek() || props.forceFillOff) {
                finishNaturalPlayback(null)
            } else {
                clearPlaybackFinished()
                setForceStop(false)
                scheduleRepeatRestart()
            }
        } else if (repeat === 0) {
            finishNaturalPlayback(endRewindOpts)
        } else if (repeat > 0) {
            if (shouldScheduleMidiRepeatRestart(repeat, playCountRef.current, props.forceFillOff)) {
                incrementPlayCount()
                if (props.onRepeat) props.onRepeat(playCountRef.current + 1)
                forcePlaybackUntilStartRef.current = true
                setForceStop(false)
                clearPlaybackFinished()
                scheduleRepeatRestart()
            } else if (playCountRef.current < repeat - 1) {
                setPlayCount(0)
                finishNaturalPlayback(null)
            } else {
                setPlayCount(0)
                finishNaturalPlayback(endRewindOpts)
            }
        } else {
            finishNaturalPlayback(endRewindOpts)
        }
    }

    function handleMidiAudioStartFailure() {
        // Count-in can leave countInPending / rhythm handoff set while audio never
        // started; without clearing these, tap-to-play retries no-op in beginMidiPlayback.
        countInPendingRef.current = false
        countInMidiPreScheduledRef.current = false
        stopMetronome()
        pauseMidiSynth()
        setIsPlaying(false)
        clearForcedPlaybackIntent()
        if (props.mediaController) {
            releaseMidiUiLoading()
            props.mediaController.setIsPlaying(false)
        }
        if (getForceStop() || shouldQuietMidiPrimeFailure()) {
            return
        }
        if (needsMidiAudioContextUnlock()) {
            showMidiTapToPlay()
        } else if (props.mediaController && props.mediaController.reportNotationPrimeFailure) {
            props.mediaController.reportNotationPrimeFailure()
        } else {
            showMidiTapToPlay()
        }
    }

    function stopNativeMidiBufferOutput() {
        nativeBufferClockRef.current = null
        if (!gmidiBuffer.current) return
        try {
            // Prefer pause so the primed buffer stays restartable after natural end.
            // stop() tears down the AudioBufferSource and forces a full re-prime
            // (which races generation bumps and toasts "Could not load notation").
            if (gmidiBuffer.current.pause) {
                gmidiBuffer.current.pause()
            } else if (gmidiBuffer.current.isRunning) {
                gmidiBuffer.current.stop()
            }
        } catch (e) {}
    }

    function resolveMidiConnectAudioTime(startAtAudioTime) {
        const ctx = gaudioContext.current
        if (!ctx || !Number.isFinite(startAtAudioTime)) {
            return null
        }
        const now = ctx.currentTime
        // Only snap to now when the scheduled start is already past (or within
        // tiny scheduling jitter). Pulling future starts forward by up to 25ms
        // made notes lead the metronome; starting late without re-anchoring
        // left clicks ~10ms early vs the audible notes.
        if (startAtAudioTime <= now + 0.002) {
            return now
        }
        return startAtAudioTime
    }

    function startMidiAudioOutput(settings, ratio, startAtAudioTime) {
        if (shouldDeferSynthStopToNative()) {
            return { ok: false, actualStartAudioTime: null }
        }
        const startRatio = typeof ratio === 'number' ? ratio : 0
        // Starting within the last 1% only re-triggers an immediate end and,
        // with the playback guard, a reconnect loop (observed ratio≈0.9978).
        if (startRatio >= 0.99 && !Number.isFinite(startAtAudioTime)) {
            return { ok: false, actualStartAudioTime: null }
        }
        const ctx = gaudioContext.current
        const when = resolveMidiConnectAudioTime(startAtAudioTime)
        const targetAudible = Number.isFinite(startAtAudioTime) ? startAtAudioTime : null
        if (pitchShifterRef.current) {
            // Never run the native abcjs buffer player alongside the SoundTouch
            // shifter — both play the same rendered buffer and cause level drift.
            stopNativeMidiBufferOutput()
            // Reuse an in-flight connect aimed at the same instant (count-in
            // pre-schedule). Reconnecting here tore it down and restarted late.
            if (pitchShifterRef.current.isConnectedOrPending
                && pitchShifterRef.current.isConnectedOrPending()) {
                const existing = typeof pitchShifterRef.current.getScheduledConnectTime === 'function'
                    ? pitchShifterRef.current.getScheduledConnectTime()
                    : null
                if (typeof existing === 'number' && typeof targetAudible === 'number'
                    && Math.abs(existing - targetAudible) < 0.025) {
                    return { ok: true, actualStartAudioTime: existing }
                }
                // Already audible: do not tear down/reconnect for a same-position
                // start — that caused first-line seek thrash on /playMidi load.
                if (pitchShifterRef.current.isConnected() && targetAudible == null) {
                    const live = typeof pitchShifterRef.current.getCurrentTime === 'function'
                        ? pitchShifterRef.current.getCurrentTime()
                        : -1
                    const dur = pitchShifterRef.current.duration > 0
                        ? pitchShifterRef.current.duration
                        : 0
                    const liveRatio = dur > 0 && live >= 0 ? live / dur : -1
                    const samePos = liveRatio >= 0 && Math.abs(liveRatio - startRatio) < 0.01
                    // forceRatio:0 while already past the opening is a repeated
                    // auto-start, not a user rewind — keep the running buffer.
                    const spuriousRestartFromZero = startRatio < 0.01 && liveRatio > 0.02
                    if (samePos || spuriousRestartFromZero) {
                        pitchShifterRef.current.applySettings(settings.tempo, settings.pitch, settings.fineTune)
                        return { ok: true, actualStartAudioTime: existing != null ? existing : (ctx ? ctx.currentTime : null) }
                    }
                }
            }
            if (pitchShifterRef.current.isConnected()) {
                try { pitchShifterRef.current.disconnect() } catch (e) {}
            }
            pitchShifterRef.current.applySettings(settings.tempo, settings.pitch, settings.fineTune)
            // Always apply the position before connecting (including 0), so a
            // reconnect after a seek/rewind starts at the requested point. The
            // previous `ratio > 0` guard let rewind-to-0 keep the old position.
            if (typeof ratio === 'number' && ratio >= 0) {
                pitchShifterRef.current.seek(ratio)
            }
            pitchShifterRef.current.connect(when)
            const connected = pitchShifterRef.current.isConnectedOrPending
                ? pitchShifterRef.current.isConnectedOrPending()
                : pitchShifterRef.current.isConnected()
            // Prefer the scheduled Web Audio start (not now) so late callers
            // that still pass a future `when` report the true audible time.
            const scheduledConnect = typeof pitchShifterRef.current.getScheduledConnectTime === 'function'
                ? pitchShifterRef.current.getScheduledConnectTime()
                : null
            const actualStart = typeof scheduledConnect === 'number'
                ? scheduledConnect
                : (when != null ? when : (ctx ? ctx.currentTime : null))
            if (props.mediaController && props.mediaController.playbackVolume !== undefined) {
                pitchShifterRef.current.setDirectOutputGain(false)
                pitchShifterRef.current.setOutputVolume(props.mediaController.playbackVolume)
            } else if (props.practiceReferenceGain != null && props.practiceReferenceGain >= 0) {
                pitchShifterRef.current.setDirectOutputGain(true)
                pitchShifterRef.current.setOutputVolume(Math.max(0, Math.min(1, props.practiceReferenceGain)))
            }
            midiPlaybackGuardUntilRef.current = Date.now() + 3000
            return connected
                ? { ok: true, actualStartAudioTime: actualStart }
                : { ok: false, actualStartAudioTime: null }
        }
        if (gmidiBuffer.current) {
            // Native abcjs buffer cannot be scheduled into the future — it starts now.
            gmidiBuffer.current.start()
            if (ctx) {
                const bufDur = gmidiBuffer.current.duration > 0 ? gmidiBuffer.current.duration : 0
                nativeBufferClockRef.current = {
                    ctxTime: ctx.currentTime,
                    offsetSec: Math.max(0, startRatio) * bufDur,
                }
            }
            return {
                ok: true,
                actualStartAudioTime: ctx ? ctx.currentTime : null,
            }
        }
        return { ok: false, actualStartAudioTime: null }
    }

    function tryResumeSynthAndStart() {
        if (hostOwnsAbcOutput()) return false
        if (getForceStop() || playbackFinishedRef.current) return false
        if (isSynthSeekGuardActive()) return false
        if (props.mediaController) {
            if (props.mediaController.isMediaPlaybackRoute
                && props.mediaController.isMediaPlaybackRoute()) {
                return false
            }
            if (props.mediaController.isLinkedMediaPlaybackInFlight
                && props.mediaController.isLinkedMediaPlaybackInFlight()
                && !(props.mediaController.isMidiPlaybackRoute
                    && props.mediaController.isMidiPlaybackRoute())) {
                return false
            }
            if (props.mediaController.requestedPlayState === 'playMedia'
                && !(props.mediaController.isMidiPlaybackRoute
                    && props.mediaController.isMidiPlaybackRoute())) {
                return false
            }
        }
        if (!wantsMidiPlayback()) return false
        if (gaudioContext.current) {
            if (gaudioContext.current.state === 'running') {
                startPlaying()
                return true
            }
            if (gaudioContext.current.state === 'suspended') {
                gaudioContext.current.resume().then(function() {
                    if (wantsMidiPlayback()) {
                        startPlaying()
                    }
                }).catch(function() {
                    if (props.mediaController && wantsMidiPlayback()) {
                        props.mediaController.setTapToPlay(true)
                        props.mediaController.setIsPlaying(false)
                        props.mediaController.setIsLoading(false)
                    }
                })
                return true
            }
        } else if (gvisualObj.current) {
            startPlaying()
            return true
        }
        return false
    }

    function getTapToPlay() {
        return props.mediaController ? props.mediaController.tapToPlay : tapToPlay
    }

    function getPlayCancelled() {
        return props.mediaController ? props.mediaController.playCancelled : playCancelled
    }

    function wantsMidiPlayback(force) {
        if (props.mediaController) {
            const midiRoute = !!(props.mediaController.isMidiPlaybackRoute
                && props.mediaController.isMidiPlaybackRoute())
            if (!midiRoute) {
                if (props.mediaController.requestedPlayState === 'playMedia') {
                    return false
                }
                if (props.mediaController.isLinkedMediaPlaybackInFlight
                    && props.mediaController.isLinkedMediaPlaybackInFlight()) {
                    return false
                }
                if (props.mediaController.isMediaPlaybackRoute && props.mediaController.isMediaPlaybackRoute()) {
                    return false
                }
            }
            if (force) {
                if (getForceStop() || playbackFinishedRef.current) return false
                return true
            }
            if (getForceStop() || playbackFinishedRef.current) return false
            if (props.mediaController.hasActivePlaybackIntent && props.mediaController.hasActivePlaybackIntent()) {
                return true
            }
            return !!props.mediaController.isPlaying
        }
        return !!force || !!isPlayingRef.current || forcePlaybackUntilStartRef.current
    }

    function isMidiPlaybackActive() {
        if (props.mediaController) {
            if (props.mediaController.isMidiPlaybackRoute && !props.mediaController.isMidiPlaybackRoute()) {
                return false
            }
            if (props.mediaController.isMediaPlaybackRoute && props.mediaController.isMediaPlaybackRoute()) {
                return false
            }
            if (props.mediaController.hasActivePlaybackIntent && props.mediaController.hasActivePlaybackIntent()) {
                return true
            }
            if (props.mediaController.isPlaying) return true
        }
        return !!isPlayingRef.current
    }
    
    
    const [abcTune, setAbcTune] = useState(props.abc);
    const [lastAbc, setLastAbc] = useState(null);
    const [lastTuneId, setLastTuneId] = useState(null);
    const [lastTempo, setLastTempo] = useState(null);
    const [lastBoost, setLastBoost] = useState(null);
    const [lastMediaLinkNumber, setLastMediaLinkNumber] = useState(null);
    const [lastPlaybackSpeed, setLastPlaybackSpeed] = useState(1)
    const lastMidiHashRef = useRef(null)
    const midiStartHandledRef = useRef(false)
    const [audioChangedHash, setAudioChangedHash] = useState(null)
    
    const [isPlaying, setIsPlayingState] = useState(false)
    function setIsPlaying(v) {
      isPlayingRef.current = !!v
      setIsPlayingState(v)
    }
    const [isLastPlaying, setIsLastPlaying] = useState(false)
    //var [milliSecondsPerMeasure,setMilliSecondsPerMeasure] = useState(null)
    const [playCount, setPlayCountInner] = useState(0)
    const playCountRef = useRef(0)
    
    function setPlayCount(v) {
      setPlayCountInner(v)
      playCountRef.current = v
    }
    function incrementPlayCount() {
      setPlayCount(playCountRef.current + 1)
    }
    
    const lastScrollTo = useRef(0)
    const autoScroll = useRef(false)
    const autoScrollBlock = useRef('nearest')
    const lastPrimedMelodyOnlyRef = useRef(null)
    const lastPrimedMidiTransposeRef = useRef(null)
    const lastPrimedAudioHashRef = useRef(null)

    function resolveMidiTransposeSemitones(synthObj, tuneForTranspose) {
      // Prefer UI prop: visualObj can lag one frame behind a transpose commit.
      if (props.visualTranspose != null && props.visualTranspose !== '') {
        const fromProps = Number(props.visualTranspose)
        if (Number.isFinite(fromProps)) return fromProps
      }
      if (synthObj && synthObj.visualTranspose != null && synthObj.visualTranspose !== '') {
        const fromSynth = Number(synthObj.visualTranspose)
        if (Number.isFinite(fromSynth)) return fromSynth
      }
      if (tuneForTranspose && tuneForTranspose.transpose != null && tuneForTranspose.transpose !== '') {
        const fromTune = Number(tuneForTranspose.transpose)
        if (Number.isFinite(fromTune)) return fromTune
      }
      return 0
    }
    const realProgress = useRef(0) // updated by onplaying events
    // keep a copy as a ref to be available for lookup in callbacks
    
   
    //const [isWaiting, setIsWaiting] = useState(false)
    const [seekTo, setSeekTo] = useState(false)
    const [forceSeekTo, setForceSeekTo] = useState(false)
    const [ready, setReady] = useState(false)
    const [started, setStarted] = useState(false)
  
    const store = localForage.createInstance({
      name: "abcaudiocache"
    });
    const abcTools = useAbcTools()
    const inputEl = useRef(null);
    const playTimerRef = useRef(null);
    
    function setAudioContext(v) {
      gaudioContext.current = v
    }
    function setMidiBuffer(v) {
      gmidiBuffer.current = v
    }
    function setVisualObj(v) {
      gvisualObj.current = v
    }
    function setPlaybackVisualObj(v) {
      gplaybackVisualObj.current = v
    }
    function synthVisualObj(displayVisualObj) {
      return gplaybackVisualObj.current || displayVisualObj
    }
    function setTimingCallbacks(v) {
      gtimingCallbacks.current = v
    }
    function setCursor(v) {
      gcursor.current = v
    }
    
      
    function setForceStop(val) {
        forceStopRef.current = !!val
    }
    function getForceStop() {
        return forceStopRef.current
    }
    
     
    
    function getWarp() {
      if (props.warp > 0) {
        return parseInt(props.warp * 100)/100
      } else {
        return 1
      }
    }
    
    function getWarpTempo() {
      return parseInt(tune.tempo * getWarp())
    }
    
    
    // abcjs programOffsets trim instrument attack transients (milliseconds), not
    // volume. Large offsets on melody instruments (esp. violin) can make short
    // melody notes nearly inaudible against sustained chord pads.
    const programOffsets = {
        "bright_acoustic_piano": 55,
        "honkytonk_piano": 55,
        "electric_piano_1": 45,
        "electric_piano_2": 45,
        "harpsichord": 40,
        "clavinet": 20,
        "celesta": 20,
        "glockenspiel": 40,
        "vibraphone": 30,
        "marimba": 35,
        "xylophone": 30,
        "tubular_bells": 35,
        "dulcimer": 30,
        "drawbar_organ": 20,
        "percussive_organ": 25,
        "rock_organ": 20,
        "church_organ": 40,
        "reed_organ": 40,
        "accordion": 40,
        "harmonica": 40,
        "acoustic_guitar_nylon": 15,
        "acoustic_guitar_steel": 20,
        "electric_guitar_jazz": 25,
        "electric_guitar_clean": 15,
        "electric_guitar_muted": 35,
        "overdriven_guitar": 25,
        "distortion_guitar": 20,
        "guitar_harmonics": 30,
        "electric_bass_finger": 15,
        "electric_bass_pick": 30,
        "fretless_bass": 40,
        "violin": 35,
        "viola": 30,
        "cello": 30,
        "contrabass": 40,
        "trumpet": 10,
        "trombone": 90,
        "alto_sax": 15,
        "tenor_sax": 15,
        "clarinet": 15,
        "flute": 18,
        "tin_whistle": 15,
        "recorder": 18,
        "banjo": 30,
        "mandolin": 25,
        "woodblock": 20,
     }
     
     
    function isSynthSeekGuardActive() {
        if (!props.mediaController) return false
        if (props.mediaController.isSeekGuardActive && props.mediaController.isSeekGuardActive()) {
            return true
        }
        if (props.mediaController.shouldSuppressSpuriousPause
            && props.mediaController.shouldSuppressSpuriousPause()) {
            return true
        }
        return false
    }

    /**
     * ExoPlayer (Android app) or the hosted <audio> player (mobile web) plays
     * notation for the media controller; this synth is then display/cursor only.
     */
    function hostOwnsAbcOutput() {
        if (prefersNativeMediaPlayback()) return true
        const mc = props.mediaController
        return !!(mc && typeof mc.hostOwnsMidiOutput === 'function' && mc.hostOwnsMidiOutput())
    }

    function isMidiActivePlaybackRoute() {
        if (!props.mediaController || !props.mediaController.isMidiPlaybackRoute) {
            return true
        }
        return props.mediaController.isMidiPlaybackRoute()
    }

    function shouldPreserveNotationMidiEngines() {
        if (!props.mediaController || !props.mediaController.notationMidiOwner) return false
        if (props.mediaController.hasActivePlaybackIntent
            && props.mediaController.hasActivePlaybackIntent()) {
            return true
        }
        return false
    }

    function shouldSkipMidiEngineResetOnLinkChange(newLink, prevLink) {
        // Switching from a media link to notation MIDI must not tear down the
        // synth while play() is handing off from stopLinkedMediaPlayback().
        if (newLink != null || prevLink == null) return false
        if (!props.mediaController || !props.mediaController.isMidiPlaybackRoute) return false
        if (!props.mediaController.isMidiPlaybackRoute()) return false
        return !!(props.mediaController.hasActivePlaybackIntent
            && props.mediaController.hasActivePlaybackIntent())
    }

    const mediaController = props.mediaController
    const mcIsPlaying = mediaController ? mediaController.isPlaying : null
    const mcMediaLinkNumber = mediaController ? mediaController.mediaLinkNumber : null
    const mcMidiHashCurrent = mediaController && mediaController.midiHash ? mediaController.midiHash.current : null
    const mcTuneId = mediaController && mediaController.tune ? mediaController.tune.id : null
    const mcRequestedPlayState = mediaController ? mediaController.requestedPlayState : null
    const mcPlaybackRouteMode = mediaController ? mediaController.playbackRouteMode : null

      //// listen to properties on media controller to control local player
    useEffect(function() {
        // Display-only notation must not drive or reset the shared midi engine.
        if (props.playbackEngine === false) return
        const mc = props.mediaController
        if (!mc) return

        const mediaRouteActive = mc.isMediaPlaybackRoute && mc.isMediaPlaybackRoute()
        const midiRouteActive = mc.isMidiPlaybackRoute && mc.isMidiPlaybackRoute()
        const mediaInFlight = !midiRouteActive
            && mc.isLinkedMediaPlaybackInFlight && mc.isLinkedMediaPlaybackInFlight()
        const mediaRequested = !midiRouteActive && (
            mc.requestedPlayState === 'playMedia' || mc.playbackRouteMode === 'media'
        )

        if (mediaRouteActive || mediaInFlight || mediaRequested) {
            if (mediaRouteActive && mc.mediaLinkNumber !== lastMediaLinkNumber) {
                stopPlaying()
                resetAudioState()
            }
            setLastTuneId(mc.tune ? mc.tune.id : null)
            setClickSeek(mc.clickSeek)
            setLastPlaybackSpeed(mc.playbackSpeed)
            setIsLastPlaying(mc.isPlaying)
            setLastMediaLinkNumber(mc.mediaLinkNumber)
            return
        }

        //props.mediaController.isPlaying, isLastPlaying,"TIME", props.mediaController.currentTime,"CLICKTIME", props.mediaController.clickSeek,clickSeek,  props.mediaController.mediaLinkNumber, props.mediaController.midiHash.current, props.mediaController.mediaLinkNumber,lastMediaLinkNumber)
        if (mc.isMidiPlaybackRoute && mc.isMidiPlaybackRoute()) {
            const currentMidiHash = mc.midiHash ? mc.midiHash.current : null
            if (lastMidiHashRef.current !== undefined && currentMidiHash !== lastMidiHashRef.current) {
                stopRhythmPlayback()
                destroyAudioEngines()
                const pendingMidiPlay = mc.pendingMidiPlayRef && mc.pendingMidiPlayRef.current
                if (pendingMidiPlay
                    && !hasPendingNotationSeek()
                    && mc.hasActivePlaybackIntent
                    && mc.hasActivePlaybackIntent()
                    && !isSynthSeekGuardActive()) {
                    startPlayingFromIntent(true)
                }
            }
            lastMidiHashRef.current = currentMidiHash
            if (mc.playbackSpeed !== lastPlaybackSpeed) {
                syncPitchTempoSettingsFromController()
                pitchTempoSettingsRef.current.tempo = mc.playbackSpeed > 0
                    ? parseFloat(mc.playbackSpeed) : 1
            }
            var nowTuneId = mc.tune ? mc.tune.id : null
            if (nowTuneId !== lastTuneId) {
                syncPitchTempoSettingsFromController()
                if (!shouldPreserveNotationMidiEngines()) {
                    resetAudioState()
                }
                if (mc.hasActivePlaybackIntent
                    && mc.hasActivePlaybackIntent()
                    && !isSynthSeekGuardActive()) {
                        startPlayingFromIntent(true)
                } 
                
            }
            if (mc.mediaLinkNumber !== lastMediaLinkNumber) {
                const newLink = mc.mediaLinkNumber
                const prevLink = lastMediaLinkNumber
                if (!shouldPreserveNotationMidiEngines()
                    && !shouldSkipMidiEngineResetOnLinkChange(newLink, prevLink)) {
                    resetAudioState()
                }
                if (mc.hasActivePlaybackIntent
                    && mc.hasActivePlaybackIntent()
                    && !isSynthSeekGuardActive()
                    && !(mc.isLinkedMediaPlaybackInFlight && mc.isLinkedMediaPlaybackInFlight())) {
                    startPlayingFromIntent()
                }
            } 

            if (mc.isPlaying !== isLastPlaying) {
                const nativeOwnsOutput = hostOwnsAbcOutput()
                    && shouldDeferSynthStopToNative()
                if (!nativeOwnsOutput) {
                    if (mc.isPlaying) {
                        if (getForceStop() || playbackFinishedRef.current) {
                            midiStartHandledRef.current = false
                        } else if (midiStartHandledRef.current || isSynthSeekGuardActive()) {
                            midiStartHandledRef.current = false
                        } else {
                            startPlayingFromIntent()
                        }
                    } else {
                        if (!isSynthSeekGuardActive()) {
                            midiStartHandledRef.current = false
                            pauseMidiSynth()
                        }
                    }
                }
            }

            setLastTuneId(mc.tune ? mc.tune.id : null)
            setClickSeek(mc.clickSeek)
            setLastPlaybackSpeed(mc.playbackSpeed)
            setIsLastPlaying(mc.isPlaying)
            setLastMediaLinkNumber(mc.mediaLinkNumber)
        }
        //return function cleanup() {
           //resetAudioState()
        //}
        
    // eslint-disable-next-line react-hooks/exhaustive-deps -- syncs synth playback from mediaController snapshot fields above
    },[mcIsPlaying, mcMediaLinkNumber, mcMidiHashCurrent, mcTuneId, mcRequestedPlayState, mcPlaybackRouteMode])
    function getMidiPlaybackSeconds() {
        // Prefer the audio-context-anchored clock so the playhead (and mirrored
        // notation cursor) advance between beatCallback ticks. Falling back to
        // the beat-quantized currentTime.current left 2/4 cursors up to half a
        // bar behind the audible notes until the next beat fired.
        return getRhythmMusicSeconds()
    }

    useEffect(function() {
        if (props.mediaController && props.mediaController.playbackVolume !== undefined) return
        if (props.practiceReferenceGain == null || props.practiceReferenceGain < 0) return
        if (pitchShifterRef.current) {
            pitchShifterRef.current.setDirectOutputGain(true)
            pitchShifterRef.current.setOutputVolume(Math.max(0, Math.min(1, props.practiceReferenceGain)))
        }
    }, [props.practiceReferenceGain, props.mediaController])

    // Drop primed MIDI when UI transpose changes so the next play rebuilds at the new pitch.
    // Do not tear down mid-playback; startPlaying also invalidates a stale transpose buffer.
    useEffect(function() {
        if (props.playbackEngine === false) return
        if (isMidiKickoffActive()) return
        if (isMidiPlaybackActive()) return
        const want = resolveMidiTransposeSemitones(
            gvisualObj.current,
            (props.mediaController && props.mediaController.tune) || null
        )
        if (lastPrimedMidiTransposeRef.current == null) return
        if (lastPrimedMidiTransposeRef.current === want) return
        try { destroyAudioEngines() } catch (e) {}
    }, [props.visualTranspose])
    
    const mcTapToPlay = mediaController ? mediaController.tapToPlay : tapToPlay
    const mcPlayCancelled = mediaController ? mediaController.playCancelled : playCancelled

     useEffect(function() {
         if (props.playbackEngine === false) return
         if (hostOwnsAbcOutput()) return
         if (isSynthSeekGuardActive()) return
         if (!props.mediaController) return
         if (props.mediaController.isMediaPlaybackRoute
             && props.mediaController.isMediaPlaybackRoute()) {
             return
         }
         if (props.mediaController.isLinkedMediaPlaybackInFlight
             && props.mediaController.isLinkedMediaPlaybackInFlight()
             && !(props.mediaController.isMidiPlaybackRoute
                 && props.mediaController.isMidiPlaybackRoute())) {
             return
         }
         if (props.mediaController.requestedPlayState === 'playMedia'
             && !(props.mediaController.isMidiPlaybackRoute
                 && props.mediaController.isMidiPlaybackRoute())) {
             return
         }
         if (props.mediaController.isMidiPlaybackRoute
             && props.mediaController.isMidiPlaybackRoute()) {
             if (!getTapToPlay() && !getPlayCancelled()) {
                 const wantsPlay = props.mediaController.hasActivePlaybackIntent
                     ? props.mediaController.hasActivePlaybackIntent()
                     : props.mediaController.isPlaying
                 if (!wantsPlay) return
                 tryResumeSynthAndStart()
             }
         }
     // eslint-disable-next-line react-hooks/exhaustive-deps -- resume synth when tap/play flags change; helpers read latest controller state
     },[
         mcTapToPlay,
         mcPlayCancelled,
         mcMediaLinkNumber,
         mcRequestedPlayState,
         mcPlaybackRouteMode,
     ])

    useLayoutEffect(function() {
        if (!mediaControllerRef.current) return undefined
        if (props.playbackEngine === false) return undefined
        const mc = mediaControllerRef.current
        assignMediaControllerRef(mc, 'applyMidiTempoRef', applyMidiPlaybackSettings)
        assignMediaControllerRef(mc, 'applyPlaybackSettingsLiveRef', applyMidiPlaybackSettings)
        assignMediaControllerRef(mc, 'applyPlaybackVolumeRef', applySynthPlaybackVolume)
        assignMediaControllerRef(mc, 'resumeSynthAudioContextRef', resumeSynthAudioContext)
        assignMediaControllerRef(mc, 'getSynthAudioContextRef', function() {
            return gaudioContext.current || null
        })
        assignMediaControllerRef(mc, 'pauseSynthRef', pauseMidiSynth)
        assignMediaControllerRef(mc, 'suspendSynthAudioContextForNativeRef', suspendSynthAudioContextForNative)
        assignMediaControllerRef(mc, 'stopMetronomeRef', stopMetronome)
        assignMediaControllerRef(mc, 'invalidatePendingMidiStartsRef', invalidatePendingMidiStarts)
        assignMediaControllerRef(mc, 'abortMidiEnginesRef', abortMidiEngines)
        assignMediaControllerRef(mc, 'armPlaybackFromZeroRef', armPlaybackFromZero)
        assignMediaControllerRef(mc, 'getRhythmPlaybackPhaseRef', function() {
            return getRhythmPlaybackPhase(getRhythmController())
        })
        assignMediaControllerRef(mc, 'getRhythmDiagnosticsRef', getRhythmDiagnostics)
        assignMediaControllerRef(mc, 'playMidiRef', playMidiBridgeRef.current)
        if (mc.clearMidiEngineRegistrationFallback) {
            mc.clearMidiEngineRegistrationFallback()
        }
        let consumedPending = false
        if (mc.flushPendingPlayRequest) {
            consumedPending = mc.flushPendingPlayRequest()
        }
        if (!consumedPending && mc.kickPlaybackAfterEngineReady) {
            mc.kickPlaybackAfterEngineReady()
        }
        assignMediaControllerRef(mc, 'resumeMidiAfterSeekRef', resumeMidiAfterSeek)
        assignMediaControllerRef(mc, 'stopMidiSynthRef', stopMidiSynth)
        assignMediaControllerRef(mc, 'getMidiPlaybackSecondsRef', getMidiPlaybackSeconds)
        assignMediaControllerRef(mc, 'getMidiCursorSecondsRef', getCursorMusicSeconds)
        assignMediaControllerRef(mc, 'getAudibleMsPerMeasureRef', function() {
            return getEffectiveMsPerMeasure()
        })
        assignMediaControllerRef(mc, 'seekMidiRef', seekMidiPlayback)
        assignMediaControllerRef(mc, 'isMidiKickoffActiveRef', isMidiKickoffActive)
        return function() {
            if (mc.playMidiRef && mc.playMidiRef.current === playMidiBridgeRef.current) {
                mc.playMidiRef.current = null
            }
        }
    }, [props.playbackEngine])

    const mcAbc = props.abc
    useEffect(function() {
        if (props.playbackEngine === false || !mediaControllerRef.current) return undefined
        const mc = mediaControllerRef.current
        const pendingMidiPlay = mc.pendingMidiPlayRef && mc.pendingMidiPlayRef.current
        if (!pendingMidiPlay || !gvisualObj.current || !beginMidiPlaybackRef.current) {
            return undefined
        }
        if (mc.isMidiPlaybackRoute && !mc.isMidiPlaybackRoute()) return undefined
        if (!(mc.hasActivePlaybackIntent && mc.hasActivePlaybackIntent())) return undefined
        if (isMidiKickoffActive()) {
            if (primePromiseRef.current || countInPendingRef.current || isRhythmHandoffPhase()) {
                return undefined
            }
            clearStaleMidiKickoffLock()
        }
        if (mc.pendingMidiPlayRef) {
            mc.pendingMidiPlayRef.current = null
        }
        beginMidiPlaybackRef.current(pendingMidiPlay)
        return undefined
    }, [props.playbackEngine, mcAbc, ready])

    useLayoutEffect(function() {
        if (!props.playbackControlRef) return undefined
        const controlRef = props.playbackControlRef
        const owner = {}
        const api = {
            pause: function() {
                pauseMidiSynth()
                setIsPlaying(false)
            },
            resume: function() {
                midiPlaybackGuardUntilRef.current = 0
                forcePlaybackUntilStartRef.current = true
                setForceStop(false)
                resumeSynthAudioContext()
                if (beginMidiPlaybackRef.current) {
                    beginMidiPlaybackRef.current({ resume: true })
                }
            },
            restart: function() {
                midiPlaybackGuardUntilRef.current = 0
                forcePlaybackUntilStartRef.current = true
                setForceStop(false)
                setPlayCount(0)
                resumeSynthAudioContext()
                if (beginMidiPlaybackRef.current) {
                    beginMidiPlaybackRef.current({ restart: true })
                }
            },
            play: function(options) {
                midiPlaybackGuardUntilRef.current = 0
                forcePlaybackUntilStartRef.current = true
                setForceStop(false)
                resumeSynthAudioContext()
                if (beginMidiPlaybackRef.current) {
                    return beginMidiPlaybackRef.current(options || {})
                }
                return false
            },
            stop: function() {
                midiPlaybackGuardUntilRef.current = 0
                pauseMidiSynth()
                setForceStop(true)
                setIsPlaying(false)
            },
            getAudioContext: function() {
                return gaudioContext.current || null
            },
            __owner: owner,
        }
        controlRef.current = api
        return function() {
            if (controlRef.current && controlRef.current.__owner === owner) {
                controlRef.current = null
            }
        }
    }, [props.playbackControlRef])

    function resolveDisplayTuneId() {
        if (props.displayTuneId != null) return props.displayTuneId
        if (props.mediaController && props.mediaController.tune && props.mediaController.tune.id != null) {
            return props.mediaController.tune.id
        }
        return null
    }

    function canMirrorMidiPlaybackCursor() {
        return shouldMirrorMidiPlaybackCursor({
            mirrorNotationPlaybackCursor: props.mirrorNotationPlaybackCursor,
            playbackEngine: props.playbackEngine,
            mediaController: props.mediaController,
            displayTuneId: resolveDisplayTuneId(),
        })
    }

    function ensureMirrorTimingCallbacks() {
        if (!canMirrorMidiPlaybackCursor()) return false
        const visual = gvisualObj.current
        if (!visual) return false
        if (gtimingCallbacks.current && gtimingCallbacks.current.__mirrorVisual === visual) {
            return true
        }
        try {
            if (gtimingCallbacks.current) {
                try { gtimingCallbacks.current.pause() } catch (e) {}
            }
            // Display-only: no extraMeasures / no audio. Map music-only currentTime
            // onto the same noteTimings used to draw this SVG. Prefer QPM derived
            // from millisecondsPerMeasure so TimingCallbacks match audible bar length
            // (getBpm can disagree and shift note lookups by ~1 beat).
            const tempo = visual.metaText ? visual.metaText.tempo : null
            let scoreQpm = (typeof visual.getBpm === 'function' && visual.getBpm(tempo)) || 120
            try {
                const mpm = typeof visual.millisecondsPerMeasure === 'function'
                    ? parseFloat(visual.millisecondsPerMeasure())
                    : 0
                const meter = typeof visual.getMeterFraction === 'function'
                    ? visual.getMeterFraction()
                    : null
                const beats = meter && meter.num > 0 ? meter.num : 0
                if (mpm > 0 && beats > 0) {
                    const fromMpm = beats * 60000 / mpm
                    if (fromMpm > 0 && isFinite(fromMpm)) scoreQpm = fromMpm
                }
            } catch (e) { /* keep getBpm */ }
            const callbacks = new abcjs.TimingCallbacks(visual, {
                qpm: scoreQpm,
            })
            callbacks.__mirrorVisual = visual
            callbacks.__mirrorQpm = scoreQpm
            gtimingCallbacks.current = callbacks
            gcursor.current = null
            createCursor()
            return true
        } catch (e) {
            return false
        }
    }

    function syncMirroredPlaybackCursor() {
        if (!canMirrorMidiPlaybackCursor()) return
        if (!ensureMirrorTimingCallbacks()) return
        const mc = props.mediaController
        const timing = gtimingCallbacks.current
        if (!timing || !timing.noteTimings) return
        const stateSec = mc && mc.currentTime
        const nativeOwns = !!(mc && (
          (typeof mc.isAndroidNativeOutputActive === 'function' && mc.isAndroidNativeOutputActive())
          || (hostOwnsAbcOutput() && mc.isMidiPlaybackRoute && mc.isMidiPlaybackRoute()
            && (isAndroidNativePlayerActive() || isAbcNativePlayInFlight()))
        ))
        // While WAV is rendering, keep the staff cursor at 0 — Exo still holds the
        // paused prior media and would otherwise paint mid-score.
        const forceStart = !!(hostOwnsAbcOutput() && isAbcNativePlayInFlight())
        const cursorSec = (!nativeOwns && !forceStart && mc && mc.getMidiCursorSecondsRef
          && typeof mc.getMidiCursorSecondsRef.current === 'function')
          ? mc.getMidiCursorSecondsRef.current()
          : null
        // Prefer live engine clock (currentTimeRef via getPlaybackProgress) over
        // React state — state can lag a beat behind Exo while the scrubber/poll updates.
        let liveProgressSec = null
        if (!forceStart && mc && typeof mc.getPlaybackProgress === 'function') {
          try {
            const progress = mc.getPlaybackProgress()
            if (progress && progress.currentTime >= 0 && isFinite(progress.currentTime)) {
              liveProgressSec = progress.currentTime
            }
          } catch (e) { /* fall through */ }
        }
        const liveSec = forceStart
          ? 0
          : ((liveProgressSec != null)
          ? liveProgressSec
          : ((cursorSec != null && cursorSec >= 0 && isFinite(cursorSec))
          ? cursorSec
          : ((nativeOwns && stateSec >= 0 && isFinite(stateSec))
            ? stateSec
            : ((mc && mc.getMidiPlaybackSecondsRef
            && typeof mc.getMidiPlaybackSecondsRef.current === 'function')
            ? mc.getMidiPlaybackSecondsRef.current()
            : stateSec))))
        const playbackSec = (liveSec >= 0 && isFinite(liveSec)) ? liveSec : stateSec
        const meterFrac = gvisualObj.current && typeof gvisualObj.current.getMeterFraction === 'function'
          ? gvisualObj.current.getMeterFraction()
          : null
        const barWholeDbg = barWholeNotesFromMeter(meterFrac)
        const beatsDbg = meterFrac && meterFrac.num > 0 ? meterFrac.num : 0
        const beatWholeDbg = (barWholeDbg > 0 && beatsDbg > 0) ? (barWholeDbg / beatsDbg) : 0
        const pickupWholeDbg = (() => {
          const map = (mc && mc.soundingWrittenMapRef)
            ? mc.soundingWrittenMapRef.current
            : soundingWrittenMapRef.current
          if (map && map.pickupWhole != null) return map.pickupWhole
          return gvisualObj.current && typeof gvisualObj.current.getPickupLength === 'function'
            ? parseFloat(gvisualObj.current.getPickupLength()) || 0
            : 0
        })()
        // Prefer the primed audio bar length — display visual QPM often differs
        // from the buffer tempo (abcjs default 180 vs tune.tempo).
        let audibleMpm = 0
        if (mc && mc.getAudibleMsPerMeasureRef
            && typeof mc.getAudibleMsPerMeasureRef.current === 'function') {
          audibleMpm = parseFloat(mc.getAudibleMsPerMeasureRef.current()) || 0
        }
        if (!(audibleMpm > 0)) {
          const visual = gvisualObj.current
          audibleMpm = (visual && typeof visual.millisecondsPerMeasure === 'function')
            ? parseFloat(visual.millisecondsPerMeasure())
            : 0
        }
        const audioDurationSec = (mc && mc.duration > 0)
          ? parseFloat(mc.duration)
          : 0
        const cursorOpts = {
          musicSec: playbackSec,
          audibleMsPerMeasure: audibleMpm,
          audioDurationSec: audioDurationSec,
          lastMomentMs: timing.lastMoment,
          soundingWrittenMap: (mc && mc.soundingWrittenMapRef)
            ? mc.soundingWrittenMapRef.current
            : soundingWrittenMapRef.current,
          musicStartMs: 0,
          barWholeNotes: barWholeDbg,
          pickupWhole: pickupWholeDbg,
          trackNotePositions: false,
          // Staff bar jumps were consistently one beat late vs the sounding
          // downbeat on Android native MIDI — lead by one beat of whole-notes.
          barCursorPhaseLeadWhole: beatWholeDbg > 0 ? beatWholeDbg : (
            pickupWholeDbg > 0 ? pickupWholeDbg : 0
          ),
        }
        const root = inputEl && inputEl.current
        const svg = root ? root.querySelector('svg') : null
        const cursor = applyPlaybackCursorAtTime(
          svg,
          gcursor.current,
          timing.noteTimings,
          playbackSec * 1000,
          cursorOpts
        )
        if (cursor) setCursor(cursor)
        // Host-owned MIDI has no local TimingCallbacks beat/event scroll — keep the
        // mirrored staff line in view (needed when fit-height is off, e.g. page stack).
        if (autoScroll.current && cursor) {
          scrollPlaybackCursorIntoTopHalf(cursor, {
            isPlaying: true,
            noteTimings: timing.noteTimings,
            currentTimeMs: playbackSec * 1000,
            audibleMsPerMeasure: audibleMpm,
          })
        }
    }

    useLayoutEffect(function() {
        if (!props.mirrorNotationPlaybackCursor) return undefined
        const mc = props.mediaController
        if (!mc) return undefined
        function tick() {
            if (!isMidiPlaybackActive()) return
            syncMirroredPlaybackCursor()
        }
        tick()
        const intervalId = window.setInterval(tick, 50)
        if (props.staffDisplayControlRef) {
            props.staffDisplayControlRef.current = { syncCursor: tick }
        }
        if (mc.notationStaffCursorRef) {
            mc.notationStaffCursorRef.current = tick
        }
        return function() {
            window.clearInterval(intervalId)
            if (props.staffDisplayControlRef && props.staffDisplayControlRef.current
                && props.staffDisplayControlRef.current.syncCursor === tick) {
                props.staffDisplayControlRef.current = null
            }
            if (mc.notationStaffCursorRef && mc.notationStaffCursorRef.current === tick) {
                mc.notationStaffCursorRef.current = null
            }
        }
    }, [
        props.mirrorNotationPlaybackCursor,
        props.staffDisplayControlRef,
        props.mediaController,
        mcIsPlaying,
        mcTuneId,
    ])

    // Keep the engine cursor on the audio clock between TimingCallbacks beats.
    // Without this, rAF drift leaves the line on the previous bar until the next
    // beatCallback fires.
    useLayoutEffect(function() {
        if (props.playbackEngine === false) return undefined
        if (props.mirrorNotationPlaybackCursor) return undefined
        function tick() {
            if (!(isPlayingRef.current || isMidiPlaybackActive())) return
            syncEnginePlaybackCursor()
        }
        tick()
        const intervalId = window.setInterval(tick, 50)
        return function() {
            window.clearInterval(intervalId)
        }
    }, [
        props.playbackEngine,
        props.mirrorNotationPlaybackCursor,
        mcIsPlaying,
        mcTuneId,
    ])

    // Hidden tabs: finish/repeat/advance from the audio clock, then resync
    // TimingCallbacks to the audio position when the tab is shown again.
    const hiddenTabClockHandlersRef = useRef(null)
    hiddenTabClockHandlersRef.current = {
        isActive: function() {
            if (props.mirrorNotationPlaybackCursor) return false
            if (!(isPlayingRef.current || isMidiPlaybackActive())) return false
            if (playbackFinishedRef.current || getForceStop()) return false
            if (shouldDeferSynthStopToNative()) return false
            return isWebAudioOutputLive()
        },
        getDurationSec: function() {
            if (pitchShifterRef.current && pitchShifterRef.current.duration > 0) {
                return pitchShifterRef.current.duration
            }
            return gmidiBuffer.current && gmidiBuffer.current.duration > 0
                ? gmidiBuffer.current.duration
                : 0
        },
        getPositionSec: getAudioClockPositionSec,
        getRate: function() {
            const shifter = pitchShifterRef.current
            if (shifter && typeof shifter.getState === 'function') {
                return shifter.getState().tempo || 1
            }
            return 1
        },
        onNearEnd: function() {
            handlePlaybackCompletion()
        },
        onVisible: function() {
            if (!hiddenTabClockHandlersRef.current.isActive()) return
            const dur = hiddenTabClockHandlersRef.current.getDurationSec()
            if (!(dur > 0) || !gtimingCallbacks.current) return
            setTimingProgressFromAudioRatio(getAudioClockPositionSec() / dur)
            syncEnginePlaybackCursor()
        },
    }

    useEffect(function() {
        if (props.playbackEngine === false) return undefined
        if (typeof document === 'undefined') return undefined
        function handlers() {
            return hiddenTabClockHandlersRef.current
        }
        const clock = createHiddenTabPlaybackClock({
            isActive: function() { return handlers().isActive() },
            getDurationSec: function() { return handlers().getDurationSec() },
            getPositionSec: function() { return handlers().getPositionSec() },
            getRate: function() { return handlers().getRate() },
            onNearEnd: function() { handlers().onNearEnd() },
        })
        function onVisibilityChange() {
            if (document.hidden) return
            try { handlers().onVisible() } catch (e) {}
        }
        document.addEventListener('visibilitychange', onVisibilityChange)
        return function() {
            document.removeEventListener('visibilitychange', onVisibilityChange)
            clock.dispose()
        }
    }, [props.playbackEngine])
     
     function getRhythmController() {
        if (!rhythmController.current) {
            rhythmController.current = createRhythmPlaybackController(rhythmOutputBus.current)
        }
        return rhythmController.current
     }

     function rhythmPlaySlotFn(audioContext, audioTime, rhythm, slotInBar, destination, meta) {
        const controller = getRhythmController()
        if (meta && meta.generation != null && controller.generation !== meta.generation) {
            return
        }
        const phase = getRhythmPlaybackPhase(controller)
        if (phase === PHASE_ENTRY_GAP
            && meta && meta.globalSlot != null && meta.globalSlot >= 0) {
            return
        }
        const nowMusic = getRhythmMusicSeconds()
        recordRhythmSlotEvent(rhythmTimingDiagnostics.current, {
            slotInBar: slotInBar,
            globalSlot: meta && meta.globalSlot,
            audioTime: audioTime,
            expectedAudioTime: meta && meta.expectedAudioTime,
            musicSeconds: nowMusic,
            phase: phase,
        })
        playRhythmSlot(audioContext, audioTime, rhythm, slotInBar, destination)
     }

     function updateRhythmMusicAnchor(musicSeconds) {
        rhythmMusicAnchorRef.current = {
            musicSeconds: typeof musicSeconds === 'number' ? musicSeconds : 0,
            audioContextTime: gaudioContext.current ? gaudioContext.current.currentTime : 0,
            active: true,
        }
     }

     function clearRhythmMusicAnchor() {
        rhythmMusicAnchorRef.current.active = false
     }

     function getMidiEngineMode() {
        if (pitchShifterRef.current && pitchShifterRef.current.isConnected()) {
            const settings = pitchShifterRef.current.getState()
            const needsProcessing = Math.abs(combinedPitchSemitones(settings.pitch, settings.fineTune)) >= 0.0001
                || Math.abs(settings.tempo - 1) >= 0.0001
            return needsProcessing ? 'soundtouch' : 'direct'
        }
        if (gmidiBuffer.current && gmidiBuffer.current.isRunning) {
            return 'buffer'
        }
        return 'none'
     }

     function prepareFreshMidiStartFromCountIn() {
        clearRhythmMusicAnchor()
        if (gtimingCallbacks.current) {
            try {
                gtimingCallbacks.current.pause()
                gtimingCallbacks.current.setProgress(0)
            } catch (e) {}
        }
     }

     function getRhythmDiagnostics() {
        const rhythm = resolvePlaybackMetronomeRhythm()
        const o = gvisualObj.current
        const metro = resolvePlaybackMetronomeOptions()
        const timelineSnap = getRhythmTimelineSnapshot(getRhythmController())
        const ring = rhythmTimingDiagnostics.current.ring.slice()
        return buildRhythmDiagnosticsSnapshot({
            phase: getRhythmPlaybackPhase(getRhythmController()),
            tempo: getRhythmController().tempo,
            tempoFactor: getTempoFactor(),
            rhythmBeatsPerBar: rhythm.beatsPerBar,
            musicSeconds: getRhythmMusicSeconds(),
            rhythmGridQpm: getRhythmGridMetronomeTempo(o),
            playbackQpm: getPlaybackMetronomeTempo(o),
            duringPlayback: metro.duringPlayback,
            isMidiPlaying: isMidiAudioEngineRunning(),
            timingCallbacksRunning: !!(gtimingCallbacks.current && gtimingCallbacks.current.isRunning),
            midiEngineMode: getMidiEngineMode(),
            rhythmSlotsPerBar: slotsPerBar(rhythm),
            rhythmPulsesPerBeat: rhythm.pulsesPerBeat ? rhythm.pulsesPerBeat.slice() : [],
            downbeatAudioTime: timelineSnap ? timelineSnap.downbeatAudioTime : null,
            musicStartAudioTime: timelineSnap ? timelineSnap.musicStartAudioTime : null,
            rhythm: rhythm,
            swing: getRhythmSwing(rhythm),
            countInSlotsEmitted: rhythmTimingDiagnostics.current.countInSlotsEmitted,
            ring: ring,
        })
     }

     function stopRhythmPlayback() {
        clearRhythmMusicAnchor()
        resetRhythmTimingDiagnostics(rhythmTimingDiagnostics.current)
        stopRhythmPlaybackController(getRhythmController())
     }

     function getEffectiveMsPerMeasure(visualObj) {
        if (effectiveMsPerMeasureRef.current > 0) {
            return effectiveMsPerMeasureRef.current
        }
        if (primedMsPerMeasureRef.current > 0) {
            return primedMsPerMeasureRef.current
        }
        const o = visualObj || gvisualObj.current
        if (o && o.millisecondsPerMeasure) {
            return o.millisecondsPerMeasure()
        }
        return 0
     }

     function getMsPerMeasureForRhythmGrid(visualObj) {
        const o = visualObj || gvisualObj.current
        // Custom fill reprimes MIDI with inferred bar length; clicks must match
        // the rendered buffer, not abcjs visual timing alone.
        if (effectiveMsPerMeasureRef.current > 0) {
            return effectiveMsPerMeasureRef.current
        }
        return scoreMsPerMeasureForRhythmGrid(o, {
            fallbackMsPerMeasure: getEffectiveMsPerMeasure(o),
        })
     }

     function getRhythmGridMetronomeTempo(visualObj) {
        // Score BPM only — playback speed is applied via tempoFactor when
        // mapping music seconds onto the audio clock (see schedulePlayingSlots).
        const o = visualObj || gvisualObj.current
        const rhythm = resolvePlaybackMetronomeRhythm()
        const fallback = getBaseQpm()
        const msPerMeasure = getMsPerMeasureForRhythmGrid(o)
        const gridBpm = (!o || !(msPerMeasure > 0))
            ? fallback
            : computeRhythmGridTempo({
                rhythmBeatsPerBar: rhythm.beatsPerBar,
                millisecondsPerMeasure: msPerMeasure,
                tempoFactor: 1,
                fallbackQpm: fallback,
            })
        if (!o || !(msPerMeasure > 0)) {
            return fallback
        }
        return gridBpm
     }

     function getWallRhythmMetronomeTempo(visualObj) {
        const o = visualObj || gvisualObj.current
        const rhythm = resolvePlaybackMetronomeRhythm()
        const fallback = getBaseQpm()
        const msPerMeasure = getMsPerMeasureForRhythmGrid(o)
        if (!o || !(msPerMeasure > 0)) {
            return fallback
        }
        const factor = getTempoFactor()
        return computeRhythmGridTempo({
            rhythmBeatsPerBar: rhythm.beatsPerBar,
            millisecondsPerMeasure: msPerMeasure,
            tempoFactor: factor > 0 ? factor : 1,
            fallbackQpm: fallback,
        })
     }

     function armPlaybackFromZero() {
        pendingRestartFromZeroRef.current = true
        pendingPlaybackStartSecondsRef.current = null
        currentTime.current = 0
        fullyResetMidiEnginesToStart()
     }

     function clearArmPlaybackFromZero() {
        pendingRestartFromZeroRef.current = false
     }

     function isMidiAudioEngineRunning() {
        if (pitchShifterRef.current) {
            return pitchShifterRef.current.isConnected()
        }
        if (gmidiBuffer.current) {
            return !!gmidiBuffer.current.isRunning
        }
        return false
     }

     function getRhythmMusicSeconds() {
        const anchor = rhythmMusicAnchorRef.current
        let fromAnchor = null
        if (anchor.active && gaudioContext.current) {
            const elapsed = gaudioContext.current.currentTime - anchor.audioContextTime
            if (elapsed >= 0 && elapsed < 30) {
                const tempoFactor = getTempoFactor()
                fromAnchor = anchor.musicSeconds + elapsed * (tempoFactor > 0 ? tempoFactor : 1)
            } else {
                fromAnchor = anchor.musicSeconds
            }
        }
        const controller = getRhythmController()
        if (controller.timeline && getRhythmPlaybackPhase(controller) === PHASE_PLAYING) {
            if (pitchShifterRef.current && pitchShifterRef.current.isConnected()) {
                try {
                    const secs = pitchShifterRef.current.getCurrentTime()
                    if (Number.isFinite(secs) && secs >= 0) {
                        return secs
                    }
                } catch (e) {}
            }
            if (fromAnchor != null) {
                return fromAnchor
            }
        }
        if (pitchShifterRef.current && pitchShifterRef.current.isConnected()) {
            try {
                const secs = pitchShifterRef.current.getCurrentTime()
                if (Number.isFinite(secs) && secs >= 0) {
                    // SoundTouch can still report 0 briefly after a pre-scheduled downbeat.
                    if (fromAnchor != null && fromAnchor > secs + 0.02) {
                        return fromAnchor
                    }
                    return secs
                }
            } catch (e) {}
        }
        if (fromAnchor != null) {
            return fromAnchor
        }
        if (isMidiAudioEngineRunning() && typeof currentTime.current === 'number'
            && currentTime.current >= 0) {
            return currentTime.current
        }
        return currentTime.current || 0
     }

     /**
      * Music seconds for the red cursor. Prefer the audible buffer position and
      * never TimingCallbacks-derived currentTime when voltas are expanded — that
      * clock free-runs on the written score and races ahead of the beat.
      */
     function getCursorMusicSeconds() {
        const mc = props.mediaController
        if (hostOwnsAbcOutput() && mc
            && ((typeof mc.isAndroidNativeOutputActive === 'function' && mc.isAndroidNativeOutputActive())
              || isAndroidNativePlayerActive())) {
            const nativeSec = typeof mc.currentTime === 'number' ? mc.currentTime : null
            if (nativeSec != null && nativeSec >= 0 && isFinite(nativeSec)) {
                return nativeSec
            }
        }
        const shifter = pitchShifterRef.current
        const shifterLive = !!(shifter && (
          (typeof shifter.isConnectedOrPending === 'function' && shifter.isConnectedOrPending())
          || shifter.isConnected()
        ))
        if (shifterLive) {
            try {
                const s = shifter.getCurrentTime()
                if (Number.isFinite(s) && s >= 0) {
                    return s
                }
            } catch (e) {}
        }
        const fromRhythm = getRhythmMusicSeconds()
        if (Number.isFinite(fromRhythm) && fromRhythm >= 0
            && soundingWrittenMapRef.current
            && soundingWrittenMapRef.current.segments
            && soundingWrittenMapRef.current.segments.length) {
            return fromRhythm
        }
        if (typeof currentTime.current === 'number' && currentTime.current >= 0) {
            return currentTime.current
        }
        return fromRhythm >= 0 ? fromRhythm : 0
     }

     function rebuildPlaybackTimingMap(options) {
        const opts = options || {}
        const visual = gvisualObj.current
        if (!visual) {
            playbackTimingMapRef.current = null
            lastPlaybackTimingSampleRef.current = null
            return
        }
        const bufDur = opts.bufferDuration != null
          ? opts.bufferDuration
          : (gmidiBuffer.current && gmidiBuffer.current.duration)
        const scoreMs = getMsPerMeasureForRhythmGrid(visual)
        let mapVisual = visual
        try {
            mapVisual = expandVisualObjThroughVoltaRepeats(visual) || visual
        } catch (e) {
            mapVisual = visual
        }
        playbackTimingMapRef.current = buildPlaybackTimingMap(mapVisual, {
            bufferDuration: bufDur,
            millisecondsPerMeasureOverride: scoreMs > 0 ? scoreMs : 0,
        })
        lastPlaybackTimingSampleRef.current = null
     }

     function resolveRhythmBeatBpmFromMap(at) {
        const visual = gvisualObj.current
        const scoreMs = getMsPerMeasureForRhythmGrid(visual)
        if (scoreMs > 0) {
            return getRhythmGridMetronomeTempo(visual)
        }
        return at.rhythmBeatBpm
     }

     function syncPlaybackTimingFromMap(force) {
        const map = playbackTimingMapRef.current
        if (!map) return
        if (getRhythmPlaybackPhase(getRhythmController()) !== PHASE_PLAYING) return
        const musicSeconds = getRhythmMusicSeconds()
        const at = timingAtMusicSeconds(map, musicSeconds)
        const targetRhythmBpm = resolveRhythmBeatBpmFromMap(at)
        const last = lastPlaybackTimingSampleRef.current
        const controller = getRhythmController()
        const tempoFactor = getTempoFactor()
        const meterChanged = !!force || !last || last.meterText !== at.meterText
        // Count-in leaves a free-running wall-tempo timeline. Do not retarget
        // score BPM onto that timeline (scrambles clicks when speed ≠ 1x, or
        // when getBpm drifts from millisecondsPerMeasure). Still apply meter
        // changes so mid-tune M: updates keep the click pattern correct.
        if (controller.timeline) {
          if (meterChanged && at.rhythm && !force) {
            setRhythmPlaybackRhythm(controller, at.rhythm)
          }
          lastPlaybackTimingSampleRef.current = {
            meterText: at.meterText,
            rhythmBeatBpm: targetRhythmBpm,
          }
          return
        }
        const tempoChanged = !!force
            || !last
            || Math.abs(last.rhythmBeatBpm - targetRhythmBpm) > 0.5
        if (tempoChanged) {
            setRhythmPlaybackTempo(controller, targetRhythmBpm)
        }
        if (meterChanged) {
            setRhythmPlaybackRhythm(controller, at.rhythm)
        }
        lastPlaybackTimingSampleRef.current = {
            meterText: at.meterText,
            rhythmBeatBpm: targetRhythmBpm,
        }
     }

     function isRhythmHandoffPhase() {
        // Only true count-in / entry-gap — not PHASE_PLAYING. Treating playing as
        // handoff blocked startPlaying/restart while the during-playback metronome
        // ran (UI stuck on waiting; play returned true without starting).
        const phase = getRhythmPlaybackPhase(getRhythmController())
        return phase === PHASE_COUNT_IN || phase === PHASE_ENTRY_GAP
     }

     function ensureDuringPlaybackMetronome(ratio) {
        const metro = resolvePlaybackMetronomeOptions()
        if (!metro.duringPlayback) return
        if (!gaudioContext.current) return
        if (countInPendingRef.current) return
        const phase = getRhythmPlaybackPhase(getRhythmController())
        if (phase === PHASE_COUNT_IN || phase === PHASE_ENTRY_GAP || phase === PHASE_PLAYING) {
            return
        }
        if (metro.metronomeCountIn && phase === PHASE_IDLE) {
            return
        }
        if (!isMidiAudioEngineRunning()) {
            return
        }
        const musicSeconds = getMusicSecondsForMetronomeSync(ratio)
        const controller = getRhythmController()
        const snap = getRhythmTimelineSnapshot(controller)
        const tempoFactor = getTempoFactor()
        const factor = tempoFactor > 0 ? tempoFactor : 1
        const musicStartAudioTime = snap && snap.musicStartAudioTime != null
            ? snap.musicStartAudioTime
            : gaudioContext.current.currentTime - musicSeconds / factor
        enterRhythmPlaying(controller, {
            rhythm: resolvePlaybackMetronomeRhythm(),
            tempo: getWallRhythmMetronomeTempo(),
            audioContext: gaudioContext.current,
            musicSeconds: musicSeconds,
            musicStartAudioTime: musicStartAudioTime,
            // Mid-tune entry is not anacrusis — clear leftover pickup slot.
            musicStartSlot: 0,
            playSlot: rhythmPlaySlotFn,
            getMusicSeconds: getRhythmMusicSeconds,
            getTempoFactor: getTempoFactor,
        })
        updateRhythmMusicAnchor(musicSeconds)
     }

     function reconcileDuringPlaybackRhythm() {
        const metro = resolvePlaybackMetronomeOptions()
        if (!metro.duringPlayback) return
        if (!isMidiAudioEngineRunning()) return
        if (isRhythmPlaybackActive(getRhythmController())) return
        ensureDuringPlaybackMetronome(getMidiPlaybackRatio())
     }

     function stopMetronome() {
        countInPendingRef.current = false
        midiPrimeInFlightRef.current = false
        countInMidiPreScheduledRef.current = false
        if (metronomeTimeout.current) {
            clearTimeout(metronomeTimeout.current)
            metronomeTimeout.current = null
        }
        stopRhythmPlayback()
     }

     function getMusicSecondsForMetronomeSync(ratio) {
        if (rhythmMusicAnchorRef.current.active || (typeof currentTime.current === 'number' && currentTime.current >= 0)) {
            return getRhythmMusicSeconds()
        }
        if (gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
            const r = ratio !== undefined && ratio !== null
                ? parseFloat(ratio)
                : getMidiPlaybackRatio()
            return Math.max(0, r * gmidiBuffer.current.duration)
        }
        return getMidiPlaybackSeconds()
     }

     function invalidatePendingMidiStarts() {
        const prevGen = playbackGenerationRef.current
        playbackGenerationRef.current += 1
        latchMidiPrimeCancel(2000)
        stopMetronome()
        if (primeTimerRef.current) {
            clearTimeout(primeTimerRef.current)
            primeTimerRef.current = null
        }
        primePromiseRef.current = null
        midiPrimeInFlightRef.current = false
        isLoading.current = false
        if (props.mediaController) {
            const keepLoading = props.mediaController.hasPlayingIntent
                && props.mediaController.hasPlayingIntent()
            if (!keepLoading && props.mediaController.setIsLoading) {
                props.mediaController.setIsLoading(false)
            }
        }
     }

     function abortMidiEngines() {
        invalidatePendingMidiStarts()
        forceDestroyAudioEngines()
     }

     function isPlaybackGenerationCurrent(generation) {
        return generation === playbackGenerationRef.current
     }

     function scheduleMidiStartAfterDelay(generation, delayMs, startOptions) {
        if (metronomeTimeout.current) {
            clearTimeout(metronomeTimeout.current)
            metronomeTimeout.current = null
        }
        metronomeTimeout.current = setTimeout(function() {
            metronomeTimeout.current = null
            if (!isPlaybackGenerationCurrent(generation)) {
                releaseMidiUiLoading()
                return
            }
            if (!wantsMidiPlayback(true)) {
                releaseMidiUiLoading()
                return
            }
            startMidiAndTiming(startOptions)
        }, delayMs)
     }

     function getRepeatGapMs() {
        const gapBeats = parseFloat(props.repeatGapBeats)
        if (!gapBeats || gapBeats <= 0) return 0
        const o = gvisualObj.current
        const msPerMeasure = getEffectiveMsPerMeasure(o)
        if (!o || !o.getBeatsPerMeasure || !(msPerMeasure > 0)) return 0
        const beatsPerMeasure = parseFloat(o.getBeatsPerMeasure()) || 0
        if (beatsPerMeasure <= 0) return 0
        const tempoFactor = getTempoFactor()
        const beatDurationMs = (msPerMeasure / beatsPerMeasure) / tempoFactor
        return gapBeats * beatDurationMs
     }

     function scheduleRepeatRestart() {
        const gapMs = getRepeatGapMs()
        if (gapMs <= 0) {
            seekPlayer(0, true)
            return
        }
        pauseMidiSynth()
        if (gtimingCallbacks.current) {
            try { gtimingCallbacks.current.pause() } catch (e) {}
        }
        if (gmidiBuffer.current) {
            try { gmidiBuffer.current.seek(0) } catch (e) {}
        }
        if (pitchShifterRef.current) {
            try { pitchShifterRef.current.seek(0) } catch (e) {}
        }
        currentTime.current = 0
        if (gtimingCallbacks.current) {
            try { gtimingCallbacks.current.setProgress(0) } catch (e) {}
        }
        const repeatGeneration = playbackGenerationRef.current
        scheduleMidiStartAfterDelay(repeatGeneration, gapMs, { forceRatio: 0 })
     }

     function getMidiStartPosition() {
        if (pendingRestartFromZeroRef.current) {
            return { seconds: 0, ratio: 0 }
        }
        let seconds = currentTime.current || 0
        let ratio = 0
        const tol = MIDI_START_FROM_BEGINNING_TOLERANCE_SECONDS
        if (gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
            ratio = seconds / gmidiBuffer.current.duration
        }
        if (props.mediaController) {
            const clickSeek = parseFloat(props.mediaController.clickSeek)
            const controllerSeconds = parseFloat(props.mediaController.currentTime)
            const pendingSeconds = pendingPlaybackStartSecondsRef.current
            const hasPendingStart = typeof pendingSeconds === 'number' && pendingSeconds > tol
            const hasControllerSeconds = !isNaN(controllerSeconds) && controllerSeconds > tol
            const hasClickSeek = !isNaN(clickSeek) && clickSeek > tol
            if (hasPendingStart) {
                seconds = pendingSeconds
                if (gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
                    ratio = Math.min(1, pendingSeconds / gmidiBuffer.current.duration)
                }
            } else if (hasClickSeek) {
                // setClickSeek is async React state and often lags rewind-to-start.
                // Prefer the synchronous currentTimeRef when it says we are at 0.
                const timeRefSeconds = props.mediaController.currentTimeRef
                    ? parseFloat(props.mediaController.currentTimeRef.current)
                    : NaN
                const timeRefAtStart = !isNaN(timeRefSeconds) && timeRefSeconds <= tol
                if (timeRefAtStart && clickSeek > tol) {
                    ratio = 0
                    seconds = 0
                } else {
                    ratio = clickSeek
                    if (gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
                        seconds = clickSeek * gmidiBuffer.current.duration
                    }
                }
            } else if (!isMidiPlaybackActive()
                && !isNaN(clickSeek)
                && clickSeek >= 0
                && clickSeek <= tol) {
                ratio = 0
                seconds = 0
            } else if (isMidiPlaybackActive()) {
                // Synth beat clock / shifter are the source of truth while MIDI plays.
                // mediaController.currentTime is React state and is often stale/zero
                // (especially during practice), which would seek pitch changes to 0.
                if (pitchShifterRef.current) {
                    const shifterRatio = pitchShifterRef.current.getPlaybackRatio()
                    if (typeof shifterRatio === 'number' && shifterRatio > tol) {
                        ratio = Math.min(1, shifterRatio)
                        if (gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
                            seconds = ratio * gmidiBuffer.current.duration
                        }
                    }
                }
            } else if (hasControllerSeconds) {
                seconds = controllerSeconds
                const controllerDuration = parseFloat(props.mediaController.duration)
                if (controllerDuration > 0) {
                    ratio = Math.min(1, controllerSeconds / controllerDuration)
                } else if (gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
                    ratio = Math.min(1, controllerSeconds / gmidiBuffer.current.duration)
                }
            }
        }
        return { seconds: seconds, ratio: ratio }
     }

     function clearNotationPlaybackStartSeconds() {
        if (props.mediaController && props.mediaController.notationPlaybackStartSecondsRef) {
            props.mediaController.notationPlaybackStartSecondsRef.current = null
        }
     }

     function hasPendingNotationSeek() {
        if (!props.mediaController || !props.mediaController.notationPlaybackSeekRef) return false
        const seek = props.mediaController.notationPlaybackSeekRef.current
        if (!seek) return false
        if (typeof seek.startMs === 'number' && seek.startMs >= 0) return true
        return typeof seek.startBeat === 'number' && seek.startBeat > 0
     }

     function clearNotationPlaybackSeek() {
        if (props.mediaController && props.mediaController.notationPlaybackSeekRef) {
            props.mediaController.notationPlaybackSeekRef.current = null
        }
     }

     function resolveNotationPlaybackStartSeconds(opts) {
        const options = opts || {}
        const tempoBpm = options.tempo
            || (props.mediaController && props.mediaController.tune && props.mediaController.tune.tempo)
            || 120
        if (props.mediaController && props.mediaController.notationPlaybackSeekRef) {
            const seek = props.mediaController.notationPlaybackSeekRef.current
            if (seek && typeof seek.startMs === 'number' && seek.startMs >= 0) {
                return seek.startMs / 1000
            }
            if (seek && typeof seek.startBeat === 'number' && seek.startBeat > 0) {
                const fromSeek = notationBeatToAudioSeconds(
                    seek.startBeat, gvisualObj.current, seek.tempo || tempoBpm)
                if (fromSeek > 0) return fromSeek
            }
        }
        const startBeat = typeof options.startBeat === 'number' ? options.startBeat : null
        if (typeof options.startMs === 'number' && options.startMs >= 0) {
            return options.startMs / 1000
        }
        if (startBeat > 0) {
            const fromBeat = notationBeatToAudioSeconds(startBeat, gvisualObj.current, tempoBpm)
            if (fromBeat > 0) return fromBeat
        }
        if (typeof options.startSeconds === 'number' && options.startSeconds > 0) {
            return options.startSeconds
        }
        if (props.mediaController && props.mediaController.pendingMidiPlayRef
            && props.mediaController.pendingMidiPlayRef.current) {
            const pending = props.mediaController.pendingMidiPlayRef.current
            if (typeof pending.startMs === 'number' && pending.startMs >= 0) {
                return pending.startMs / 1000
            }
            if (typeof pending.startBeat === 'number' && pending.startBeat > 0) {
                const fromPendingBeat = notationBeatToAudioSeconds(
                    pending.startBeat, gvisualObj.current, pending.tempo || tempoBpm)
                if (fromPendingBeat > 0) return fromPendingBeat
            }
            if (typeof pending.startSeconds === 'number' && pending.startSeconds > 0) {
                return pending.startSeconds
            }
        }
        if (!(startBeat > 0) && props.mediaController && props.mediaController.notationPlaybackStartSecondsRef) {
            const notationSeconds = props.mediaController.notationPlaybackStartSecondsRef.current
            if (typeof notationSeconds === 'number' && notationSeconds > 0) {
                return notationSeconds
            }
        }
        if (!options.alwaysFromSelection) {
            if (typeof pendingPlaybackStartSecondsRef.current === 'number'
                && pendingPlaybackStartSecondsRef.current > MIDI_START_FROM_BEGINNING_TOLERANCE_SECONDS) {
                return pendingPlaybackStartSecondsRef.current
            }
            const pos = getMidiStartPosition()
            if (pos.seconds > MIDI_START_FROM_BEGINNING_TOLERANCE_SECONDS) {
                return pos.seconds
            }
        }
        return null
     }

     function maybeStopNotationSelectionPlayback(audioSeconds) {
        if (props.playbackEngine === false) return
        if (!props.mediaController || !props.mediaController.notationMidiOwner) return
        const seekRef = props.mediaController.notationPlaybackSeekRef
        const seek = seekRef && seekRef.current
        if (!seek || seek.endBeat == null) return
        const tempoBpm = seek.tempo
            || (props.mediaController.tune && props.mediaController.tune.tempo)
            || 120
        const endSeconds = notationBeatToAudioSeconds(
            seek.endBeat, gvisualObj.current, tempoBpm)
        if (!(endSeconds > 0)) return
        if (audioSeconds >= endSeconds - 0.05) {
            if (props.mediaController.stopNotationMidiPlayback) {
                props.mediaController.stopNotationMidiPlayback({})
            } else {
                pauseMidiSynth()
                setIsPlaying(false)
            }
        }
     }

     function resolveNotationSeekRatio(opts, pendingStartSeconds, bufferDuration, tempoBpm) {
        if (!(bufferDuration > 0)) return 0
        const options = opts || {}
        const seek = props.mediaController && props.mediaController.notationPlaybackSeekRef
            ? props.mediaController.notationPlaybackSeekRef.current
            : null
        const startMs = typeof options.startMs === 'number' ? options.startMs
            : (seek && typeof seek.startMs === 'number' ? seek.startMs : null)
        if (startMs != null) {
            const msRatio = notationMsToAudioRatio(startMs, bufferDuration)
            if (msRatio > 0) return msRatio
        }
        const startBeat = typeof options.startBeat === 'number' ? options.startBeat
            : (seek && typeof seek.startBeat === 'number' ? seek.startBeat : null)
        const tempo = tempoBpm || (seek && seek.tempo) || 120
        if (startBeat > 0) {
            const beatRatio = notationBeatToAudioRatio(
                startBeat, gvisualObj.current, bufferDuration, tempo)
            if (beatRatio > 0) return beatRatio
        }
        if (pendingStartSeconds > 0) {
            return Math.min(1, pendingStartSeconds / bufferDuration)
        }
        return 0
     }

     function startMidiAtResolvedSeconds(startSeconds, forcePlayback, opts) {
        if (shouldDeferSynthStopToNative()) return false
        if (!(startSeconds > 0)) return false
        pendingPlaybackStartSecondsRef.current = startSeconds
        if (gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
            const ratio = resolveNotationSeekRatio(
                opts, startSeconds, gmidiBuffer.current.duration,
                opts && opts.tempo)
                || Math.min(1, startSeconds / gmidiBuffer.current.duration)
            pendingPlaybackStartSecondsRef.current = null
            syncPlaybackSeekFromSeconds(startSeconds, ratio)
            midiStartHandledRef.current = true
            setForceStop(false)
            stopMetronome()
            resumeSynthAudioContext()
            return startMidiAndTiming({ forceRatio: ratio, forcePlayback: !!forcePlayback })
        }
        return false
     }

     function startPlayingFromIntent(force) {
        if (shouldDeferSynthStopToNative()) {
            return
        }
        if (!force && (getForceStop() || playbackFinishedRef.current)) {
            return
        }
        const pending = props.mediaController && props.mediaController.pendingMidiPlayRef
            ? props.mediaController.pendingMidiPlayRef.current
            : null
        if (pending && beginMidiPlaybackRef.current) {
            if (beginMidiPlaybackRef.current(pending)) {
                if (props.mediaController && props.mediaController.pendingMidiPlayRef
                    && !hasPendingNotationSeek()) {
                    props.mediaController.pendingMidiPlayRef.current = null
                }
                return
            }
        }
        const startSeconds = resolveNotationPlaybackStartSeconds(pending)
        if (startSeconds > 0) {
            pendingPlaybackStartSecondsRef.current = startSeconds
        }
        if (startSeconds != null && startMidiAtResolvedSeconds(startSeconds, true, pending)) {
            return
        }
        startPlaying(force)
     }

     function captureLiveMidiPlaybackRatio() {
        if (pitchShifterRef.current) {
            const shifterRatio = pitchShifterRef.current.getPlaybackRatio()
            if (typeof shifterRatio === 'number' && shifterRatio >= 0) {
                return Math.min(1, shifterRatio)
            }
        }
        if (gmidiBuffer.current && gmidiBuffer.current.duration > 0 && currentTime.current > 0) {
            return Math.min(1, currentTime.current / gmidiBuffer.current.duration)
        }
        return getMidiPlaybackRatio()
    }

    function syncPlaybackSeekFromSeconds(seconds, ratio) {
        const safeSeconds = typeof seconds === 'number' && seconds > 0 ? seconds : 0
        const safeRatio = typeof ratio === 'number' && ratio > 0 ? Math.min(1, ratio) : 0
        currentTime.current = safeSeconds
        if (props.mediaController) {
            if (props.mediaController.setCurrentTime) props.mediaController.setCurrentTime(safeSeconds)
            if (props.mediaController.setClickSeek) props.mediaController.setClickSeek(safeRatio)
            if (safeSeconds > 0) clearNotationPlaybackSeek()
        }
    }

    function resolvePlaybackMetronomeOptions() {
        const sourceTune = resolveMetronomeSettingsTune(tune, {
            tunes: props.tunes,
            tablatureSourceTune: props.tablatureSourceTune,
            mediaControllerTune: props.mediaController && props.mediaController.tune,
            abc: props.abc,
            abcTools: props.tunebook && props.tunebook.abcTools,
        })
        const fromTune = getPlaybackMetronomeSettings(sourceTune, props.tunebook)
        return {
            metronomeCountIn: props.metronomeCountIn !== undefined
                ? !!props.metronomeCountIn
                : fromTune.countIn !== false,
            countInBars: fromTune.countInBars,
            duringPlayback: props.metronomeDuringPlayback !== undefined
                ? !!props.metronomeDuringPlayback
                : fromTune.duringPlayback === true,
            rhythm: fromTune.rhythm,
            countInBeats: props.metronomeCountInBeats,
            countInBarOnly: !!props.metronomeCountInBarOnly,
        }
    }

    function resolvePlaybackMetronomeRhythm() {
        const metro = resolvePlaybackMetronomeOptions()
        const sourceTune = resolveMetronomeSettingsTune(tune, {
            tunes: props.tunes,
            tablatureSourceTune: props.tablatureSourceTune,
            mediaControllerTune: props.mediaController && props.mediaController.tune,
            abc: props.abc,
            abcTools: props.tunebook && props.tunebook.abcTools,
        })
        const meter = resolveTuneTimeSignature(sourceTune, props.tunebook)
        let rhythm = null
        if (metro.rhythm && metro.rhythm.beatsPerBar > 0) {
            rhythm = normalizeRhythmConfig(metro.rhythm)
        } else if (meter) {
            rhythm = normalizeRhythmConfig(rhythmFromTimeSignature(meter))
        } else {
            rhythm = normalizeRhythmConfig(defaultMetronomeRhythm())
        }
        return alignPlaybackRhythmToMeter(rhythm, meter)
    }

    function syncPlaybackMetronomeTempo() {
        setRhythmPlaybackTempo(getRhythmController(), getRhythmGridMetronomeTempo())
    }

    function metronomeStartErrorCallback() {
        if (props.mediaController && props.mediaController.userGesturePlayRef && props.mediaController.userGesturePlayRef.current) {
            return
        }
        setIsPlaying(false)
        clearForcedPlaybackIntent()
        if (props.mediaController) {
            props.mediaController.setIsLoading(false)
            props.mediaController.setIsPlaying(false)
        }
        setTapToPlayFlag(true)
    }

    function shouldScheduleCountInPitchCue() {
        if (props.onCountInBeat) return true
        if (props.metronomeCountInCueMidi != null && Number.isFinite(props.metronomeCountInCueMidi)) {
            return true
        }
        if (props.practiceReferenceGain != null && props.practiceReferenceGain >= 0) {
            return true
        }
        if (props.mediaController && props.mediaController.isPracticeSessionActive) {
            return props.mediaController.isPracticeSessionActive()
        }
        return false
    }

    function shouldRestartMidiFromStart(forceRestart) {
        if (forceRestart) return true
        const pos = getMidiStartPosition()
        return isMidiStartFromBeginning({
            seconds: pos.seconds,
            ratio: pos.ratio,
        })
    }

     function shouldUseCountInForStart(forceRestart) {
        const pos = getMidiStartPosition()
        const metro = resolvePlaybackMetronomeOptions()
        return shouldUseMidiMetronomeCountIn({
            metronomeCountIn: metro.metronomeCountIn,
            forceRestart: !!forceRestart,
            seconds: pos.seconds,
            ratio: pos.ratio,
        })
     }

     function fullyResetMidiEnginesToStart() {
        currentTime.current = 0
        stopNativeMidiBufferOutput()
        if (pitchShifterRef.current) {
            try { pitchShifterRef.current.disconnect() } catch (e) {}
        }
        if (pitchShifterRef.current && pitchShifterBufferRef.current && gaudioContext.current) {
            initPitchShifter(gaudioContext.current, pitchShifterBufferRef.current)
        }
        if (pitchShifterRef.current) {
            try { pitchShifterRef.current.seek(0) } catch (e) {}
        }
        if (gtimingCallbacks.current) {
            try {
                gtimingCallbacks.current.pause()
                gtimingCallbacks.current.setProgress(0)
            } catch (e) {}
        }
        if (gmidiBuffer.current) {
            try { gmidiBuffer.current.seek(0) } catch (e) {}
        }
        if (props.mediaController) {
            if (props.mediaController.setCurrentTime) props.mediaController.setCurrentTime(0)
            if (props.mediaController.setClickSeek) props.mediaController.setClickSeek(0)
        }
     }

     function notifyPlaybackStarted() {
        if (!wantsMidiPlayback()) {
            releaseMidiUiLoading()
            midiPrimeInFlightRef.current = false
            return
        }
        midiPrimeInFlightRef.current = false
        midiStartHandledRef.current = true
        clearNotationPlaybackStartSeconds()
        if (props.mediaController) {
            props.mediaController.setTapToPlay(false)
            if (props.mediaController.confirmPlayingStarted) {
                props.mediaController.confirmPlayingStarted()
            }
        } else if (props.onStarted) {
            props.onStarted()
        }
     }
     
     function createCursor() {
        const root = inputEl && inputEl.current;
        const svg = root ? root.querySelector('svg') : null;
        const cursor = ensureAbcjsCursorLine(svg, gcursor.current)
        if (cursor) {
          setCursor(cursor)
          return cursor
        }
        return null
    }
    
    function clickListener(abcelem, tuneNumber, classes, analysis, drag, mouseEvent) {
        
        if (abcelem && abcelem.type === 'tempo' && props.editableTempo) { // && props.onClickTempo) {props.onClickTempo() 
          setShowTempo(true)
        }
        if (abcelem && (abcelem.el_type === 'clef' || abcelem.el_type === 'keySignature')) { // && props.onClickTempo) {props.onClickTempo() 
          setShowTranspose(true)
        }
        var ms = (Array.isArray(abcelem.currentTrackMilliseconds) && abcelem.currentTrackMilliseconds.length > 0) ? abcelem.currentTrackMilliseconds[0] : abcelem.currentTrackMilliseconds
        
        if (!props.suppressPlaybackSeek) {
          if (gmidiBuffer && gmidiBuffer.current) gmidiBuffer.current.seek(Math.floor(ms)/1000,'seconds')
          if (pitchShifterRef.current && gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
              pitchShifterRef.current.seek(Math.floor(ms) / 1000 / gmidiBuffer.current.duration)
          }
          if (gtimingCallbacks && gtimingCallbacks.current && gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
              setTimingProgressFromAudioRatio(Math.floor(ms) / 1000 / gmidiBuffer.current.duration)
          }
          if (gmidiBuffer.current && gmidiBuffer.current.duration && gmidiBuffer.current.duration > 0) setSeekTo(Math.floor(ms/gmidiBuffer.current.duration)/1000)
        }
        
        if (props.onClick)  props.onClick(abcelem, tuneNumber, classes, analysis, drag, mouseEvent)
        
    }
    
    /**
     * Place the red cursor from the audio-anchored music clock (not TimingCallbacks
     * rAF wall time). TimingCallbacks free-runs on the *written* score, which is
     * shorter than volta-expanded audio — that made the cursor plow through
     * endings once while playback repeated, and drift ahead of the beat.
     */
    function syncEnginePlaybackCursor() {
        if (props.playbackEngine === false) return
        if (!shouldDrawPlaybackCursor({
            suppressPlaybackVisuals: props.suppressPlaybackVisuals,
        })) return
        const timing = gtimingCallbacks.current
        if (!timing || !timing.noteTimings) return
        const musicSec = getCursorMusicSeconds()
        const audibleMpm = getEffectiveMsPerMeasure()
        const map = soundingWrittenMapRef.current
        let audioDurationSec = (gmidiBuffer.current && gmidiBuffer.current.duration > 0)
          ? gmidiBuffer.current.duration
          : ((props.mediaController && props.mediaController.duration > 0)
            ? parseFloat(props.mediaController.duration)
            : 0)
        if (!(audioDurationSec > 0) && map && map.expectedDurationSec > 0) {
          audioDurationSec = map.expectedDurationSec
        }
        // Do NOT setTimingProgressFromAudioRatio on every tick: seeking
        // TimingCallbacks while it is running fires beat storms and races the
        // cursor. Seek/start paths already sync progress; the cursor reads the
        // audio clock + sounding map directly.
        const root = inputEl && inputEl.current
        const svg = root ? root.querySelector('svg') : null
        if (!svg) return
        const latencyWall = (pitchShifterRef.current
            && typeof pitchShifterRef.current.getOutputLatencySec === 'function'
            && pitchShifterRef.current.isConnected
            && pitchShifterRef.current.isConnected())
          ? (parseFloat(pitchShifterRef.current.getOutputLatencySec()) || 0)
          : 0
        const tempoFactor = getTempoFactor() > 0 ? getTempoFactor() : 1
        // SoundTouch output buffer delays heard notes vs the music clock; scale
        // wall latency into music seconds when playback tempo ≠ 1.
        const latencyMusic = latencyWall * tempoFactor
        const cursor = applyPlaybackCursorAtTime(
            svg,
            gcursor.current,
            timing.noteTimings,
            musicSec * 1000,
            {
              musicSec: musicSec,
              audibleMsPerMeasure: audibleMpm,
              audioDurationSec: audioDurationSec,
              lastMomentMs: timing.lastMoment,
              outputLatencySec: latencyMusic,
              soundingWrittenMap: map,
              musicStartMs: getTimingMusicStartMs(),
              barWholeNotes: barWholeNotesFromMeter(
                gvisualObj.current && typeof gvisualObj.current.getMeterFraction === 'function'
                  ? gvisualObj.current.getMeterFraction()
                  : null
              ),
              pickupWhole: map
                ? map.pickupWhole
                : (gvisualObj.current && typeof gvisualObj.current.getPickupLength === 'function'
                  ? parseFloat(gvisualObj.current.getPickupLength()) || 0
                  : 0),
            }
        )
        if (cursor) {
            setCursor(cursor)
        }
    }

    function beatCallback(currentBeat,totalBeats,lastMoment,position, debugInfo) {
        syncEnginePlaybackCursor()
        if ((isPlayingRef.current || isMidiPlaybackActive()) && autoScroll.current) {
            scrollPlaybackCursorIntoTopHalf(gcursor.current, {
              isPlaying: true,
              noteTimings: gtimingCallbacks.current && gtimingCallbacks.current.noteTimings,
              currentTimeMs: getCursorMusicSeconds() * 1000,
              audibleMsPerMeasure: getEffectiveMsPerMeasure(),
            })
        }
        // Fall back to abcjs beat position only when timings are unavailable.
        if (shouldDrawPlaybackCursor({
            suppressPlaybackVisuals: props.suppressPlaybackVisuals,
        }) && !(gtimingCallbacks.current && gtimingCallbacks.current.noteTimings)
            && position && position.left != null) {
          if (!gcursor.current) {
            createCursor()
          }
          updateAbcjsCursorLine(gcursor.current, position, false)
        }
        if (shouldDrawPlaybackCursor({
            suppressPlaybackVisuals: props.suppressPlaybackVisuals,
        })) {
          colorElements([])
        }
        if (shouldDeferSynthStopToNative()) {
            return
        }
        const isPlaybackEngine = props.playbackEngine !== false
        const hasEngineBuffer = !!(gmidiBuffer.current && gmidiBuffer.current.duration > 0 && totalBeats)
        if (isPlaybackEngine && hasEngineBuffer) {
        // When voltas are expanded, TimingCallbacks walks the *written* score once.
        // Never stretch that progress onto the expanded buffer — it races the cursor
        // through endings while audio is still on early passes. Prefer the real
        // audio / rhythm clock; keep TimingCallbacks synced via sounding→written.
        const hasSoundingMap = !!(soundingWrittenMapRef.current
            && soundingWrittenMapRef.current.segments
            && soundingWrittenMapRef.current.segments.length)
        let newSeconds
        if (hasSoundingMap) {
            newSeconds = getCursorMusicSeconds()
            if (!(newSeconds >= 0) || !isFinite(newSeconds)) {
                newSeconds = getMidiPlaybackSeconds()
            }
            // Do not seek TimingCallbacks from every beat — that storms callbacks
            // and races the cursor. Progress is set on seek/start only.
        } else {
            newSeconds = timingProgressToAudioSeconds(
                currentBeat / totalBeats,
                getTimingMusicStartMs(),
                lastMoment,
                gmidiBuffer.current.duration
            )
        }
        let skipPositionUpdate = false
        if (props.mediaController && props.mediaController.getSeekSettlement) {
            const settlement = props.mediaController.getSeekSettlement()
            if (settlement && isStaleSeekEngineReading(newSeconds, {
                seekTargetSeconds: settlement.target,
                seekFromSeconds: settlement.from,
            })) {
                skipPositionUpdate = true
            }
        }
        if (!skipPositionUpdate) {
            const anchor = rhythmMusicAnchorRef.current
            let anchorAheadSecs = null
            if (anchor.active && gaudioContext.current) {
                const elapsed = gaudioContext.current.currentTime - anchor.audioContextTime
                const factor = getTempoFactor() > 0 ? getTempoFactor() : 1
                if (elapsed >= 0 && elapsed < 30) {
                    anchorAheadSecs = anchor.musicSeconds + elapsed * factor
                } else {
                    anchorAheadSecs = anchor.musicSeconds
                }
            }
            // With a sounding map, never let TimingCallbacks-derived time outrun
            // the audio anchor (that caused growing lead through volta passes).
            const useSeconds = hasSoundingMap
                ? newSeconds
                : ((anchorAheadSecs != null && anchorAheadSecs > newSeconds + 0.02)
                    ? anchorAheadSecs
                    : newSeconds)
            if (props.mediaController && props.mediaController.onAbcTimeUpdate) {
                props.mediaController.onAbcTimeUpdate(useSeconds)
            }
            currentTime.current = useSeconds
            if (hasSoundingMap || anchorAheadSecs == null || anchorAheadSecs <= newSeconds + 0.02) {
                updateRhythmMusicAnchor(useSeconds)
            }
            maybeStopNotationSelectionPlayback(useSeconds)
            if (getRhythmPlaybackPhase(getRhythmController()) === PHASE_PLAYING) {
                syncPlaybackTimingFromMap(false)
            } else {
                reconcileDuringPlaybackRhythm()
            }
        }
        if (props.onPracticeBeat) {
            const latencyInfo = playalongOutputLatencyInTimelineRef.current || {}
            const shifterLatency = pitchShifterRef.current
                && typeof pitchShifterRef.current.getOutputLatencySec === 'function'
                ? pitchShifterRef.current.getOutputLatencySec()
                : 0
            const ctx = gaudioContext.current
            const deviceLatency = (ctx
                ? (Math.max(0, parseFloat(ctx.outputLatency) || 0)
                    + Math.max(0, parseFloat(ctx.baseLatency) || 0))
                : 0)
            const reportedLatency = Math.max(0, deviceLatency)
                + Math.max(0, parseFloat(shifterLatency) || 0)
            props.onPracticeBeat({
                currentBeat: currentBeat,
                totalBeats: totalBeats,
                musicStartMs: getTimingMusicStartMs(),
                audioSeconds: newSeconds,
                repIndex: playCountRef.current,
                outputLatencySeconds: reportedLatency,
                outputLatencyAlreadyInTimeline: !!latencyInfo.alreadyInTimeline,
                outputLatencyAppliedSeconds: Number.isFinite(latencyInfo.appliedSeconds)
                    ? latencyInfo.appliedSeconds
                    : 0,
            })
        }
         // FINISHED PLAYBACK — defer until audio is near the buffer end (see
         // handlePlaybackCompletion / isMidiAudioNearNaturalEnd).
         if (currentBeat === totalBeats) {
           handlePlaybackCompletion()
         }
        }
      }
      
      var lastEls = [];
      
      function colorElements(els) {
        var i;
        var j;
        for (i = 0; i < lastEls.length; i++) {
          for (j = 0; j < lastEls[i].length; j++) {
            lastEls[i][j].classList.remove("color");
          }
        }
        for (i = 0; i < els.length; i++) {
          for (j = 0; j < els[i].length; j++) {
            els[i][j].classList.add("color");
          }
        }
        lastEls = els;
    }
  
    function eventCallback(ev) {
        if (!ev) {
          return;
        }
        // Use ref / live MIDI check — TimingCallbacks keep a stale closure over isPlaying.
        const playing = isPlayingRef.current || isMidiPlaybackActive()
        if (playing && autoScroll.current && gmidiBuffer && gmidiBuffer.current && gmidiBuffer.current.duration > 0) { 
          //setSeekTo(ev.milliseconds/(gmidiBuffer.current.duration*1000)*getWarp())
          //if (props.mediaController)  props.mediaController.setCurrentTime((ev.milliseconds / 1000)/(gmidiBuffer.current.duration)*getWarp())
          if (lastScrollTo.current != ev.top) {
            // Playalong hides the source SVG and shows interleaved staff+roll rows —
            // scroll those visible rows, not the invisible note element.
            if (!scrollPlayalongPlayingLineIntoCenter(ev)) {
              const cursorEl = gcursor.current
              const noteEl = ev.elements && ev.elements[0] && ev.elements[0][0]
              if (cursorLineHasActivePosition(cursorEl) || noteEl) {
                const scrolled = scrollPlaybackCursorIntoTopHalf(cursorEl, {
                  isPlaying: true,
                  noteTimings: gtimingCallbacks.current && gtimingCallbacks.current.noteTimings,
                  currentTimeMs: ev.milliseconds,
                  audibleMsPerMeasure: getEffectiveMsPerMeasure(),
                })
                if (!scrolled && noteEl && typeof noteEl.getBoundingClientRect === 'function') {
                  const rect = noteEl.getBoundingClientRect()
                  if (rect && (rect.height > 0 || rect.width > 0)) {
                    try {
                      noteEl.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' })
                    } catch (err) {
                      noteEl.scrollIntoView(true)
                    }
                  }
                }
              } else if (noteEl && typeof noteEl.scrollIntoView === 'function') {
                const block = autoScrollBlock.current === 'center' ? 'center' : 'nearest'
                try {
                  noteEl.scrollIntoView({ block: block, inline: 'nearest' })
                } catch (err) {
                  noteEl.scrollIntoView(true)
                }
              } else if (Number.isFinite(ev.top)) {
                var screenRatio = window.visualViewport.width/window.visualViewport.height
                const mobileAdjust =  (isMobile && window.visualViewport.height < 400) ? 0.45 : 1
                var finalScroll = ((ev.top) * screenRatio ) * mobileAdjust
                window.scrollTo(0,finalScroll)
              }
            }
          }
          lastScrollTo.current = ev.top
        }
        colorElements([]);
    }
    
      async function saveAudioToCache(tuneId,audioBuffers, duration) {
      if (!(duration > 0) || !audioBuffers || !audioBuffers[0]) return null
      return encodeAudioBufferWithSetting(audioBuffers[0]).then(function (encoded) {
        return store.setItem(tuneId, [duration, encoded.blob, encoded.format] ).then(function () {
          scheduleMediaCacheStorageCheck()
          return store.getItem(tuneId);
        })
      })
    }
    
    async function getAudioFromCache(tuneId, audioContext) {
      if (!audioContext) return
      return store.getItem(tuneId).then(function (val) {
        if (val && Array.isArray(val)) {
          const duration = val[0]
          const buffers = val[1]
          return buffers.arrayBuffer().then(function(arrayBuffer) {
            return audioContext.decodeAudioData(arrayBuffer).then(function(audioBuffer) {
              return [duration, [audioBuffer, audioBuffer]]
            })
          }).catch(function() {
            return null
          })
        }
      })
    }
    
    
    function getBaseQpm() {
        if (!gvisualObj.current) return 120
        const tempo = gvisualObj.current.metaText ? gvisualObj.current.metaText.tempo : null
        return gvisualObj.current.getBpm(tempo) || 120
    }

    function getTempoFactor() {
        const factor = pitchTempoSettingsRef.current.tempo
        return factor > 0 ? factor : 1
    }

    function getEffectiveQpm() {
        return Math.round(getBaseQpm() * getTempoFactor())
    }

    function getPlaybackMetronomeTempo(visualObj) {
        const o = visualObj || gvisualObj.current
        const msPerMeasure = getEffectiveMsPerMeasure(o)
        if (!o || !o.getBeatsPerMeasure || !(msPerMeasure > 0)) {
            return getEffectiveQpm()
        }
        return computePlaybackMetronomeTempo({
            beatsPerMeasure: o.getBeatsPerMeasure(),
            millisecondsPerMeasure: msPerMeasure,
            tempoFactor: getTempoFactor(),
            fallbackQpm: getEffectiveQpm(),
        })
    }

    function getExtraMeasuresAtBeginning(visualObj) {
        const o = visualObj || gvisualObj.current
        const metro = resolvePlaybackMetronomeOptions()
        const msPerMeasure = getEffectiveMsPerMeasure(o)
        if (!metro.metronomeCountIn || !o || !(msPerMeasure > 0)) return 0
        return computeExtraMeasuresAtBeginning({
            beatsPerMeasure: o.getBeatsPerMeasure(),
            pickupLength: o.getPickupLength(),
            beatLength: o.getBeatLength(),
            millisecondsPerMeasure: msPerMeasure,
            tempoFactor: getTempoFactor(),
            countInBarOnly: metro.countInBarOnly,
            countInBars: metro.countInBars,
        })
    }

    function getTimingMusicStartMs(visualObj) {
        const o = visualObj || gvisualObj.current
        const extra = getExtraMeasuresAtBeginning(o)
        if (extra <= 0 || !o) return 0
        return computeTimingMusicStartMs({
            extraMeasuresAtBeginning: extra,
            qpm: getPlaybackMetronomeTempo(o),
            beatLength: o.getBeatLength(),
            barLength: o.getBarLength(),
            pickupLength: o.getPickupLength(),
        })
    }

    function setTimingProgressFromAudioRatio(audioRatio) {
        if (!gtimingCallbacks.current) return
        const lastMoment = gtimingCallbacks.current.lastMoment
        const map = soundingWrittenMapRef.current
        const buf = gmidiBuffer.current
        const r = Math.max(0, Math.min(1, parseFloat(audioRatio) || 0))
        // Prefer sounding→written so TimingCallbacks land on the volta pass that
        // matches expanded audio (not compressed into the written lastMoment end).
        if (map && Array.isArray(map.segments) && map.segments.length
            && buf && buf.duration > 0 && lastMoment > 0) {
            const soundingSec = r * buf.duration
            let soundingWhole = 0
            const audibleMpm = getEffectiveMsPerMeasure()
            const barWhole = barWholeNotesFromMeter(
                gvisualObj.current && typeof gvisualObj.current.getMeterFraction === 'function'
                  ? gvisualObj.current.getMeterFraction()
                  : null
            )
            if (audibleMpm > 0 && barWhole > 0) {
                const secPerWhole = (audibleMpm / 1000) / barWhole
                soundingWhole = secPerWhole > 0 ? soundingSec / secPerWhole : 0
            } else if (map.soundingWhole > 0) {
                soundingWhole = soundingSec * (map.soundingWhole / buf.duration)
            }
            const mapped = mapSoundingWholeToWritten(map.segments, soundingWhole)
            if (mapped && map.writtenWhole > 0) {
                const writtenFrac = Math.max(0, Math.min(1, mapped.writtenWhole / map.writtenWhole))
                const musicStart = getTimingMusicStartMs()
                const progress = (musicStart > 0 && musicStart < lastMoment)
                  ? (musicStart + writtenFrac * (lastMoment - musicStart)) / lastMoment
                  : writtenFrac
                try {
                    gtimingCallbacks.current.setProgress(progress)
                } catch (e) {}
                return
            }
        }
        const progress = audioRatioToTimingProgress(
            audioRatio,
            getTimingMusicStartMs(),
            lastMoment
        )
        try {
            gtimingCallbacks.current.setProgress(progress)
        } catch (e) {}
    }

    function buildTimingCallbacksOptions(visualObj) {
        const opts = {
            beatCallback: beatCallback,
            eventCallback: eventCallback,
            qpm: getPlaybackMetronomeTempo(visualObj),
        }
        const extra = getExtraMeasuresAtBeginning(visualObj)
        if (extra > 0) {
            opts.extraMeasuresAtBeginning = extra
        }
        return opts
    }

    function recreateTimingCallbacksAtTempo(factor, progressRatio, autoStart) {
        if (!gvisualObj.current) return
        const wasRunning = gtimingCallbacks.current && gtimingCallbacks.current.isRunning
        try {
            if (gtimingCallbacks.current) gtimingCallbacks.current.pause()
        } catch (e) {}
        gtimingCallbacks.current = new abcjs.TimingCallbacks(gvisualObj.current, buildTimingCallbacksOptions())
        // Audio ratios are music-only; map through count-in prefix so the cursor
        // lands on the sounding notes rather than the prep measures.
        setTimingProgressFromAudioRatio(Math.max(0, Math.min(1, parseFloat(progressRatio) || 0)))
        if (autoStart && (wasRunning || isMidiPlaybackActive())) {
            gtimingCallbacks.current.start()
        }
    }

    function syncPitchTempoSettingsFromController() {
        if (props.mediaController && props.mediaController.tune) {
            const settings = getPlaybackSettings(props.mediaController.tune)
            pitchTempoSettingsRef.current = {
                tempo: settings.tempo,
                pitch: settings.pitch,
                fineTune: settings.fineTune,
            }
        }
    }

    function notifyPitchOutputReady() {
        if (props.mediaController && props.mediaController.finishPitchShiftPrepareRef) {
            props.mediaController.finishPitchShiftPrepareRef.current()
        }
    }

    function initPitchShifter(audioContext, audioBuffer) {
        destroyPitchShifter()
        if (!audioBuffer || !audioContext) return
        pitchShifterBufferRef.current = audioBuffer
        syncPitchTempoSettingsFromController()
        pitchShifterRef.current = new PitchTempoShifter(
            audioContext,
            audioBuffer,
            function onTimeUpdate(timePlayed) {
                // Audio-only callback. The timing-callback beat handler is the sole
                // writer of the playhead position; writing it here lets a stale or
                // orphaned shifter fight the real clock and corrupt seek/rewind.
            },
            function onEnded() {
                // Near-end reconnect loops refresh the 3s guard and previously
                // swallowed completion — play/pause spasmed and the queue never
                // advanced. Always honor a natural end once past ~99% / <150ms left.
                const dur = pitchShifterRef.current && pitchShifterRef.current.duration > 0
                    ? pitchShifterRef.current.duration
                    : (gmidiBuffer.current && gmidiBuffer.current.duration > 0
                        ? gmidiBuffer.current.duration
                        : 0)
                const pos = pitchShifterRef.current
                    && typeof pitchShifterRef.current.getCurrentTime === 'function'
                    ? pitchShifterRef.current.getCurrentTime()
                    : currentTime.current
                const nearEnd = dur > 0 && (pos / dur >= 0.99 || (dur - pos) < 0.15)
                if (!nearEnd && Date.now() < midiPlaybackGuardUntilRef.current) {
                    return
                }
                if (!nearEnd && isSynthSeekGuardActive()) {
                    return
                }
                midiPlaybackGuardUntilRef.current = 0
                handlePlaybackCompletion()
            },
            notifyPitchOutputReady
        )
        const s = pitchTempoSettingsRef.current
        pitchShifterRef.current.applySettings(s.tempo, s.pitch, s.fineTune)
        if (props.mediaController && props.mediaController.playbackVolume !== undefined) {
            pitchShifterRef.current.setDirectOutputGain(false)
            pitchShifterRef.current.setOutputVolume(props.mediaController.playbackVolume)
        } else if (props.practiceReferenceGain != null && props.practiceReferenceGain >= 0) {
            pitchShifterRef.current.setDirectOutputGain(true)
            pitchShifterRef.current.setOutputVolume(Math.max(0, Math.min(1, props.practiceReferenceGain)))
        }
    }

    function applySynthPlaybackVolume(volume) {
        if (pitchShifterRef.current) {
            pitchShifterRef.current.setOutputVolume(volume)
        }
    }

    function applyMidiPlaybackSettings(tempo, pitch, fineTune, options) {
        const opts = options || {}
        const nextTempo = tempo > 0 ? parseFloat(tempo) : 1
        const nextPitch = pitch !== undefined && pitch !== null
            ? parseInt(pitch, 10) : pitchTempoSettingsRef.current.pitch
        const nextFineTune = fineTune !== undefined && fineTune !== null
            ? parseInt(fineTune, 10) : pitchTempoSettingsRef.current.fineTune
        const resolvedPitch = isNaN(nextPitch) ? 0 : nextPitch
        const resolvedFineTune = isNaN(nextFineTune) ? 0 : nextFineTune
        const tempoChanged = pitchTempoSettingsRef.current.tempo !== nextTempo
        const pitchChanged = pitchTempoSettingsRef.current.pitch !== resolvedPitch
        const fineTuneChanged = pitchTempoSettingsRef.current.fineTune !== resolvedFineTune
        const wasPlaying = isMidiPlaybackActive()
        // Capture before any disconnect so pitch changes do not seek to 0.
        const ratio = captureLiveMidiPlaybackRatio()
        pitchTempoSettingsRef.current = {
            tempo: nextTempo,
            pitch: resolvedPitch,
            fineTune: resolvedFineTune,
        }
        const settings = pitchTempoSettingsRef.current
        if (opts.liveTempoOnly && wasPlaying && tempoChanged && !pitchChanged && !fineTuneChanged) {
            if (pitchShifterRef.current) {
                pitchShifterRef.current.applySettings(settings.tempo, settings.pitch, settings.fineTune)
            }
            syncPlaybackMetronomeTempo()
            return
        }
        // Live pitch/fine-tune: retune the running shifter in place. Recreating
        // or calling startMidiAudioOutput here disconnects audio on every tick.
        if (wasPlaying && !tempoChanged && (pitchChanged || fineTuneChanged)) {
            clearDeferredNaturalEndRecovery()
            if (!pitchShifterRef.current && pitchShifterBufferRef.current && gaudioContext.current) {
                initPitchShifter(gaudioContext.current, pitchShifterBufferRef.current)
            }
            if (!pitchShifterRef.current) {
                notifyPitchOutputReady()
                return
            }
            midiPlaybackGuardUntilRef.current = Date.now() + 3000
            pitchShifterRef.current.applySettings(settings.tempo, settings.pitch, settings.fineTune)
            const connectedOrPending = function() {
                return pitchShifterRef.current.isConnected()
                    || (typeof pitchShifterRef.current.isConnectedOrPending === 'function'
                        && pitchShifterRef.current.isConnectedOrPending())
            }
            if (connectedOrPending()) {
                return
            }
            // Mode switch should leave the shifter connected. Soft-reconnect in
            // place — never rebuild via startMidiAudioOutput on pitch ticks.
            if (pitchShifterBufferRef.current) {
                try {
                    if (typeof ratio === 'number' && ratio >= 0) {
                        pitchShifterRef.current.seek(ratio)
                    }
                    pitchShifterRef.current.connect()
                } catch (e) {}
                if (connectedOrPending()) {
                    return
                }
            }
            notifyPitchOutputReady()
            return
        }
        if (wasPlaying) {
            midiPlaybackGuardUntilRef.current = Date.now() + 3000
        }
        if (pitchShifterRef.current) {
            pitchShifterRef.current.applySettings(settings.tempo, settings.pitch, settings.fineTune)
        }
        if (gvisualObj.current && tempoChanged) {
            recreateTimingCallbacksAtTempo(settings.tempo, ratio, wasPlaying)
            syncPlaybackMetronomeTempo()
        }
        if (wasPlaying && pitchShifterRef.current && gaudioContext.current
            && gaudioContext.current.state === 'running') {
            if (tempoChanged) {
                // Restart the audio pipe so SoundTouch tempo changes stay aligned with
                // the recreated timing callbacks and output level stays steady.
                if (pitchShifterRef.current.isConnected()) {
                    pitchShifterRef.current.disconnect()
                }
                startMidiAudioOutput(settings, ratio)
                midiPlaybackGuardUntilRef.current = Date.now() + 3000
            } else if (!pitchShifterRef.current.isConnected()) {
                startMidiAudioOutput(settings, ratio)
                midiPlaybackGuardUntilRef.current = Date.now() + 3000
            }
        }
    }

    function applyMidiTempo(factor) {
        applyMidiPlaybackSettings(factor, pitchTempoSettingsRef.current.pitch, pitchTempoSettingsRef.current.fineTune)
    }

    function setTempoFactor(factor) {
        applyMidiPlaybackSettings(factor, pitchTempoSettingsRef.current.pitch, pitchTempoSettingsRef.current.fineTune)
    }

    function applyPlaybackSettings(settings) {
        applyMidiPlaybackSettings(
            settings && settings.tempo !== undefined ? settings.tempo : 1,
            settings && settings.pitch !== undefined ? settings.pitch : 0,
            settings && settings.fineTune !== undefined ? settings.fineTune : 0
        )
    }
    function destroyPitchShifter() {
        if (pitchShifterRef.current) {
            pitchShifterRef.current.destroy()
            pitchShifterRef.current = null
        }
    }

    function getMidiPlaybackRatio() {
        const pos = getMidiStartPosition()
        if (isMidiStartFromBeginning(pos)) {
            return 0
        }
        if (gmidiBuffer.current && gmidiBuffer.current.duration > 0 && pos.seconds > 0) {
            return Math.min(1, pos.seconds / gmidiBuffer.current.duration)
        }
        if (!isNaN(pos.ratio) && pos.ratio > 0) {
            return Math.min(1, pos.ratio)
        }
        return 0
    }

    function getPitchTempoState() {
        return {
            tempo: pitchTempoSettingsRef.current.tempo,
            pitch: pitchTempoSettingsRef.current.pitch,
            fineTune: pitchTempoSettingsRef.current.fineTune,
        }
    }

    function resetPitchTempo() {
        applyMidiPlaybackSettings(1, 0, 0)
    }

    function startPlaying(force = false) {
        if (hostOwnsAbcOutput()) {
            releaseMidiUiLoading()
            return
        }
        // Forced restarts (play-along, From start) must cancel an in-flight
        // count-in / handoff; otherwise this returns and leaves mid-tune audio.
        if (force) {
            countInPendingRef.current = false
            forceDestroyAudioEngines()
        }
        // Media-settings MIDI after reload can kick startPlaying from
        // beginMidiPlayback, Abc pending-play, and autoPrime createPlayer.
        // Stacking those restarts count-in mid-schedule (3 quick + 3 even).
        if (!force && (countInPendingRef.current || isRhythmHandoffPhase())) {
            return
        }
        if (!force && midiPrimeInFlightRef.current) {
            if (primePromiseRef.current || countInPendingRef.current || isRhythmHandoffPhase()) {
                return
            }
            midiPrimeInFlightRef.current = false
        }
        if (!force && isSynthSeekGuardActive()) {
            releaseMidiUiLoading()
            return
        }
        if (!wantsMidiPlayback(force)) {
            if (force) {
                releaseMidiUiLoading()
            }
            return
        }
        if (!force && primePromiseRef.current && isLoading.current) {
            return
        }
        if (force) {
            clearPlaybackFinished()
            forcePlaybackUntilStartRef.current = true
        }
        setForceStop(false)
        midiPrimeInFlightRef.current = true
        // Keep start lock across stopMetronome (it clears the flag for external stops).
        stopMetronome()
        midiPrimeInFlightRef.current = true
        resumeSynthAudioContext()
        const wantMelodyOnly = !!props.forceFillOff
        const primedMelodyOnly = lastPrimedMelodyOnlyRef.current
        if (gaudioContext.current && gmidiBuffer.current
            && primedMelodyOnly != null
            && wantMelodyOnly !== !!primedMelodyOnly) {
          // Drop chord-fill buffer when entering play-along (or restore fills after).
          try { destroyAudioEngines() } catch (e) {}
        }
        const wantMidiTranspose = resolveMidiTransposeSemitones(
          gvisualObj.current,
          tune || (props.mediaController && props.mediaController.tune)
        )
        if (gaudioContext.current && gmidiBuffer.current
            && (lastPrimedMidiTransposeRef.current == null
                || lastPrimedMidiTransposeRef.current !== wantMidiTranspose)) {
          // Transpose changed (or unknown) while another pitch buffer was primed.
          try { destroyAudioEngines() } catch (e) {}
        }
        if (gaudioContext.current && gmidiBuffer.current) {
          startPrimedTune(force)
        } else {
            setStarted(true)
            const loadGeneration = playbackGenerationRef.current
            createPlayer(tune, gvisualObj.current, { showUiLoading: true, forcePrime: !!force }).then(function(p) {
                  if (!isPlaybackGenerationCurrent(loadGeneration)) {
                      releaseMidiUiLoading()
                      return
                  }
                  var [audioContext, midiBuffer, timingCallbacks, cursor] = p
                 if (!midiBuffer) {
                   setReady(false)
                   setStarted(false)
                   midiPrimeInFlightRef.current = false
                   if (getForceStop()
                       || shouldQuietMidiPrimeFailure()
                       || !wantsMidiPlayback(true)
                       || primePromiseRef.current) {
                     releaseMidiUiLoading()
                     return
                   }
                   reportMidiPlaybackFailure()
                   return
                 }
                 assignStateOnCompletion(audioContext, midiBuffer, timingCallbacks, cursor)
                 const pending = props.mediaController && props.mediaController.pendingMidiPlayRef
                     ? props.mediaController.pendingMidiPlayRef.current
                     : null
                 let pendingStartSeconds = resolveNotationPlaybackStartSeconds(pending || {})
                 if (!(pendingStartSeconds > 0)) {
                   pendingStartSeconds = pendingPlaybackStartSecondsRef.current
                 }
                 if (typeof pendingStartSeconds === 'number' && pendingStartSeconds > 0 && midiBuffer.duration > 0) {
                   pendingPlaybackStartSecondsRef.current = null
                   if (props.mediaController && props.mediaController.pendingMidiPlayRef) {
                     props.mediaController.pendingMidiPlayRef.current = null
                   }
                   const ratio = resolveNotationSeekRatio(
                     pending || {}, pendingStartSeconds, midiBuffer.duration,
                     pending && pending.tempo)
                     || Math.min(1, pendingStartSeconds / midiBuffer.duration)
                   syncPlaybackSeekFromSeconds(pendingStartSeconds, ratio)
                   setPlayCount(0)
                   if (!shouldDeferSynthStopToNative()) {
                     startMidiAndTiming({ forceRatio: ratio, forcePlayback: true })
                   }
                   return
                 }
                 setSeekTo(0)
                 setPlayCount(0)
                 startPrimedTune(!hasPendingNotationSeek())
            }).catch(function(e) {
              if (e === 'cancelled' || !isPlaybackGenerationCurrent(loadGeneration)) {
                  releaseMidiUiLoading()
                  return
              }
              setReady(false)
              setStarted(false)
              releaseMidiUiLoading()
              if (props.mediaController && props.mediaController.abortPlayingIntent
                  && !(props.mediaController.notationMidiOwner)) {
                  props.mediaController.abortPlayingIntent()
              }
              if (props.onStopped) props.onStopped()
            })
          
        }
    }

    function syncTimingCallbacksToSettings(progressRatio) {
        syncPitchTempoSettingsFromController()
        const settings = pitchTempoSettingsRef.current
        if (!gvisualObj.current) return
        const ratio = progressRatio > 0 ? progressRatio : getMidiPlaybackRatio()
        recreateTimingCallbacksAtTempo(settings.tempo, ratio, false)
    }

    function pauseMidiSynth() {
        stopRhythmPlayback()
        const skipPositionWrite = isSynthSeekGuardActive()
        // Always stop audio output, even during a seek guard window.
        midiPlaybackGuardUntilRef.current = Date.now() + 1000
        stopNativeMidiBufferOutput()
        if (pitchShifterRef.current) pitchShifterRef.current.disconnect()
        if (gtimingCallbacks && gtimingCallbacks.current) gtimingCallbacks.current.pause()
        if (gmidiBuffer && gmidiBuffer.current) gmidiBuffer.current.pause()
        if (skipPositionWrite) return
        // Only write the shared playback position when MIDI is the active engine.
        const midiIsActiveRoute = isMidiActivePlaybackRoute()
        if (midiIsActiveRoute && props.mediaController && props.mediaController.setCurrentTime) {
            const seconds = getMidiPlaybackSeconds()
            if (seconds >= 0) {
                props.mediaController.setCurrentTime(seconds)
            }
            const total = props.mediaController.duration > 0
                ? parseFloat(props.mediaController.duration) : 0
            if (total > 0 && props.mediaController.setClickSeek) {
                props.mediaController.setClickSeek(Math.min(1, seconds / total))
            }
        }
    }

    function shouldDeferSynthStopToNative() {
        if (!hostOwnsAbcOutput() || !props.mediaController) return false
        const mc = props.mediaController
        if (typeof mc.isAndroidNativeOutputActive === 'function' && mc.isAndroidNativeOutputActive()) {
            return true
        }
        if (isAbcNativePlayInFlight() || isAndroidNativePlayerActive()) {
            return true
        }
        return false
    }

    function stopPlaying()  {
        playbackGenerationRef.current += 1
        latchMidiPrimeCancel(4000)
        const deferNative = shouldDeferSynthStopToNative()
        if (deferNative) {
            pauseMidiSynth()
            return
        }
        pauseMidiSynth()
        setForceStop(true)
        setIsPlaying(false)
        clearForcedPlaybackIntent()
        clearDeferredNaturalEndRecovery()
        if (props.mediaController && props.mediaController.setIsPlaying) {
            props.mediaController.setIsPlaying(false)
        }
        if (props.onStopped) {
            props.onStopped()
        }
    }

    function stopMidiSynth() {
        stopMetronome()
        if (hostOwnsAbcOutput()) {
            pauseMidiSynth()
            return
        }
        seekPlayer(0)
        currentTime.current = 0
        const midiIsActiveRoute = isMidiActivePlaybackRoute()
        if (midiIsActiveRoute && props.mediaController) {
            if (props.mediaController.setCurrentTime) props.mediaController.setCurrentTime(0)
            if (props.mediaController.setClickSeek) props.mediaController.setClickSeek(0)
        }
        stopPlaying()
    }

    async function resumeMidiPlayback() {
        // Pause arms a short seek guard; clear it so immediate unpause can restart.
        midiPlaybackGuardUntilRef.current = 0
        forcePlaybackUntilStartRef.current = true
        setForceStop(false)
        if (!wantsMidiPlayback(true)) return false
        midiStartHandledRef.current = true
        if (!resolvePlaybackMetronomeOptions().duringPlayback) {
            stopMetronome()
        }
        resumeSynthAudioContext()
        const ratio = getMidiPlaybackRatio()
        syncTimingCallbacksToSettings(ratio)
        return startMidiAndTiming({ forcePlayback: true })
    }

    function beginMidiPlayback(options) {
        if (hostOwnsAbcOutput()) {
            releaseMidiUiLoading()
            return false
        }
        if (props.mediaController && props.mediaController.hasPlayingIntent
            && props.mediaController.hasPlayingIntent()) {
            clearPlaybackFinished()
        }
        let opts = options || {}
        if (opts.fromStart) {
            opts = Object.assign({}, opts, {
                restart: true,
                fresh: true,
                preservePosition: false,
                resume: false,
                alwaysFromSelection: false,
                fromNotationSelection: false,
                startBeat: undefined,
                startMs: undefined,
                startSeconds: undefined,
            })
            pendingPlaybackStartSecondsRef.current = null
            pendingRestartFromZeroRef.current = true
            currentTime.current = 0
            if (props.mediaController) {
                if (props.mediaController.currentTimeRef) {
                    props.mediaController.currentTimeRef.current = 0
                }
                if (props.mediaController.setCurrentTime) props.mediaController.setCurrentTime(0)
                if (props.mediaController.setClickSeek) props.mediaController.setClickSeek(0)
                if (props.mediaController.notationPlaybackSeekRef) {
                    props.mediaController.notationPlaybackSeekRef.current = null
                }
                if (props.mediaController.notationPlaybackStartSecondsRef) {
                    props.mediaController.notationPlaybackStartSecondsRef.current = null
                }
            }
        }
        // Media-controls "Play MIDI" uses fresh:true after reload; treat like
        // From start so count-in is a single clean rewind path.
        if (opts.fresh && !opts.resume && !opts.preservePosition) {
            opts = Object.assign({}, opts, { restart: true })
        }
        const alwaysFromSelection = !!opts.alwaysFromSelection
        const tempoBpm = opts.tempo
            || (props.mediaController && props.mediaController.tune && props.mediaController.tune.tempo)
            || 120
        const explicitStartBeat = typeof opts.startBeat === 'number' && opts.startBeat > 0
            ? opts.startBeat
            : null
        const explicitStartMs = typeof opts.startMs === 'number' && opts.startMs >= 0
            ? opts.startMs
            : null
        let explicitStartSeconds = typeof opts.startSeconds === 'number' && opts.startSeconds > 0
            ? opts.startSeconds
            : null
        if (explicitStartSeconds == null && explicitStartMs != null) {
            explicitStartSeconds = explicitStartMs / 1000
        }
        if (explicitStartSeconds == null && explicitStartBeat != null) {
            explicitStartSeconds = notationBeatToAudioSeconds(
                explicitStartBeat, gvisualObj.current, tempoBpm)
            if (!(explicitStartSeconds > 0)) {
                explicitStartSeconds = null
            }
        }
        if (explicitStartSeconds != null) {
            pendingPlaybackStartSecondsRef.current = explicitStartSeconds
            midiPlaybackGuardUntilRef.current = 0
        }
        const hasStartPosition = explicitStartSeconds != null
            || explicitStartBeat != null
            || explicitStartMs != null
            || alwaysFromSelection
            || opts.fromNotationSelection
            || opts.preservePosition === true
        if (hasStartPosition && explicitStartSeconds == null
            && explicitStartBeat == null && explicitStartMs == null) {
            const resolvedStart = resolveNotationPlaybackStartSeconds(opts)
            if (resolvedStart > 0) {
                pendingPlaybackStartSecondsRef.current = resolvedStart
                midiPlaybackGuardUntilRef.current = 0
            }
        }
        if (isSynthSeekGuardActive() && !opts.restart && !opts.resume && !hasStartPosition) {
            releaseMidiUiLoading()
            return false
        }
        if (props.mediaController && props.mediaController.isMidiPlaybackRoute
            && !props.mediaController.isMidiPlaybackRoute()) {
            releaseMidiUiLoading()
            return false
        }
        if (opts.resume && !alwaysFromSelection) {
            if (pendingRestartFromZeroRef.current) {
                opts = Object.assign({}, opts, { resume: false, restart: true })
            } else {
            midiPlaybackGuardUntilRef.current = 0
            forcePlaybackUntilStartRef.current = true
            midiStartHandledRef.current = true
            setForceStop(false)
            if (gtimingCallbacks.current && gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
                resumeMidiPlayback()
            } else {
                startPlaying(true)
            }
            return true
            }
        }
        const pendingStartSeconds = explicitStartSeconds != null
            ? explicitStartSeconds
            : resolveNotationPlaybackStartSeconds(opts)
        if (opts.restart) {
            // Keep pendingRestartFromZeroRef armed for fromStart until audio
            // actually begins — clearArmPlaybackFromZero runs after start.
            if (!opts.fromStart && !pendingRestartFromZeroRef.current) {
                clearArmPlaybackFromZero()
            } else {
                pendingRestartFromZeroRef.current = true
            }
            clearNotationPlaybackSeek()
            clearNotationPlaybackStartSeconds()
            pendingPlaybackStartSecondsRef.current = null
            if (props.mediaController && props.mediaController.pendingMidiPlayRef) {
                props.mediaController.pendingMidiPlayRef.current = null
            }
        }
        const fromBeginning = opts.restart
            ? true
            : (alwaysFromSelection
                ? !(pendingStartSeconds > 0 || explicitStartBeat > 0
                    || (explicitStartMs != null && explicitStartMs > 0))
                : (!hasStartPosition && shouldRestartMidiFromStart(opts.restart)))
        if (fromBeginning) {
            if (opts.restart) {
                // Cancel count-in / stuck kickoff, but keep a good primed buffer so
                // replay after natural end does not re-prime (and toast on race).
                countInPendingRef.current = false
                countInMidiPreScheduledRef.current = false
                if (primeTimerRef.current) {
                    clearTimeout(primeTimerRef.current)
                    primeTimerRef.current = null
                }
                if (!(gmidiBuffer.current && gtimingCallbacks.current)) {
                    invalidatePendingMidiStarts()
                } else {
                    stopMetronome()
                    midiPrimeInFlightRef.current = false
                    if (!opts.fromStart && !pendingRestartFromZeroRef.current) {
                        clearArmPlaybackFromZero()
                    } else {
                        pendingRestartFromZeroRef.current = true
                    }
                }
                if (!primePromiseRef.current) {
                    midiPrimeInFlightRef.current = false
                }
            }
            if (isMidiKickoffActive()) {
                if (!opts.restart
                    && (primePromiseRef.current || countInPendingRef.current || isRhythmHandoffPhase())) {
                    if (props.mediaController && props.mediaController.pendingMidiPlayRef) {
                        props.mediaController.pendingMidiPlayRef.current = opts
                    }
                    return true
                }
                clearStaleMidiKickoffLock()
            }
            if (gaudioContext.current && gmidiBuffer.current && gtimingCallbacks.current
                && wantsMidiPlayback(true)) {
                if (props.mediaController && props.mediaController.pendingMidiPlayRef) {
                    props.mediaController.pendingMidiPlayRef.current = null
                }
                pendingPlaybackStartSecondsRef.current = null
                midiStartHandledRef.current = true
                setForceStop(false)
                midiPrimeInFlightRef.current = true
                fullyResetMidiEnginesToStart()
                startPrimedTune(!!opts.restart)
                return true
            }
            if (props.mediaController && props.mediaController.pendingMidiPlayRef) {
                props.mediaController.pendingMidiPlayRef.current = null
            }
            pendingPlaybackStartSecondsRef.current = null
            if (!opts.restart) {
                invalidatePendingMidiStarts()
            }
            fullyResetMidiEnginesToStart()
            midiStartHandledRef.current = true
            setForceStop(false)
            startPlaying(true)
            return true
        }
        if (pendingStartSeconds > 0) {
            pendingPlaybackStartSecondsRef.current = pendingStartSeconds
            midiPlaybackGuardUntilRef.current = 0
        }
        if (pendingStartSeconds > 0 && gmidiBuffer.current && gmidiBuffer.current.duration > 0) {
            const ratio = resolveNotationSeekRatio(
                opts, pendingStartSeconds, gmidiBuffer.current.duration, tempoBpm)
                || Math.min(1, pendingStartSeconds / gmidiBuffer.current.duration)
            pendingPlaybackStartSecondsRef.current = null
            if (props.mediaController && props.mediaController.pendingMidiPlayRef) {
                props.mediaController.pendingMidiPlayRef.current = null
            }
            syncPlaybackSeekFromSeconds(pendingStartSeconds, ratio)
            midiStartHandledRef.current = true
            setForceStop(false)
            startMidiAndTiming({ forceRatio: ratio, forcePlayback: true })
            return true
        }
        const alreadyPlaying = isMidiPlaybackActive()
        if (alreadyPlaying && pitchShifterRef.current && pitchShifterRef.current.isConnected()
            && gtimingCallbacks.current && gtimingCallbacks.current.isRunning) {
            if (props.mediaController && props.mediaController.confirmPlayingStarted) {
                props.mediaController.confirmPlayingStarted()
            }
            return true
        }
        if (hasStartPosition && (pendingStartSeconds > 0 || explicitStartBeat > 0
            || (explicitStartMs != null && explicitStartMs > 0))) {
            midiStartHandledRef.current = true
            setForceStop(false)
            startPlaying(true)
            return true
        }
        let ratio = getMidiPlaybackRatio()
        if (!alwaysFromSelection && ratio > 0 && gtimingCallbacks.current && gmidiBuffer.current) {
            if (isSynthSeekGuardActive()) {
                releaseMidiUiLoading()
                return false
            }
            if (alreadyPlaying && pitchShifterRef.current && pitchShifterRef.current.isConnected()
                && gtimingCallbacks.current.isRunning) {
                if (props.mediaController && props.mediaController.confirmPlayingStarted) {
                    props.mediaController.confirmPlayingStarted()
                }
                return true
            }
            resumeMidiPlayback()
            return true
        }
        midiStartHandledRef.current = true
        setForceStop(false)
        startPlaying(true)
        return true
    }

    useLayoutEffect(function() {
        beginMidiPlaybackRef.current = beginMidiPlayback
    })

    function assignStateOnCompletion(audioContext, midiBuffer, timingCallbacks, cursor) {
         setAudioContext(audioContext)
         if (midiBuffer && midiBuffer.duration > 0) { 
           setMidiBuffer(midiBuffer)
           if (props.mediaController) props.mediaController.setDuration(midiBuffer.duration)
           setTimingCallbacks(timingCallbacks)
           setCursor(cursor)
           if (midiBuffer.audioBuffers && midiBuffer.audioBuffers[0]) {
             initPitchShifter(audioContext, midiBuffer.audioBuffers[0])
           }
           try {
             rebuildPlaybackTimingMap({ bufferDuration: midiBuffer.duration })
           } catch (e) {}
           syncTimingCallbacksToSettings(0)
           setReady(true)
           setStarted(true)
         } else {
           setReady(false)
           setStarted(false)
         }
         isLoading.current = false
    }

  
    function resetAudioState() {
        abortMidiEngines()
    }

    function forceDestroyAudioEngines() {
        effectiveMsPerMeasureRef.current = 0
        primedMsPerMeasureRef.current = 0
        lastPrimedMelodyOnlyRef.current = null
        lastPrimedMidiTransposeRef.current = null
        lastPrimedAudioHashRef.current = null
        soundingWrittenMapRef.current = null
        if (props.mediaController) {
          assignMediaControllerRef(props.mediaController, 'soundingWrittenMapRef', null)
        }
        nativeBufferClockRef.current = null
        try {
          destroyPitchShifter()
          if (gmidiBuffer.current)  gmidiBuffer.current.stop()
          if (gtimingCallbacks.current) gtimingCallbacks.current.stop()
          delete gmidiBuffer.current
          delete gtimingCallbacks.current
        } catch (e) {}
    }

    function destroyAudioEngines() {
        if (isMidiKickoffActive()) {
            return
        }
        forceDestroyAudioEngines()
    }

    function seekMidiPlayback(ratio, options) {
        const opts = options || {}
        const clamped = Math.max(0, Math.min(1, parseFloat(ratio) || 0))
        if (!gmidiBuffer.current || gmidiBuffer.current.duration <= 0) return
        pendingPlaybackStartSecondsRef.current = null
        pendingRestartFromZeroRef.current = clamped <= (MIDI_START_FROM_BEGINNING_TOLERANCE_SECONDS
            / gmidiBuffer.current.duration)
        const wasPlaying = props.mediaController && props.mediaController.hasActivePlaybackIntent
            ? props.mediaController.hasActivePlaybackIntent()
            : isMidiPlaybackActive()
        const allowAutoResume = wasPlaying && !opts.skipAutoResume && !getForceStop()
        midiPlaybackGuardUntilRef.current = Date.now() + 3000
        currentTime.current = clamped * gmidiBuffer.current.duration
        updateRhythmMusicAnchor(currentTime.current)
        if (opts.skipAutoResume) {
            stopNativeMidiBufferOutput()
            stopMetronome()
            if (pitchShifterRef.current) {
                try { pitchShifterRef.current.disconnect() } catch (e) {}
            }
            if (gtimingCallbacks.current) {
                try { gtimingCallbacks.current.pause() } catch (e) {}
            }
        }
        // Seeking a SoundTouch pipe in place (or by disconnect/reconnect of the
        // same node) does not reliably reset its source position — most visibly on
        // rewind-to-0, where the node keeps its previous position. Recreate the
        // shifter from the cached buffer so the seek below always takes effect.
        if (pitchShifterRef.current && pitchShifterBufferRef.current && gaudioContext.current) {
            initPitchShifter(gaudioContext.current, pitchShifterBufferRef.current)
        }
        if (pitchShifterRef.current) {
            try { pitchShifterRef.current.seek(clamped) } catch (e) {}
        }
        setTimingProgressFromAudioRatio(clamped)
        if (gmidiBuffer.current && !pitchShifterRef.current) {
            try { gmidiBuffer.current.seek(clamped) } catch (e) {}
        }
        try { syncEnginePlaybackCursor() } catch (e) {}
        if (allowAutoResume) {
            const settings = pitchTempoSettingsRef.current
            if (pitchShifterRef.current) {
                if (gaudioContext.current && gaudioContext.current.state === 'running') {
                    if (pitchShifterRef.current.isConnected()) {
                        try { pitchShifterRef.current.disconnect() } catch (e) {}
                    }
                    startMidiAudioOutput(settings, clamped)
                }
            } else if (gmidiBuffer.current && !gmidiBuffer.current.isRunning) {
                startMidiAudioOutput(settings, clamped)
            }
            if (gtimingCallbacks.current && !gtimingCallbacks.current.isRunning) {
                try {
                    gtimingCallbacks.current.start()
                } catch (e) {}
            }
        }
        if (props.mediaController) {
            if (props.mediaController.setCurrentTime) {
                props.mediaController.setCurrentTime(currentTime.current)
            }
        }
        if (resolvePlaybackMetronomeOptions().duringPlayback && gmidiBuffer.current
            && gmidiBuffer.current.duration > 0) {
            const musicSeconds = clamped * gmidiBuffer.current.duration
            if (getRhythmPlaybackPhase(getRhythmController()) === PHASE_PLAYING) {
                seekRhythmPlaying(getRhythmController(), musicSeconds)
                syncPlaybackTimingFromMap(true)
            } else if (allowAutoResume) {
                ensureDuringPlaybackMetronome(clamped)
            }
        }
    }

    function resumeMidiAfterSeek() {
        if (playbackFinishedRef.current || getForceStop()) return
        if (shouldDeferSynthStopToNative()) {
            if (props.mediaController && props.mediaController.confirmPlayingStarted) {
                props.mediaController.confirmPlayingStarted()
            }
            return
        }
        const shouldResume = props.mediaController && props.mediaController.hasActivePlaybackIntent
            ? props.mediaController.hasActivePlaybackIntent()
            : (props.mediaController && props.mediaController.hasPlayingIntent
                && props.mediaController.hasPlayingIntent())
        if (!shouldResume && !(props.mediaController && props.mediaController.isPlaying)) {
            return
        }
        const ratio = getMidiPlaybackRatio()
        // Do not revive a tune that already finished — that caused ~200ms
        // reconnect spasms at ratio≈0.998 and blocked queue advance.
        if (ratio >= 0.99) {
            return
        }
        midiPlaybackGuardUntilRef.current = Date.now() + 3000
        setForceStop(false)
        midiStartHandledRef.current = true
        const duringPlayback = resolvePlaybackMetronomeOptions().duringPlayback
        if (!duringPlayback) {
            stopMetronome()
        }
        resumeSynthAudioContext()
        const settings = pitchTempoSettingsRef.current
        if (pitchShifterRef.current && gaudioContext.current
            && gaudioContext.current.state === 'running') {
            if (pitchShifterRef.current.isConnected()) {
                try { pitchShifterRef.current.disconnect() } catch (e) {}
            }
            startMidiAudioOutput(settings, ratio >= 0 ? ratio : 0)
        } else if (gmidiBuffer.current && gaudioContext.current
            && gaudioContext.current.state === 'running' && !gmidiBuffer.current.isRunning) {
            startMidiAudioOutput(settings, ratio > 0 ? ratio : 0)
        }
        if (gtimingCallbacks.current) {
            try {
                if (!gtimingCallbacks.current.isRunning) {
                    setTimingProgressFromAudioRatio(ratio > 0 ? ratio : 0)
                    gtimingCallbacks.current.start()
                }
            } catch (e) {}
        }
        if (props.mediaController.confirmPlayingStarted) {
            props.mediaController.confirmPlayingStarted()
        }
        if (duringPlayback) {
            seekRhythmPlaying(getRhythmController(), ratio > 0 ? ratio * (gmidiBuffer.current ? gmidiBuffer.current.duration : 0) : 0)
            ensureDuringPlaybackMetronome(ratio)
        }
    }

    function seekPlayer(seekTo, play = false, options) {
        seekMidiPlayback(seekTo, options)
        if (play) startMidiAndTiming({ forcePlayback: true })
    }
    
  async function startMidiAndTiming(startOptions) {
      if (shouldDeferSynthStopToNative()) {
          releaseMidiUiLoading()
          return false
      }
      const opts = startOptions || {}
      const forceWanted = !!opts.forcePlayback
      const duringPlayback = resolvePlaybackMetronomeOptions().duringPlayback
      const rhythmPhaseAtStart = getRhythmPlaybackPhase(getRhythmController())
      const rhythmAlreadyPlaying = rhythmPhaseAtStart === PHASE_PLAYING
      const countInHandoffPending = typeof opts.startAtAudioTime === 'number'
      const deferTimingUntilHandoff = countInHandoffPending
          && duringPlayback
          && rhythmPhaseAtStart === PHASE_ENTRY_GAP
      if (!isRhythmHandoffPhase() && !rhythmAlreadyPlaying && !countInPendingRef.current) {
          stopMetronome()
      }
      if (!forceWanted && isSynthSeekGuardActive()) {
          releaseMidiUiLoading()
          return false
      }
      const generation = playbackGenerationRef.current
      if (!wantsMidiPlayback(forceWanted) || getForceStop()) {
          releaseMidiUiLoading()
          return false
      }
      try {
          const contextReady = await ensureSynthAudioContextRunning()
          if (!isPlaybackGenerationCurrent(generation)) {
              releaseMidiUiLoading()
              return false
          }
          if (!wantsMidiPlayback(forceWanted) || getForceStop()) {
              releaseMidiUiLoading()
              return false
          }
          if (!contextReady) {
              handleMidiAudioStartFailure()
              return false
          }

          const settings = pitchTempoSettingsRef.current
          const ratio = opts.forceRatio !== undefined
              ? Math.max(0, Math.min(1, parseFloat(opts.forceRatio) || 0))
              : getMidiPlaybackRatio()
          // Detect pre-scheduled count-in MIDI before any forceRatio seek.
          // Direct-mode seek() disconnects the BufferSource; doing that after
          // pre-schedule makes the tune start on the downbeat then restart.
          const midiWasPreScheduled = countInMidiPreScheduledRef.current
              && pitchShifterRef.current
              && (pitchShifterRef.current.isConnectedOrPending
                  ? pitchShifterRef.current.isConnectedOrPending()
                  : pitchShifterRef.current.isConnected())
          if (opts.forceRatio !== undefined) {
              currentTime.current = gmidiBuffer.current && gmidiBuffer.current.duration > 0
                  ? ratio * gmidiBuffer.current.duration
                  : 0
              // forceRatio is music-only (0 = first note). Do not reset TimingCallbacks
              // to 0 when count-in prep measures are present — that put the cursor a
              // full count-in behind the audio for the rest of the tune.
              const atMusicStart = isMidiStartFromBeginning({ ratio: ratio })
              if (atMusicStart
                  && getExtraMeasuresAtBeginning() <= 0
                  && gtimingCallbacks.current) {
                  try {
                      gtimingCallbacks.current.pause()
                      gtimingCallbacks.current.setProgress(0)
                  } catch (e) {}
              }
              if (!deferTimingUntilHandoff) {
                  setTimingProgressFromAudioRatio(ratio)
              }
              if (!midiWasPreScheduled) {
                  if (pitchShifterRef.current) {
                      try { pitchShifterRef.current.seek(ratio) } catch (e) {}
                  } else if (gmidiBuffer.current) {
                      try { gmidiBuffer.current.seek(ratio) } catch (e) {}
                  }
              }
          }
          const audioStart = midiWasPreScheduled
              ? {
                  ok: true,
                  actualStartAudioTime: typeof opts.startAtAudioTime === 'number'
                      ? opts.startAtAudioTime
                      : (gaudioContext.current ? gaudioContext.current.currentTime : null),
              }
              : startMidiAudioOutput(
                  settings,
                  ratio,
                  opts.startAtAudioTime != null ? opts.startAtAudioTime : null
              )
          countInMidiPreScheduledRef.current = false
          if (!audioStart || !audioStart.ok) {
              // Primed buffer may be unusable after stop(); re-prime once instead of toasting.
              if (!midiAudioRestartAfterFailRef.current
                  && wantsMidiPlayback(true)
                  && !getForceStop()
                  && !shouldQuietMidiPrimeFailure()) {
                  midiAudioRestartAfterFailRef.current = true
                  startPlaying(true)
                  return false
              }
              midiAudioRestartAfterFailRef.current = false
              handleMidiAudioStartFailure()
              return false
          }
          midiAudioRestartAfterFailRef.current = false

          if (!isPlaybackGenerationCurrent(generation) || !wantsMidiPlayback(forceWanted) || getForceStop()) {
              pauseMidiSynth()
              releaseMidiUiLoading()
              return false
          }
          if (gtimingCallbacks.current && !deferTimingUntilHandoff) {
              const progressRatio = opts.forceRatio !== undefined
                  ? Math.max(0, Math.min(1, parseFloat(opts.forceRatio) || 0))
                  : (ratio > 0 ? ratio : 0)
              setTimingProgressFromAudioRatio(progressRatio)
              // Mark playing before TimingCallbacks.start so eventCallback autoScroll
              // sees a live ref (callbacks are created at prime-time with a stale closure).
              isPlayingRef.current = true
              if (!gtimingCallbacks.current.isRunning) {
                  gtimingCallbacks.current.start()
              }
          } else if (deferTimingUntilHandoff) {
              isPlayingRef.current = true
          }
          if (!isPlaybackGenerationCurrent(generation) || !wantsMidiPlayback(forceWanted) || getForceStop()) {
              pauseMidiSynth()
              releaseMidiUiLoading()
              return false
          }
          setIsPlaying(true)
          clearForcedPlaybackIntent()
          clearArmPlaybackFromZero()
          releaseMidiUiLoading()
          notifyPlaybackStarted()
          const rhythmPhase = getRhythmPlaybackPhase(getRhythmController())
          const countInHandoff = typeof opts.startAtAudioTime === 'number'
          const rhythmHandoffReady = rhythmPhase === PHASE_PLAYING
              || (duringPlayback && rhythmPhase === PHASE_ENTRY_GAP && countInHandoff)
          if (rhythmHandoffReady && isPlaybackGenerationCurrent(generation)) {
              const controller = getRhythmController()
              // Count-in already built an audio-clock timeline at the correct
              // wall tempo. Do not retarget score BPM here — that made post
              // anacrusis clicks late/slow versus the tune.
              if (!controller.timeline) {
                  syncPlaybackMetronomeTempo()
              }
              const snap = getRhythmTimelineSnapshot(controller)
              const scheduledStart = typeof opts.startAtAudioTime === 'number'
                  ? opts.startAtAudioTime
                  : (snap && snap.musicStartAudioTime != null ? snap.musicStartAudioTime : null)
              const tempoFactor = getTempoFactor()
              const factor = tempoFactor > 0 ? tempoFactor : 1
              const now = gaudioContext.current ? gaudioContext.current.currentTime : null
              let musicStartAudioTime = scheduledStart
              let musicSeconds = 0
              let forceTimelineReanchor = false
              let handoffMusicSeconds = null
              const alreadyHandedOff = controller.phase === PHASE_PLAYING && !!controller.timeline
              const priorAnchor = rhythmMusicAnchorRef.current
              // True audible MIDI start: prefer the pitch-shifter connect time.
              // When MIDI was pre-scheduled, audioStart.actualStartAudioTime is only
              // the original target — using it re-anchored the click grid backward
              // onto the early schedule and left ~8ms metronome skew.
              let audibleMidiStart = null
              if (pitchShifterRef.current
                  && typeof pitchShifterRef.current.getScheduledConnectTime === 'function') {
                  const sc = pitchShifterRef.current.getScheduledConnectTime()
                  if (typeof sc === 'number') audibleMidiStart = sc
              }
              if (audibleMidiStart == null && !midiWasPreScheduled
                  && audioStart && typeof audioStart.actualStartAudioTime === 'number') {
                  audibleMidiStart = audioStart.actualStartAudioTime
              }
              if (alreadyHandedOff) {
                  const entrySlot = controller.musicStartSlot != null
                      ? controller.musicStartSlot
                      : 0
                  const plannedEntryAudio = controller.timeline
                      ? audioTimeForGlobalSlot(controller.timeline, entrySlot)
                      : (controller.musicStartAudioTime != null
                          ? controller.musicStartAudioTime
                          : scheduledStart)
                  if (audibleMidiStart != null && typeof plannedEntryAudio === 'number'
                      && Math.abs(audibleMidiStart - plannedEntryAudio) > 0.003
                      && Math.abs(audibleMidiStart - plannedEntryAudio) < 0.08
                      && controller.timeline) {
                      reanchorTimelineAtSlot(
                          controller.timeline,
                          entrySlot,
                          audibleMidiStart
                      )
                      controller.musicStartAudioTime = audibleMidiStart
                      musicStartAudioTime = audibleMidiStart
                      rhythmMusicAnchorRef.current = {
                          musicSeconds: 0,
                          audioContextTime: audibleMidiStart,
                          active: true,
                      }
                  } else if (!priorAnchor.active) {
                      musicStartAudioTime = controller.musicStartAudioTime != null
                          ? controller.musicStartAudioTime
                          : scheduledStart
                      rhythmMusicAnchorRef.current = {
                          musicSeconds: 0,
                          audioContextTime: typeof musicStartAudioTime === 'number'
                              ? musicStartAudioTime
                              : (now || 0),
                          active: true,
                      }
                  }
                  rebuildPlaybackTimingMap()
              } else {
              const handoff = resolveCountInHandoffAnchor(
                  scheduledStart,
                  gaudioContext.current ? gaudioContext.current.currentTime : null,
                  {
                      minLeadSec: 0.002,
                      audioStartedAtScheduled: midiWasPreScheduled,
                      tempoFactor: getTempoFactor(),
                  }
              )
              musicStartAudioTime = handoff.actualStartAudioTime
              musicSeconds = handoff.musicSeconds
              handoffMusicSeconds = handoff.musicSeconds
              forceTimelineReanchor = false
              if (audibleMidiStart != null && typeof musicStartAudioTime === 'number'
                  && Math.abs(audibleMidiStart - musicStartAudioTime) > 0.003
                  && Math.abs(audibleMidiStart - musicStartAudioTime) < 0.08) {
                  // Prefer the audible MIDI connect time over the handoff estimate.
                  if (musicSeconds < 0.001) {
                      musicStartAudioTime = audibleMidiStart
                      forceTimelineReanchor = true
                  } else {
                      musicStartAudioTime = audibleMidiStart - musicSeconds / factor
                      forceTimelineReanchor = true
                  }
              }
              // Keep the count-in grid when handoff is on time. Only re-anchor when
              // music audibly started late (SoundTouch connect drift, async handoff).
              if (controller.timeline && now != null) {
                  const scheduledConnect = pitchShifterRef.current
                      && typeof pitchShifterRef.current.getScheduledConnectTime === 'function'
                      ? pitchShifterRef.current.getScheduledConnectTime()
                      : null
                  if (typeof scheduledConnect === 'number' && scheduledConnect > now + 0.001) {
                      musicStartAudioTime = scheduledConnect
                      musicSeconds = 0
                  } else if (handoff.musicSeconds > 0.05) {
                      const actualMusicSeconds = isMidiAudioEngineRunning()
                          ? getRhythmMusicSeconds()
                          : musicSeconds
                      if (actualMusicSeconds > handoff.musicSeconds + 0.02) {
                          musicSeconds = actualMusicSeconds
                          musicStartAudioTime = now - actualMusicSeconds / factor
                          forceTimelineReanchor = true
                      }
                  }
              } else if (isMidiAudioEngineRunning()) {
                  const actualMusicSeconds = getRhythmMusicSeconds()
                  if (actualMusicSeconds > musicSeconds + 0.05 && now != null) {
                      musicSeconds = actualMusicSeconds
                      musicStartAudioTime = now - actualMusicSeconds / factor
                      forceTimelineReanchor = true
                  }
              }
              const anchorAudio = typeof musicStartAudioTime === 'number'
                  ? musicStartAudioTime + musicSeconds / factor
                  : (gaudioContext.current ? gaudioContext.current.currentTime : 0)
              rhythmMusicAnchorRef.current = {
                  musicSeconds: musicSeconds,
                  audioContextTime: anchorAudio,
                  active: true,
              }
              beginRhythmPlayingAtMusicStart(controller, {
                  musicSeconds: musicSeconds,
                  musicStartAudioTime: musicStartAudioTime,
                  forceTimelineReanchor: forceTimelineReanchor,
                  // 0 = downbeat; negative = anacrusis (e.g. -1 for one-beat pickup).
                  musicStartSlot: snap && snap.musicStartSlot != null
                      ? snap.musicStartSlot
                      : (controller.musicStartSlot != null ? controller.musicStartSlot : 0),
              })
              rebuildPlaybackTimingMap()
              }
              var _map0 = playbackTimingMapRef.current
              var _mapBpm = _map0 && _map0.tempoBreaks && _map0.tempoBreaks[0]
                  ? _map0.tempoBreaks[0].rhythmBeatBpm
                  : null
              syncPlaybackTimingFromMap(true)
              if (deferTimingUntilHandoff && gtimingCallbacks.current) {
                  const bufDur = gmidiBuffer.current && gmidiBuffer.current.duration > 0
                      ? gmidiBuffer.current.duration
                      : 0
                  const handoffProgress = bufDur > 0
                      ? Math.max(0, Math.min(1, musicSeconds / bufDur))
                      : 0
                  setTimingProgressFromAudioRatio(handoffProgress)
                  if (!gtimingCallbacks.current.isRunning) {
                      gtimingCallbacks.current.start()
                  }
              }
          } else if (rhythmHandoffReady) {
          }
          if (duringPlayback
              && rhythmPhase !== PHASE_PLAYING
              && rhythmPhase !== PHASE_COUNT_IN
              && rhythmPhase !== PHASE_ENTRY_GAP) {
              ensureDuringPlaybackMetronome(ratio)
          }
          return true
      } catch (e) {
        handleMidiAudioStartFailure()
        return false
      }
  }
    
  function startPrimedTune(force = false) {
    if (hostOwnsAbcOutput()) {
      return
    }
    var emergencyStop = getForceStop()
    var fromStart = shouldRestartMidiFromStart(force)
    var useCountIn = shouldUseCountInForStart(force)
    if (wantsMidiPlayback(force)) {
        if (!emergencyStop) {
          if (gtimingCallbacks && gtimingCallbacks.current && gmidiBuffer && gmidiBuffer.current) {
              if (!fromStart) {
                const pending = props.mediaController && props.mediaController.pendingMidiPlayRef
                    ? props.mediaController.pendingMidiPlayRef.current
                    : null
                let pendingStartSeconds = resolveNotationPlaybackStartSeconds(pending || {})
                if (!(pendingStartSeconds > 0)) {
                    pendingStartSeconds = pendingPlaybackStartSecondsRef.current
                }
                if (typeof pendingStartSeconds === 'number' && pendingStartSeconds > 0
                    && gmidiBuffer.current.duration > 0) {
                    pendingPlaybackStartSecondsRef.current = null
                    if (props.mediaController && props.mediaController.pendingMidiPlayRef) {
                        props.mediaController.pendingMidiPlayRef.current = null
                    }
                    const ratio = resolveNotationSeekRatio(
                        pending || {}, pendingStartSeconds, gmidiBuffer.current.duration,
                        pending && pending.tempo)
                        || Math.min(1, pendingStartSeconds / gmidiBuffer.current.duration)
                    syncPlaybackSeekFromSeconds(pendingStartSeconds, ratio)
                    startMidiAndTiming({ forceRatio: ratio, forcePlayback: true })
                    return
                }
                if (hasPendingNotationSeek()) {
                    const seekOpts = pending || {}
                    let seekStartSeconds = resolveNotationPlaybackStartSeconds(seekOpts)
                    if (!(seekStartSeconds > 0)) {
                        seekStartSeconds = resolveNotationPlaybackStartSeconds({})
                    }
                    if (seekStartSeconds > 0 && gmidiBuffer.current.duration > 0) {
                        pendingPlaybackStartSecondsRef.current = null
                        if (props.mediaController && props.mediaController.pendingMidiPlayRef) {
                            props.mediaController.pendingMidiPlayRef.current = null
                        }
                        const ratio = resolveNotationSeekRatio(
                            seekOpts, seekStartSeconds, gmidiBuffer.current.duration,
                            seekOpts && seekOpts.tempo)
                            || Math.min(1, seekStartSeconds / gmidiBuffer.current.duration)
                        syncPlaybackSeekFromSeconds(seekStartSeconds, ratio)
                        startMidiAndTiming({ forceRatio: ratio, forcePlayback: true })
                        return
                    }
                    // Do not re-enter beginMidiPlayback here — that path loops back
                    // through startPrimedTune and overflows the stack.
                    clearNotationPlaybackSeek()
                }
                const resumeRatio = getMidiPlaybackRatio()
                if (resumeRatio > 0) {
                    startMidiAndTiming({ forceRatio: resumeRatio, forcePlayback: true })
                    return
                }
                startMidiAndTiming({ forcePlayback: true })
              } else if (!useCountIn) {
                latchMidiPrimeCancel(2000)
                invalidatePendingMidiStarts()
                fullyResetMidiEnginesToStart()
                startMidiAndTiming({ forceRatio: 0, forcePlayback: true })
              } else {
                latchMidiPrimeCancel(2000)
                invalidatePendingMidiStarts()
                const countInGeneration = playbackGenerationRef.current
                fullyResetMidiEnginesToStart()
                countInPendingRef.current = true
                var o = gvisualObj.current
                var tempoFactor = getTempoFactor()
                var metro = resolvePlaybackMetronomeOptions()
                var countInRhythm = resolvePlaybackMetronomeRhythm()
                const countInMeter = resolveTuneTimeSignature(
                    resolveMetronomeSettingsTune(tune, {
                        tunes: props.tunes,
                        tablatureSourceTune: props.tablatureSourceTune,
                        mediaControllerTune: props.mediaController && props.mediaController.tune,
                        abc: props.abc,
                        abcTools: props.tunebook && props.tunebook.abcTools,
                    }),
                    props.tunebook
                )
                // Full bar when practice forces it or there is no anacrusis.
                const scoreMsPerMeasure = getMsPerMeasureForRhythmGrid(o)
                const countInOptions = {
                    tempoFactor: tempoFactor,
                    countInBeats: metro.countInBeats,
                    countInBarOnly: !!metro.countInBarOnly,
                    countInBars: effectiveCountInBars(countInMeter, metro.countInBars),
                    meter: countInMeter,
                    effectiveMsPerMeasure: scoreMsPerMeasure,
                }
                var countInInput = rhythmAlignedCountInInput(o, countInRhythm, countInOptions)
                if (!countInInput) {
                    const abcBeats = parseFloat(o.getBeatsPerMeasure()) || countInRhythm.beatsPerBar
                    const abcBeatLen = parseFloat(o.getBeatLength()) || 0
                    const fallbackBeatLen = abcBeatLen > 0
                        ? abcBeatLen
                        : defaultAbcBeatLengthForMeter(countInMeter || '4/4')
                    countInInput = {
                        beatsPerMeasure: countInRhythm.beatsPerBar,
                        pickupLength: metro.countInBarOnly
                            ? 0
                            : (parseFloat(o.getPickupLength()) || 0),
                        beatLength: fallbackBeatLen > 0
                            ? fallbackBeatLen * (abcBeats / countInRhythm.beatsPerBar)
                            : 0,
                        millisecondsPerMeasure: scoreMsPerMeasure || o.millisecondsPerMeasure(),
                        tempoFactor: tempoFactor,
                        countInBeats: metro.countInBeats,
                        countInBarOnly: !!metro.countInBarOnly,
                        countInBars: effectiveCountInBars(countInMeter, metro.countInBars),
                    }
                }
                var countIn = computeMidiMetronomeCountIn(countInInput)
                var metronomeBeats = countIn.metronomeBeats
                const extraMeasures = getExtraMeasuresAtBeginning()
                const pickupDelayMs = parseFloat(countIn.delayMs) || 0
                const countInBeatLength = parseFloat(countInInput.beatLength) || 0
                const tunePickupLength = parseFloat(o.getPickupLength()) || 0
                const countInPickupLength = metro.countInBarOnly
                    ? 0
                    : tunePickupLength
                // Rhythm-beat units (e.g. 1.0 for a quarter anacrusis in 3/4).
                const pickupBeats = (countInPickupLength > 0 && countInBeatLength > 0)
                    ? countInPickupLength / countInBeatLength
                    : 0

                function startCountInCursor() {
                    if (metro.duringPlayback) return
                    if (extraMeasures > 0 && gtimingCallbacks.current) {
                        try {
                            gtimingCallbacks.current.reset()
                            gtimingCallbacks.current.start(0)
                        } catch (e) {}
                    }
                }

                function startWithMetronome() {
                    if (!isPlaybackGenerationCurrent(countInGeneration)) {
                        countInPendingRef.current = false
                        return
                    }
                    if (!metro.metronomeCountIn || metronomeBeats <= 0) {
                       countInPendingRef.current = false
                       startMidiAndTiming({ forceRatio: 0, forcePlayback: true })
                       return
                    }

                    const beginCountIn = function(contextReady) {
                        if (!isPlaybackGenerationCurrent(countInGeneration)) {
                            countInPendingRef.current = false
                            return
                        }
                        if (!wantsMidiPlayback()) {
                            countInPendingRef.current = false
                            return
                        }
                        if (!contextReady) {
                            countInPendingRef.current = false
                            releaseMidiUiLoading()
                            showMidiTapToPlay()
                            return
                        }

                        if (!(effectiveMsPerMeasureRef.current > 0)
                            && primedMsPerMeasureRef.current > 0) {
                            effectiveMsPerMeasureRef.current = primedMsPerMeasureRef.current
                        }
                        var effectiveTempo = computeCountInGridTempo(o, countInRhythm, Object.assign({}, countInOptions, {
                            fallbackQpm: getPlaybackMetronomeTempo(o),
                        }))
                        // Count-in is scheduled on the audio clock, so use wall BPM
                        // (score BPM × playback speed). During-playback uses score BPM
                        // + tempoFactor separately.
                        if (!(effectiveTempo > 0)) {
                            const baseGrid = getRhythmGridMetronomeTempo()
                            const factor = getTempoFactor()
                            effectiveTempo = baseGrid > 0 ? baseGrid * (factor > 0 ? factor : 1) : 0
                        }
                        if (!(effectiveTempo > 0)) effectiveTempo = getPlaybackMetronomeTempo()
                        if (!(effectiveTempo > 0)) effectiveTempo = 120
                        var countInBeatCount = resolveCountInBeatCount(
                            o,
                            countInRhythm,
                            countInOptions,
                            metro
                        )
                        var countInSlots = resolveCountInSlotCount(
                            o,
                            countInRhythm,
                            countInOptions,
                            metro
                        )
                        if (!(countInSlots > 0)) countInSlots = metronomeBeats
                        metronomeBeats = countInBeatCount > 0 ? countInBeatCount : countInSlots
                        const duringPlayback = metro.duringPlayback === true
                        const preferredCue = (props.metronomeCountInCueMidi != null
                          && Number.isFinite(props.metronomeCountInCueMidi))
                          ? Math.round(props.metronomeCountInCueMidi)
                          : null
                        const fromVisual = firstPlaybackCueMidiFromVisual(gvisualObj.current)
                        const cueMidi = preferredCue != null
                          ? preferredCue
                          : ((fromVisual != null && Number.isFinite(fromVisual))
                            ? fromVisual
                            : firstWarmupCueMidi(props.abc))
                        const beatDurationSec = 60 / effectiveTempo
                        const cueDurationSec = Math.max(0.35, Math.min(1.2, beatDurationSec * 0.95))
                        const primedCueBuffer = gmidiBuffer.current
                          && gmidiBuffer.current.audioBuffers
                          && gmidiBuffer.current.audioBuffers[0]
                        const cueReady = primedCueBuffer
                          ? Promise.resolve(primedCueBuffer)
                          : (cueMidi != null && Number.isFinite(cueMidi)
                            ? preloadCountInCueInstrument(gaudioContext.current)
                            : Promise.resolve(null))

                        const launchCountIn = function() {
                            if (!isPlaybackGenerationCurrent(countInGeneration)) {
                                countInPendingRef.current = false
                                releaseMidiUiLoading()
                                return
                            }
                            const gridTempo = computeCountInGridTempo(o, countInRhythm, Object.assign({}, countInOptions, {
                                fallbackQpm: effectiveTempo,
                            }))
                            const tempo = gridTempo > 0 ? gridTempo : effectiveTempo
                            countInMidiPreScheduledRef.current = false
                            const countInStarted = startRhythmCountIn(getRhythmController(), {
                                rhythm: countInRhythm,
                                tempo: tempo,
                                slotCount: countInSlots,
                                duringPlayback: duringPlayback,
                                pickupBeats: pickupBeats,
                                entryGapDelayMs: pickupDelayMs,
                                audioContext: gaudioContext.current,
                                playSlot: rhythmPlaySlotFn,
                                getMusicSeconds: getRhythmMusicSeconds,
                                getTempoFactor: getTempoFactor,
                                getGridTempo: function() {
                                    return computeCountInGridTempo(o, countInRhythm, Object.assign({}, countInOptions, {
                                        fallbackQpm: effectiveTempo,
                                    })) || effectiveTempo
                                },
                                onSlot: function(slot, emitted, total) {
                                    if (!isPlaybackGenerationCurrent(countInGeneration)) return
                                    if (props.onCountInBeat) {
                                        props.onCountInBeat({
                                            beat: Math.min(emitted, total),
                                            totalBeats: total,
                                            remaining: Math.max(0, total - emitted + 1),
                                            slotsRemaining: Math.max(0, countInSlots - emitted),
                                        })
                                    }
                                },
                                onFirstNoteSchedule: (!duringPlayback && shouldScheduleCountInPitchCue())
                                    ? function(time) {
                                    if (!isPlaybackGenerationCurrent(countInGeneration)) return
                                    const cueGain = props.practiceReferenceGain != null
                                        ? Math.max(0.35, Math.min(1, props.practiceReferenceGain * 4))
                                        : 0.75
                                    cueReady.then(function(buffer) {
                                        if (!isPlaybackGenerationCurrent(countInGeneration)) return
                                        scheduleCountInCueNote(
                                            gaudioContext.current,
                                            cueMidi,
                                            time,
                                            cueDurationSec,
                                            cueGain,
                                            null
                                        )
                                    }).catch(function() {})
                                }
                                    : null,
                                onPreScheduleMusic: duringPlayback
                                    ? function(musicStartAudioTime) {
                                    if (!isPlaybackGenerationCurrent(countInGeneration)) return
                                    if (!wantsMidiPlayback(true)) return
                                    if (!(typeof musicStartAudioTime === 'number')) return
                                    const midiOut = startMidiAudioOutput(
                                        pitchTempoSettingsRef.current,
                                        0,
                                        musicStartAudioTime
                                    )
                                    countInMidiPreScheduledRef.current = !!(midiOut && midiOut.ok)
                                }
                                    : null,
                                onMusicStart: function(scheduledMusicStartAudioTime) {
                                    if (!isPlaybackGenerationCurrent(countInGeneration)) {
                                        countInPendingRef.current = false
                                        releaseMidiUiLoading()
                                        return
                                    }
                                    if (!wantsMidiPlayback(true)) {
                                        countInPendingRef.current = false
                                        releaseMidiUiLoading()
                                        return
                                    }
                                    const startAt = typeof scheduledMusicStartAudioTime === 'number'
                                        ? scheduledMusicStartAudioTime
                                        : null
                                    if (!duringPlayback) {
                                        prepareFreshMidiStartFromCountIn()
                                    }
                                    if (duringPlayback && startAt != null) {
                                        // Prefer the count-in pre-schedule; only arm here if
                                        // that path missed (reuse guard avoids tearing it down).
                                        const midiOut = startMidiAudioOutput(
                                            pitchTempoSettingsRef.current,
                                            0,
                                            startAt
                                        )
                                        countInMidiPreScheduledRef.current = !!(midiOut && midiOut.ok)
                                        const controller = getRhythmController()
                                        const snap = getRhythmTimelineSnapshot(controller)
                                        const handoffNow = gaudioContext.current
                                            ? gaudioContext.current.currentTime
                                            : null
                                        const outputLatencySec = pitchShifterRef.current
                                            && typeof pitchShifterRef.current.getOutputLatencySec === 'function'
                                            ? pitchShifterRef.current.getOutputLatencySec()
                                            : 0
                                        const scheduledConnect = pitchShifterRef.current
                                            && typeof pitchShifterRef.current.getScheduledConnectTime === 'function'
                                            ? pitchShifterRef.current.getScheduledConnectTime()
                                            : null
                                        const midiStart = (typeof scheduledConnect === 'number'
                                            ? scheduledConnect
                                            : (midiOut && typeof midiOut.actualStartAudioTime === 'number'
                                                ? midiOut.actualStartAudioTime
                                                : handoffNow))
                                        const audibleMusicStart = typeof midiStart === 'number'
                                            ? midiStart + Math.max(0, outputLatencySec)
                                            : (startAt != null
                                                ? startAt + Math.max(0, outputLatencySec)
                                                : null)
                                        const connectDrift = audibleMusicStart != null && startAt != null
                                            ? audibleMusicStart - (startAt + Math.max(0, outputLatencySec))
                                            : 0
                                        const entrySlot = snap && snap.musicStartSlot != null
                                            ? snap.musicStartSlot
                                            : 0
                                        // Keep count-in phase; shift only when MIDI connects
                                        // measurably late so entry slot matches audible start.
                                        const pickupBeats = controller.pickupBeats || 0
                                        const secPerBeat = controller.tempo > 0 ? 60 / controller.tempo : 0
                                        const fractionalPickup = entrySlot === 0 && pickupBeats > 0
                                            && pickupBeats < 1 - 1e-6 && secPerBeat > 0
                                        const scheduledAnacrusis = startAt != null
                                            ? startAt + Math.max(0, outputLatencySec)
                                            : null
                                        let timelineAnchorSlot = entrySlot
                                        let timelineMusicStart = scheduledAnacrusis != null
                                            ? scheduledAnacrusis
                                            : (startAt != null
                                                ? startAt + Math.max(0, outputLatencySec)
                                                : startAt)
                                        let needsReanchor = outputLatencySec > 0.001
                                        if (fractionalPickup) {
                                            // First note is anacrusis; timeline slot 0 is downbeat.
                                            // Prefer the real audible anacrusis when connect drifted.
                                            const anacrusisTime = (connectDrift > 0.003
                                                && audibleMusicStart != null)
                                                ? audibleMusicStart
                                                : (scheduledAnacrusis != null
                                                    ? scheduledAnacrusis
                                                    : audibleMusicStart)
                                            timelineMusicStart = (anacrusisTime != null
                                                ? anacrusisTime
                                                : timelineMusicStart) + pickupBeats * secPerBeat
                                            timelineAnchorSlot = 0
                                            needsReanchor = true
                                        } else if (connectDrift > 0.003 && audibleMusicStart != null) {
                                            timelineMusicStart = audibleMusicStart
                                            timelineAnchorSlot = entrySlot
                                            needsReanchor = true
                                        }
                                        playalongOutputLatencyInTimelineRef.current = {
                                            alreadyInTimeline: needsReanchor && outputLatencySec > 0.001,
                                            appliedSeconds: needsReanchor && outputLatencySec > 0.001
                                                ? Math.max(0, outputLatencySec)
                                                : 0,
                                        }
                                        // Music seconds 0 = first sounding note (anacrusis),
                                        // not the downbeat used as timelineMusicStart for pickups.
                                        const musicClockAudioTime = (connectDrift > 0.003
                                            && audibleMusicStart != null)
                                            ? audibleMusicStart
                                            : (scheduledAnacrusis != null
                                                ? scheduledAnacrusis
                                                : audibleMusicStart)
                                        rhythmMusicAnchorRef.current = {
                                            musicSeconds: 0,
                                            audioContextTime: musicClockAudioTime != null
                                                ? musicClockAudioTime
                                                : (audibleMusicStart != null
                                                    ? audibleMusicStart
                                                    : (startAt || 0)),
                                            active: true,
                                        }
                                        beginRhythmPlayingAtMusicStart(controller, {
                                            musicSeconds: 0,
                                            musicStartAudioTime: timelineMusicStart,
                                            forceTimelineReanchor: needsReanchor,
                                            timelineAnchorSlot: needsReanchor
                                                ? timelineAnchorSlot
                                                : undefined,
                                            musicStartSlot: snap && snap.musicStartSlot != null
                                                ? snap.musicStartSlot
                                                : 0,
                                        })
                                    }
                                    // Keep count-in pending until handoff finishes so media
                                    // cold-start kicks cannot stack a second startMidiAndTiming.
                                    Promise.resolve(startMidiAndTiming({
                                        forceRatio: 0,
                                        forcePlayback: true,
                                        startAtAudioTime: startAt,
                                    })).finally(function() {
                                        countInPendingRef.current = false
                                    })
                                },
                            })
                            if (!countInStarted) {
                                countInPendingRef.current = false
                                releaseMidiUiLoading()
                                prepareFreshMidiStartFromCountIn()
                                startMidiAndTiming({ forceRatio: 0, forcePlayback: true })
                            } else {
                                releaseMidiUiLoading()
                            }
                        }

                        launchCountIn()
                        startCountInCursor()
                    }

                    ensureSynthAudioContextRunning().then(function(contextReady) {
                        const startCountInFlow = function() {
                            beginCountIn(contextReady)
                        }
                        if (countInRhythm.engineMode === ENGINE_MODE_DRUMS) {
                            primeDrumKit(gaudioContext.current).then(startCountInFlow).catch(startCountInFlow)
                        } else {
                            startCountInFlow()
                        }
                    })
                }
                var speakTitle = localStorage.getItem('bookstorage_announcesong') === "true" ? true : false
                if (speakTitle && tune) {
                  var toSpeak = tune.name
                  if (tune.composer) toSpeak += " by " + tune.composer
                  window.speak(toSpeak)
                  setTimeout(function() {
                    if (!isPlaybackGenerationCurrent(countInGeneration)) return
                    if (wantsMidiPlayback()) {
                      startWithMetronome()
                    }
                  }, 1000)
                } else {
                  startWithMetronome()
                }
              }
          } else {
          }
        } else {
          stopPlaying()
        }
    }
  }

  
  function primeAudio() {
      
    return new Promise(function(resolve,reject) {
        if (!abcjs.synth.supportsAudio()) {
          setTapToPlayFlag(true)
          if (props.mediaController) {
              props.mediaController.setIsLoading(false)
              props.mediaController.setIsPlaying(false)
          }
          reject('No audio available')
          return
        }
        const existing = gaudioContext.current
        if (existing && existing.state !== 'closed') {
            if (existing.state === 'suspended') {
                try { existing.resume() } catch (e) { /* ignore */ }
            }
            resolve(existing)
            return
        }
        window.AudioContext = window.AudioContext ||
          window.webkitAudioContext ||
          navigator.mozAudioContext ||
          navigator.msAudioContext;
        const audioContext = createPlaybackAudioContext() || new window.AudioContext();
        gaudioContext.current = audioContext
        const fromGesture = props.mediaController && props.mediaController.userGesturePlayRef
            && props.mediaController.userGesturePlayRef.current
        const fromPracticeGesture = props.consumePlaybackGesture && props.consumePlaybackGesture()
        if ((fromGesture || fromPracticeGesture) && audioContext.state === 'suspended') {
            audioContext.resume().catch(function() {})
        }
        resolve(audioContext)
    })
  } 

  function primeTune(tune, audioContext, visualObj, force = false, options = {}) {
      const showUiLoading = !!options.showUiLoading
      const synthObj = options.playbackVisualObj || visualObj
      if (primePromiseRef.current && isLoading.current && !force) {
          return primePromiseRef.current
      }
      //var tempo = tune ? tune.tempo : 100
      const promise = new Promise(function(resolve,reject) {
          const generation = playbackGenerationRef.current
          const canReuseConcurrentPrime = !force
              && gmidiBuffer.current
              && gtimingCallbacks.current
              && (primePromiseRef.current || countInPendingRef.current || isRhythmHandoffPhase())
          if (canReuseConcurrentPrime) {
            isLoading.current = false
            if (showUiLoading && props.mediaController) props.mediaController.setIsLoading(false)
            resolve({
              midiBuffer: gmidiBuffer.current,
              timingCallbacks: gtimingCallbacks.current,
              cursor: gcursor.current,
            })
            return
          }
          isLoading.current = true
          midiPrimeInFlightRef.current = true
          if (showUiLoading && props.mediaController) props.mediaController.setIsLoading(true)
          // cleanup first — tear down old engines only; do not bump playback
          // generation or we invalidate the prime we are about to start.
          forceDestroyAudioEngines()
          if (visualObj) {
            setMidiBuffer(null)
            var midiBuffer = new abcjs.synth.CreateSynth()
            var count = 0
            var fillTune = props.forceFillOff
              ? Object.assign({}, tune, {
                  playbackFillStyle: FILL_STYLE_OFF,
                  playbackFillLevel: 0,
                })
              : tune
            var fillPlayback = resolveFillPlaybackOptions(fillTune, props.tunebook)
            lastPrimedMelodyOnlyRef.current = !!props.forceFillOff || fillPlayback.settings.style === FILL_STYLE_OFF
            var soundFontPlan = getPlaybackSoundFontPlan({ tune: tune })
            var a = soundFontPlan.url
            //var warp =  props.warp > 0 ? props.warp : 1
            var soundFontVolume = getSoundFontVolumeMultiplier()
            // MusyngKite must not use original-bank programOffsets — those shift
            // note peaks ~15–90ms early vs the metronome (median ~35ms measured).
            var effectiveProgramOffsets = programOffsetsForPlaybackPlan(a, soundFontPlan)
            // Practice quiet/loud level is applied live via pitch-shifter output
            // gain so the session volume slider can change without re-priming.
            var initOptions = {
                audioContext: audioContext,
              //onPlaying: function(details) {
                //if (midiBuffer.duration > 0) setSeekTo((details.timePlayed + details.startOffset)/midiBuffer.duration)
              //},
              millisecondsPerMeasure: synthObj.millisecondsPerMeasure(),
              options: abcjsPlaybackSynthOptions({
                 soundFontUrl: a,
                 soundFontVolumeMultiplier: soundFontVolume,
                 //program: 21,
                 chordsOff: fillPlayback.chordsOff,
                 programOffsets: effectiveProgramOffsets,
               }),
            }
            var useSequencePath = true
            var audibleMsPerMeasureForPrime = initOptions.millisecondsPerMeasure
            try {
              soundingWrittenMapRef.current = buildSoundingWrittenMap(synthObj)
              if (props.mediaController) {
                assignMediaControllerRef(
                  props.mediaController,
                  'soundingWrittenMapRef',
                  soundingWrittenMapRef.current
                )
              }
              var flattened = buildPlaybackSequence(synthObj, {
                fillOptions: fillPlayback,
                tune: tune,
                tunebook: props.tunebook,
                millisecondsPerMeasure: initOptions.millisecondsPerMeasure,
                transpose: resolveMidiTransposeSemitones(synthObj, tune),
              })
              if (!flattened || !Array.isArray(flattened.tracks) || !flattened.tracks.length) {
                throw new Error('Playback sequence missing tracks')
              }
              // Stamp expected buffer length so cursor clock matches sequence even
              // before midiBuffer.duration is assigned.
              if (soundingWrittenMapRef.current && audibleMsPerMeasureForPrime > 0) {
                var barW = barWholeNotesFromMeter(
                  typeof synthObj.getMeterFraction === 'function'
                    ? synthObj.getMeterFraction()
                    : null
                )
                if (barW > 0 && soundingWrittenMapRef.current.soundingWhole > 0) {
                  soundingWrittenMapRef.current.expectedDurationSec =
                    soundingWrittenMapRef.current.soundingWhole
                    * (audibleMsPerMeasureForPrime / 1000) / barW
                }
              }
              if (soundFontPlan.remap) {
                remapFlattenedMidiPrograms(flattened)
              }
              initOptions.sequence = flattened
              delete initOptions.visualObj
              // abcjs CreateSynth defaults meterSize=1 when only `sequence` is
              // passed (visualObj path sets num/den). Scale the init option for
              // CreateSynth, but keep audible bar length for TimingCallbacks /
              // metronome QPM (see resolveSequencePathMeasureTiming).
              var measureTiming = resolveSequencePathMeasureTiming(
                initOptions.millisecondsPerMeasure,
                typeof synthObj.getMeterFraction === 'function'
                  ? synthObj.getMeterFraction()
                  : null
              )
              audibleMsPerMeasureForPrime = measureTiming.audibleMsPerMeasure
              initOptions.millisecondsPerMeasure = measureTiming.createSynthMsPerMeasure
              if (audibleMsPerMeasureForPrime > 0) {
                primedMsPerMeasureRef.current = audibleMsPerMeasureForPrime
                effectiveMsPerMeasureRef.current = audibleMsPerMeasureForPrime
              }
            } catch (remapErr) {
              console.warn('Playback sequence build failed; using visualObj', remapErr)
              soundingWrittenMapRef.current = null
              if (props.mediaController) {
                assignMediaControllerRef(props.mediaController, 'soundingWrittenMapRef', null)
              }
              initOptions.visualObj = synthObj
              delete initOptions.sequence
              useSequencePath = false
            }
            // Legacy "online" preference: when resolver bank is ready, MusyngKite
            // already covers full GM. When not ready, keep remapped local fonts
            // (no longer clears soundFontUrl to hit FluidR3 CDN).
            initOptions.options.midiTranspose = resolveMidiTransposeSemitones(synthObj, tune)
            lastPrimedMidiTransposeRef.current = initOptions.options.midiTranspose
         
            function getAudioHash(tune) {
              // Include meter so pre-fix sequence-path caches (wrong meterSize)
              // are not reused after the millisecondsPerMeasure compensation.
              // Include ofsMusyng0 so MusyngKite buffers rendered with original-bank
              // programOffsets are not reused after offsets were cleared.
              // Include visual/MIDI transpose so capo/transpose display changes
              // do not reuse a buffer primed at a different pitch.
              var meterKey = ''
              try {
                if (synthObj && typeof synthObj.getMeterFraction === 'function') {
                  var mf = synthObj.getMeterFraction()
                  if (mf && mf.num && mf.den) meterKey = mf.num + '_' + mf.den + 'msfix'
                }
              } catch (e) {}
              var seqMsKey = useSequencePath
                ? Math.round(initOptions.millisecondsPerMeasure || 0)
                : 0
              var ofsKey = Object.keys(effectiveProgramOffsets || {}).length ? 'ofs1' : 'ofsMusyng0'
              var bankKey = soundFontPlan.bank === 'online' ? 'online' : 'sel'
              var fillKey = props.forceFillOff
                ? 'off-0'
                : ((tune && tune.playbackFillStyle ? tune.playbackFillStyle : 'boom-chick')
                  + '-' + (tune && tune.playbackFillLevel != null ? tune.playbackFillLevel : 100))
              var transposeKey = initOptions.options && initOptions.options.midiTranspose != null
                ? initOptions.options.midiTranspose
                : (tune && tune.transpose != null ? tune.transpose : 0)
              // vtx1: caches from before %%MIDI+visualTranspose de-dupe must not be reused.
              // tempoFix1-rep3: reject duration-mismatched caches (pitch-clock + meterSize).
              return tune.id + "-" + tune.tempo  + '-'+transposeKey+"-vtx1-tempoFix1-rep3-"+meterKey+"-"+seqMsKey+"-"+ofsKey+"-"+bankKey+"-"+fillKey+"-"+props.tunebook.utils.hash(props.tunebook.abcTools.getNotesFromAbc(props.abc))
            }
            
            function resolveWithTimingAndCursor(midiBuffer) {
              if (!isPlaybackGenerationCurrent(generation) || primePromiseRef.current !== promise) {
                isLoading.current = false
                if (primePromiseRef.current !== promise) {
                  // Superseded by a forced re-prime; leave loading to the active prime.
                } else {
                  releaseMidiUiLoading()
                }
                resolve(null)
                return
              }
              var timingCallbacks = new abcjs.TimingCallbacks(visualObj, buildTimingCallbacksOptions(visualObj))
              var cursor = createCursor()
              lastPrimedAudioHashRef.current = getAudioHash(tune)
              if (midiBuffer && midiBuffer.duration > 0 && props.mediaController) {
                props.mediaController.setDuration(midiBuffer.duration)
              }
              if (midiBuffer && midiBuffer.duration > 0 && soundingWrittenMapRef.current) {
                soundingWrittenMapRef.current.expectedDurationSec = midiBuffer.duration
                if (props.mediaController) {
                  assignMediaControllerRef(
                    props.mediaController,
                    'soundingWrittenMapRef',
                    soundingWrittenMapRef.current
                  )
                }
              }
              try {
                rebuildPlaybackTimingMap({ bufferDuration: midiBuffer && midiBuffer.duration })
              } catch (e) {}
              releaseMidiUiLoading()
              resolve({midiBuffer, timingCallbacks, cursor})
            }
             
            function primeAndResolve() {
                  // Rejected sample fetches are cached forever by abcjs; clear them
                  // (and fully clear on user play) so missing pitches can reload.
                  if (force || showUiLoading) {
                    clearAbcjsSoundsCache()
                  } else {
                    clearRejectedAbcjsSoundsCache()
                  }
                  midiBuffer.init(initOptions).then(
                  function (response) {
                    var loadErrors = response && Array.isArray(response.error) ? response.error : []
                    if (loadErrors.length) {
                      console.warn('Soundfont sample load errors', loadErrors.slice(0, 12))
                      clearRejectedAbcjsSoundsCache()
                    }
                    midiBuffer.prime().then(function(presponse) {
                      resolveWithTimingAndCursor(midiBuffer)
                      if (isPlaybackGenerationCurrent(generation)
                          && tune && tune.id && props.cacheAudio !== false) {
                        saveAudioToCache(getAudioHash(tune), midiBuffer.audioBuffers, midiBuffer.duration)
                          .catch(function() {})
                      }
                    })
                    .catch(function (error) {
                      if (isPlaybackGenerationCurrent(generation) && primePromiseRef.current === promise) {
                        releaseMidiUiLoading()
                      } else {
                        isLoading.current = false
                      }
                      resolve(null)
                    })
                  }).catch(function (error) {
                     clearRejectedAbcjsSoundsCache()
                     if (isPlaybackGenerationCurrent(generation) && primePromiseRef.current === promise) {
                       releaseMidiUiLoading()
                     } else {
                       isLoading.current = false
                     }
                    resolve(null)
                  })
             }

              
              var skipAudioCache = fillPlayback.injectCustomFill || force || showUiLoading
              if ((tune && tune.id)) {
                if (props.cacheAudio !== false && !skipAudioCache) {
                    getAudioFromCache(getAudioHash(tune), audioContext).then(function(audioResult) {
                        if (audioResult) {
                          const [cachedDuration, audioBuffers] = audioResult
                          if (audioBuffers && audioBuffers[0]) {
                             const bufferDur = parseFloat(audioBuffers[0].duration) || 0
                             const expectedDur = soundingWrittenMapRef.current
                               && soundingWrittenMapRef.current.expectedDurationSec > 0
                               ? soundingWrittenMapRef.current.expectedDurationSec
                               : 0
                             // Trust the decoded buffer length; drop caches whose
                             // duration disagrees with the expanded-score expectation
                             // (stale meterSize / pitch-clock bugs stretched audio).
                             const dur = bufferDur > 0 ? bufferDur : (parseFloat(cachedDuration) || 0)
                             const mismatch = expectedDur > 0 && dur > 0
                               && Math.abs(dur - expectedDur) / expectedDur > 0.12
                             if (mismatch) {
                               primeAndResolve()
                               return
                             }
                             if (audibleMsPerMeasureForPrime > 0) {
                               primedMsPerMeasureRef.current = audibleMsPerMeasureForPrime
                             }
                             midiBuffer.init(initOptions).then(function (response) { 
                                midiBuffer.audioBuffers = audioBuffers
                                midiBuffer.duration = dur
                                resolveWithTimingAndCursor(midiBuffer)
                            })
                          } else {
                            primeAndResolve()
                          }
                        } else {
                          primeAndResolve()
                        } 
                    })
                  } else {
                    primeAndResolve()
                  }
              } else {
                  primeAndResolve()
              }

          } else {
              if (isPlaybackGenerationCurrent(generation)) {
                releaseMidiUiLoading()
              } else {
                isLoading.current = false
              }
              reject(null)
          }
      })
      primePromiseRef.current = promise
      promise.finally(function() {
          if (primePromiseRef.current === promise) {
              primePromiseRef.current = null
          }
      })
      return promise
  }
                    
  

  function createPlayer(tune, visualObj, options = {}) {
      const generation = playbackGenerationRef.current
      const showUiLoading = !!options.showUiLoading
      const debounceMs = showUiLoading
          ? 0
          : (props.audioRenderTimeout > 0 ? props.audioRenderTimeout : 1500)
      const alreadyPrimed = !!(gmidiBuffer.current && gtimingCallbacks.current
          && gcursor.current && gaudioContext.current)
      if (!alreadyPrimed) {
          midiPrimeInFlightRef.current = true
      }
      return new Promise(function(resolve, reject) {
        if (tune && visualObj) {
            if (alreadyPrimed) {
            }
            if (true) {
                primeAudio().then(function(audioContext) {
                    if (audioContext) {
                        //setReady(false)
                        //renderActive = true
                        if (primeTimerRef && primeTimerRef.current) clearTimeout(primeTimerRef.current)
                        // use timeout to prevent duplicate calls on load
                        primeTimerRef.current = setTimeout(function() {
                          if (!isPlaybackGenerationCurrent(generation)) {
                            if (!primePromiseRef.current) {
                                midiPrimeInFlightRef.current = false
                            }
                            resolve([audioContext, null, null, null, visualObj])
                            return
                          }
                          primeTune(tune, audioContext, visualObj, !!options.forcePrime, Object.assign({}, options, {
                            playbackVisualObj: synthVisualObj(visualObj),
                          })).then(function(primeParams) {
                             if (!isPlaybackGenerationCurrent(generation)) {
                               resolve([audioContext, null, null, null, visualObj])
                               return
                             }
                             if (primeParams) {
                               const {midiBuffer, timingCallbacks, cursor} = primeParams
                               resolve([audioContext, midiBuffer, timingCallbacks, cursor, visualObj])
                             } else {
                               resolve([audioContext, null,null,null, visualObj])
                             }
                          }).catch(function(e) {
                              if (e === 'cancelled') {
                                resolve([audioContext, null, null, null, visualObj])
                                return
                              }
                              reject(e)
                          })
                        }, debounceMs)
                    } else reject('No audio context')
                }).catch(function(e) {
                    reject(e.message)
                })
            }
        } else reject('Missing rendered tune')
    })
  }

  return {createCursor, programOffsets, clickListener, beatCallback, eventCallback, metronomeTimeout, metronome, rhythmController, gaudioContext, gmidiBuffer, gvisualObj, gplaybackVisualObj, gtimingCallbacks, gcursor,  showTempo, setShowTempo,showTranspose, setShowTranspose, clickSeek, setClickSeek, lastPlaybackSpeed, setLastPlaybackSpeed, audioChangedHash, setAudioChangedHash, tapToPlay, setTapToPlay, playCancelled, setPlayCancelled, abcTune, setAbcTune, lastAbc, setLastAbc, lastTempo, setLastTempo, lastBoost, setLastBoost, isPlaying, setIsPlaying, playCount, setPlayCountInner, playCountRef, setPlayCount, incrementPlayCount, lastScrollTo, autoScroll, autoScrollBlock, realProgress, seekTo, setSeekTo, forceSeekTo, setForceSeekTo, ready, setReady, started, setStarted, store, abcTools, inputEl, playTimerRef, setAudioContext, setMidiBuffer, setVisualObj, setPlaybackVisualObj, setTimingCallbacks, setCursor, setForceStop, getForceStop, getWarp, getWarpTempo, saveAudioToCache, getAudioFromCache, startPlaying, startPlayingFromIntent, stopPlaying, assignStateOnCompletion, resetAudioState, seekPlayer, createPlayer, primeTune, primeAudio, startPrimedTune, tune, setTune, isLastPlaying, setIsLastPlaying, setTempoFactor, applyMidiTempo, getPitchTempoState, resetPitchTempo, applyPlaybackSettings, getPlaybackGeneration: function() { return playbackGenerationRef.current }, isPlaybackGenerationCurrent}
}



